/**
 * Wheel Configurator — unified custom element that replaces the separate
 * vehicle-variant-filter, et-picker, and finish-picker blocks.
 *
 * Uses a "puppet" approach: hides Horizon's <variant-picker> (via CSS in the
 * Liquid stylesheet) but drives it programmatically. When the user picks an
 * option here, we set radio.checked + dispatch change on Horizon's picker,
 * which triggers Horizon's own price/image/URL/cart updates.
 */

import { getVehicle, saveVehicle, clearVehicle } from './vehicle-store.js';

const API_BASE = 'https://static.218.250.90.157.clients.your-server.de';
const SBF_AI_BUILD_ID = 'SBF-AI-20260810-V20-SIZE-STUDIO-LOCKED';
const DIAMETER_TOLERANCE = 2;
const ET_TOLERANCE = 500;
const MAX_WIDTH_NARROWER_INCH = 1.0;
const MAX_WIDTH_WIDER_INCH = 0.5;
const MAX_OUTER_CHANGE_MM = 7.5;
const MAX_OUTER_INSET_MM = 10;
const MAX_INNER_CHANGE_MM = 10;

// Custom Fit service: machine the center opening for the vehicle's original center caps.
// This is purchased separately and may exist only once in the Shopify cart.
const CENTER_CAP_ADDON_HANDLE = 'tilpasning-for-originale-senterkopper';
const CENTER_CAP_ADDON_FALLBACK_PRICE = 129000; // 1 290 NOK in øre

// Credit system constants (cookie/Vipps-per-image removed)
const CHECK_SVG = '<svg class="wc__info-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>';
const WARN_SVG = '<svg class="wc__info-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>';
const X_SVG = '<svg class="wc__info-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>';

class WheelConfiguratorElement extends HTMLElement {

  /* ================================================================
   *  Lifecycle
   * ================================================================ */

  connectedCallback() {
    // Parse variant + inventory data
    this._parseData();

    // Build option sets (PCD → Size → ET)
    this._buildOptionSets();

    // Detect ET mode: 'single' (finished) or 'range' (blank)
    this._detectEtMode();

    // Detect finish mode from data attributes
    this._finishMode = this.dataset.customFinish || '';
    this._finishBase = this.dataset.finishBase || '';
    this._selectedFinish = '';

    // URL selection from vehicle search.
    // custom_fit=1 means the selected diameter must open directly in Custom Fit.
    const urlParams = new URLSearchParams(window.location.search);
    this._urlDiameter = parseFloat(urlParams.get('diameter')) || 0;
    this._urlRearDiameter = parseFloat(urlParams.get('rear_diameter')) || 0;
    this._urlOemSetKey = String(urlParams.get('oem_set') || '').trim();
    this._openCustomFitFromUrl = urlParams.get('custom_fit') === '1' && this._urlDiameter > 0;

    // State
    this._selectedPcd = null;
    this._selectedDiameter = null;
    this._selectedSize = null;
    this._selectedEt = null;
    this._selectedExactEt = null;
    this._vehicle = getVehicle();
    this._filteredVariants = this._allVariants;
    this._boreByPcd = {};
    this._specsFetched = false;
    this._diamSpecs = new Map();

    // Staggered axle state
    this._activeAxle = 'front';
    this._axleSelections = { front: null, rear: null };
    this._cartedAxles = { front: null, rear: null };

    // Quick Buy mode state
    this._isHybridForged =
      this.hasAttribute('data-hybrid-forged') ||
      /hybrid[\s_-]*forged/i.test(this.dataset.productHandle || '') ||
      /hybrid[\s_-]*forged/i.test(this.dataset.productTitle || '');

    // Fully Forged Aventador Wheels are made to order for the selected car.
    // Do not require data-custom-bore here: some product templates omit that
    // attribute, which previously caused false bolt/ring and OEM warnings.
    const productHandle = String(this.dataset.productHandle || '').toLowerCase();
    const productVendor = String(this.dataset.productVendor || '').toLowerCase();
    const productTitle = String(this.dataset.productTitle || '').toLowerCase();

    this._isAventadorForged =
      !this._isHybridForged && (
        this.hasAttribute('data-aventador') ||
        productVendor.includes('aventador') ||
        productHandle.startsWith('aventador-wheels-forged-') ||
        productTitle.includes('aventador wheels forged')
      );

    // Hybrid-Forged has fixed variants and must open recommended fitment.
    if (this._isHybridForged) {
      this._openCustomFitFromUrl = false;
    }
    this._quickBuyMode = false;
    this._manualMode = false;
    this._hidePcdInManualMode = false;
    this._qbRearSelection = { pcd: null, size: null, et: null, exactEt: null };

    // Standalone Custom Fit service. It is bought directly from its own button,
    // independently of front/rear wheel purchases, and is kept to one cart item.
    this._centerCapAddonVariantId = null;
    this._centerCapAddonPrice = CENTER_CAP_ADDON_FALLBACK_PRICE;
    this._centerCapAddonLoadPromise = null;
    this._centerCapAddonEl = null;
    this._centerCapAddonInCart = false;
    this._centerCapAddonBusy = false;
    this._centerCapAddonCartChecked = false;

    // Selected OEM front/rear combination in Quick Buy.
    // Allows several OEM combinations for the same diameter, for example
    // 20x8.5 ET35 / 20x9 ET42 and 20x8.5 ET35 / 20x10 ET39.
    this._qbSelectedPairKey = null;
    this._qbActivePair = null;

    // Cache DOM refs
    this._stepsBar = this.querySelector('[data-role="steps-bar"]');
    this._vehicleBanner = this.querySelector('[data-role="vehicle-banner"]');
    this._titleBackEl = this.querySelector('[data-role="title-back"]');
    this._noMatchCards = this.querySelector('[data-role="no-match-cards"]');
    this._manualModeCard = this.querySelector('[data-role="manual-mode-card"]');
    this._axleToggleEl = this.querySelector('[data-role="axle-toggle"]');
    this._quickBuyEl = this.querySelector('[data-role="quick-buy"]');
    this._qbNoVehicleEl = this.querySelector('[data-role="qb-no-vehicle"]');
    this._qbDiamRow = this.querySelector('[data-role="qb-diam-row"]');
    this._qbOemCard = this.querySelector('[data-role="qb-oem-card"]');
    this._qbCustomCard = this.querySelector('[data-role="qb-custom-card"]');
    this._qbFinish = this.querySelector('[data-role="qb-finish"]');
    this._pcdGrid = this.querySelector('[data-role="pcd-grid"]');
    this._sizeGrid = this.querySelector('[data-role="size-grid"]');
    this._etArea = this.querySelector('[data-role="et-area"]');
    this._finishArea = this.querySelector('[data-role="finish-area"]');
    this._finishStep = this.querySelector('[data-step="finish"]');
    this._etHidden = this.querySelector('select[data-et-select]');
    this._finishHidden = this.querySelector('input[data-finish-input]');

    // Remove hidden selects/inputs so cart-properties.js doesn't validate them.
    // Our _addToCart sends properties directly — no interceptor needed.
    if (this._etHidden) this._etHidden.remove();
    if (this._finishHidden) this._finishHidden.remove();

    // Cart + compat section refs
    this._cartSection = this.querySelector('[data-role="wc-cart"]');
    this._compatCard = this.querySelector('[data-role="wc-compat-card"]');
    this._priceEl = this.querySelector('[data-role="wc-price"]');
    this._qtyInput = this.querySelector('[data-role="wc-qty-input"]');
    this._qtyWrap = this._qtyInput?.closest('.wc__qty');
    this._addBtn = this.querySelector('[data-role="wc-add-to-cart"]');
    this._cartError = this.querySelector('[data-role="wc-cart-error"]');

    // Compact standalone purchase card shown on Aventador Wheels OEM Fit and Custom Fit.
    this._initCenterCapAddon();

    // Move finish drawer to body so it escapes any parent stacking context
    const drawerBackdrop = this.querySelector('[data-role="wc-finish-backdrop"]');
    const drawerPanel = this.querySelector('[data-role="wc-finish-drawer"]');
    if (drawerBackdrop) document.body.appendChild(drawerBackdrop);
    if (drawerPanel) document.body.appendChild(drawerPanel);

    // Hide Horizon's variant-picker and buy-buttons
    this._hideHorizonPicker();
    this._hideBuyButtons();

    // Apply vehicle filter first
    this._applyVehicleFilter();

    // Initialize normal OEM/Quick Buy mode.
    this._initQuickBuyMode();

    // A Custom size selected on the search page opens the manual Custom Fit flow
    // immediately and keeps that exact diameter selected.
    if (this._openCustomFitFromUrl) {
      this._quickBuyMode = false;
      this._manualMode = true;
      this._hidePcdInManualMode = true;
      this._selectedDiameter = this._urlDiameter;
      this._autoSelectManualPcd();
      this._applyVehicleFilter();
    }

    // Eagerly fetch per-diameter specs for width/ET filtering
    this._fetchDiamSpecs();

    // Initial render
    this._render();

    // Fetch all product data in one call (variants + bore + Shopify IDs)
    // Defer data-ready until API data arrives (prevents flash of wrong state)
    this._readyTimeout = setTimeout(() => this._reveal(), 2000);
    this._fetchProductData()
      .then(() => this._hydrateHybridShopifyVariants())
      .finally(() => this._reveal());

    // AI Visualizer
    this._initAiVisualizer();
    this._placeAventadorFlowButtons();

    // Quick Buy: custom/aggressive → switch to full manual mode
    this.querySelector('[data-role="qb-custom-toggle"]')?.addEventListener('click', () => {
      this._enterManualMode({ prefillRearFromQb: true });
    });
    this.querySelector('[data-role="manual-mode-toggle"]')
      ?.addEventListener('click', () => this._enterManualMode({ hidePcd: true }));
    this._qbNoVehicleEl?.querySelector('[data-role="qb-add-vehicle"]')
      ?.addEventListener('click', () => this._openVehicleModal());

    // Listen for events
    this._boundOnVehicleChanged = () => {
      this._vehicle = getVehicle();
      this._manualMode = false;
      this._hidePcdInManualMode = false;
      this._specsFetched = false;
      this._diamSpecs = new Map();
      this._diamSpecsData = null;
      this._qbSelectedPairKey = null;
      this._qbActivePair = null;
      this._applyVehicleFilter();
      this._initQuickBuyMode();
      this._fetchDiamSpecs();
      this._render();
      if (this._vehicle?.specs && !this._quickBuyMode) {
        this._preselectBestVariant();
      }
    };
    this._boundOnBoreLoaded = (e) => {
      if (e.detail?.boreByPcd) {
        Object.assign(this._boreByPcd, e.detail.boreByPcd);
      } else if (e.detail?.boreData) {
        for (const entry of e.detail.boreData) {
          if (entry.pcd) this._boreByPcd[entry.pcd] = entry;
        }
      }
      this._renderCompatInfo();
    };
    window.addEventListener('vehicle:changed', this._boundOnVehicleChanged);
    window.addEventListener('bore:loaded', this._boundOnBoreLoaded);

    // Bore data is fetched by _fetchProductData() above

    // Listen for Horizon variant:update to stay in sync
    this._sectionRoot = this.closest('.shopify-section, dialog');
    if (this._sectionRoot) {
      this._boundOnVariantUpdate = () => {
        requestAnimationFrame(() => this._syncFromHorizon());
      };
      this._sectionRoot.addEventListener('variant:update', this._boundOnVariantUpdate);
    }

    // Bind modal + drawer + cart events
    this._bindModalEvents();
    this._bindDrawerEvents();
    this._bindCartEvents();

    // Check if returning from Vipps credit purchase
    this._checkVippsReturn();
  }

  disconnectedCallback() {
    if (this._readyTimeout) clearTimeout(this._readyTimeout);
    window.removeEventListener('vehicle:changed', this._boundOnVehicleChanged);
    window.removeEventListener('bore:loaded', this._boundOnBoreLoaded);
    if (this._sectionRoot && this._boundOnVariantUpdate) {
      this._sectionRoot.removeEventListener('variant:update', this._boundOnVariantUpdate);
    }

    // Release the current generated Blob only when the whole component is removed.
    const resultImg = this._aiModal?.querySelector('[data-role="ai-result-img"]');
    if (resultImg) {
      resultImg.onload = null;
      resultImg.onerror = null;
      resultImg.removeAttribute('srcset');
      resultImg.removeAttribute('src');
    }
    const staleResultObjectUrl = this._aiResultObjectUrl;
    this._aiResultObjectUrl = null;
    if (staleResultObjectUrl) {
      try { URL.revokeObjectURL(staleResultObjectUrl); } catch { /* ignore */ }
    }

    // Clean up drawer elements moved to body
    document.querySelector('[data-role="wc-finish-backdrop"]')?.remove();
    document.querySelector('[data-role="wc-finish-drawer"]')?.remove();

    // The premium AI CTA is intentionally moved outside this custom element.
    // Remove it when Shopify unloads the block so theme-editor reloads cannot duplicate it.
    if (this._aiBtn && !this.contains(this._aiBtn)) {
      try { this._aiBtn.remove(); } catch { /* ignore */ }
    }
    // Restore Horizon picker + buy-buttons visibility
    const picker = this._getHorizonPicker();
    if (picker) picker.style.display = '';
    const section = this.closest('.shopify-section') || document;
    const buyBtns = section.querySelector('buy-buttons, .buy-buttons, [data-buy-buttons]');
    if (buyBtns) buyBtns.style.display = '';
  }

  /* ================================================================
   *  Data parsing
   * ================================================================ */

  /** Option key used as universal internal ID for variants and inventory */
  _variantKey(v) {
    return `${(v.option1 || '').trim()}|${(v.option2 || '').trim()}|${(v.option3 || '').trim()}`;
  }

  _parseData() {
    // Liquid variants for fast initial render (capped at 250 by Shopify).
    // _fetchFullVariants() replaces with complete data from our API (including real Shopify IDs).
    const varScript = document.querySelector('script[data-product-variants]');
    let liquidVariants = [];
    try { liquidVariants = JSON.parse(varScript?.textContent || '[]'); }
    catch { /* ignore */ }

    // Keep Shopify-rendered variant data as the authoritative storefront context.
    // For logged-in B2B customers, Liquid variant.price / compare_at_price already
    // contain the customer's contextual catalog pricing. External wheel APIs must
    // never overwrite these values.
    this._allVariants = liquidVariants.map(v => ({
      ...v,
      _shopifyContextualPrice: Number(v.price) || 0,
      _shopifyContextualCompareAtPrice: Number(v.compare_at_price) || 0,
      _hasShopifyContext: true,
    }));

    // Inventory keyed by option key (PCD|Size|ET)
    const invScript = this.querySelector('script[data-wc-inventory]') ||
      document.querySelector('script[data-wc-inventory]');
    let invRaw = [];
    try { invRaw = JSON.parse(invScript?.textContent || '[]'); } catch { /* ignore */ }

    this._inventory = {};
    for (const item of invRaw) {
      // Find matching variant to compute option key
      const variant = liquidVariants.find(v => v.id === item.id);
      const key = variant ? this._variantKey(variant) : item.id;
      this._inventory[key] = { ...item, id: key };
    }
  }

  /** Resolve Shopify numeric variant ID for cart operations */
  _resolveVariantId(variant) {
    if (!variant) return null;
    // Primary: variant.id from merged product-data endpoint (covers ALL variants)
    if (variant.id && typeof variant.id === 'number') return variant.id;
    // Fallback: URL param (Horizon sets ?variant=ID after driving picker)
    return new URLSearchParams(window.location.search).get('variant');
  }

  /** Reveal configurator content (called once after API or timeout). */
  _reveal() {
    if (this.hasAttribute('data-ready')) return; // already revealed
    clearTimeout(this._readyTimeout);

    // Auto-select best variant if vehicle is saved (runs after API data)
    if (this._vehicle?.specs) {
      if (!this._quickBuyMode) {
        this._preselectBestVariant();
      }
      if (this._isVehicleStaggered() && this._qtyInput) {
        this._qtyInput.value = 2;
        this._updatePrice();
      }
    } else if (!this._quickBuyMode) {
      this._autoSelectFirst();
    }

    this.setAttribute('data-ready', '');
  }

  _isWheeltradeProduct() {
    if (this.hasAttribute('data-wheeltrade')) return true;
    const vendor = (this.dataset.productVendor || '').trim().toLowerCase();
    return /wheel[\s_-]*trade/.test(vendor) || vendor.includes('wheeltrade');
  }

  _enterManualMode({ prefillRearFromQb = false, hidePcd = false } = {}) {
    // Preserve the diameter chosen on the search page when opening Custom Fit.
    // Example: ?diameter=22 opens Custom Fit with 22" selected and highlighted.
    const requestedDiameter = Number(this._urlDiameter);
    if (Number.isFinite(requestedDiameter) && requestedDiameter > 0) {
      const diameterExists = this._allVariants.some(variant =>
        Math.round(this._variantDiameter(variant) || 0) === Math.round(requestedDiameter)
      );
      if (diameterExists) {
        this._selectedDiameter = Math.round(requestedDiameter);
        this._selectedSize = null;
        this._selectedEt = null;
        this._selectedExactEt = null;
      }
    }

    if (prefillRearFromQb && this._isVehicleStaggered() && this._qbRearSelection.pcd) {
      this._axleSelections.rear = {
        pcd: this._qbRearSelection.pcd,
        diameter: this._selectedDiameter,
        size: this._qbRearSelection.size,
        et: this._qbRearSelection.et,
        exactEt: this._qbRearSelection.exactEt,
        finish: this._selectedFinish || null,
        savedByDiam: {},
      };
    }

    // Fully switch to manual mode — show ALL variants, not just vehicle-compatible
    this._quickBuyMode = false;
    this._manualMode = true;
    this._hidePcdInManualMode = !!hidePcd;
    if (this._hidePcdInManualMode) this._autoSelectManualPcd();
    this._applyVehicleFilter();
    this._render();
  }

  _autoSelectManualPcd() {
    const vehicleMatchedPcd = this._getVehicleMatchedPcd();
    if (vehicleMatchedPcd) {
      this._selectedPcd = vehicleMatchedPcd;
      return;
    }

    if (this._selectedPcd && this._pcdSet?.has(this._selectedPcd)) return;

    if (this._pcdSet?.has('custom')) {
      this._selectedPcd = 'custom';
      return;
    }

    const firstPcd = this._pcdSet?.keys()?.next?.().value;
    if (firstPcd) this._selectedPcd = firstPcd;
  }

  _getVehicleMatchedPcd() {
    const vehiclePcd = (this._vehicle?.specs?.pcd || '').trim().toLowerCase();
    if (!vehiclePcd || !this._pcdSet?.size) return null;
    for (const pcd of this._pcdSet.keys()) {
      if ((pcd || '').trim().toLowerCase() === vehiclePcd) return pcd;
    }
    return null;
  }

  _hasManualOptionsForVehiclePcd() {
    const matchedPcd = this._getVehicleMatchedPcd();
    if (!matchedPcd) return false;
    const pcdData = this._pcdSet?.get(matchedPcd);
    if (!pcdData?.sizes?.size) return false;
    for (const size of pcdData.sizes) {
      const sizeKey = `${matchedPcd}|${size}`;
      const sizeData = this._sizeSet?.get(sizeKey);
      if (sizeData?.variants?.length) return true;
    }
    return false;
  }

  async _fetchProductData() {
    const handle = this.dataset.productHandle;
    if (!handle) return;
    try {
      const res = await fetch(
        `${API_BASE}/api/wheels/product-data?handle=${encodeURIComponent(handle)}`
      );
      if (!res.ok) return;
      const data = await res.json();
      if (!data.success) return;

      // Variants (with Shopify IDs merged in)
      if (data.variants?.length) {
        // Preserve real physical Hybrid-Forged fitment data from the server.
        // Shopify uses PCD "Custom" for purchase variants, but recommendation
        // logic must use the actual PCD, width and ET combinations.
        if (this._isHybridForged) {
          this._hybridFitmentVariants = data.variants.map(variant => ({
            ...variant,
            option1: String(variant.option1 || '').trim(),
            option2: String(variant.option2 || '').trim(),
            option3: String(variant.option3 || '').trim(),
          }));
        }

        const previousByKey = new Map(
          (this._allVariants || []).map(v => [this._variantKey(v), v])
        );

        this._allVariants = data.variants.map(v => {
          const previous = previousByKey.get(this._variantKey(v)) || {};
          const hasShopifyContext = previous._hasShopifyContext === true;
          const contextualPrice = Number(previous._shopifyContextualPrice);
          const contextualCompareAtPrice = Number(previous._shopifyContextualCompareAtPrice);

          return {
            ...previous,
            ...v,
            id: v.id ?? previous.id ?? null,
            title: `${v.option1} / ${v.option2} / ${v.option3}`,
            option1: v.option1,
            option2: v.option2,
            option3: v.option3,
            available: v.available,

            // PRICE SOURCE OF TRUTH:
            // Shopify/Liquid is contextualized for the active buyer (including B2B
            // company catalog pricing). The external wheel API is only a fallback
            // for variants that were not present in the Liquid payload.
            price: hasShopifyContext && contextualPrice > 0
              ? contextualPrice
              : Number(v.price) || Number(previous.price) || 0,
            compare_at_price: hasShopifyContext
              ? (Number.isFinite(contextualCompareAtPrice) ? contextualCompareAtPrice : 0)
              : Number(v.compare_at_price) || Number(previous.compare_at_price) || 0,
            _shopifyContextualPrice: hasShopifyContext
              ? contextualPrice
              : Number(previous._shopifyContextualPrice) || 0,
            _shopifyContextualCompareAtPrice: hasShopifyContext
              ? contextualCompareAtPrice
              : Number(previous._shopifyContextualCompareAtPrice) || 0,
            _hasShopifyContext: hasShopifyContext,

            max_load: v.max_load ?? v.maxLoad ?? previous.max_load ?? previous.maxLoad ?? null,
            tuv_max_load: v.tuv_max_load ?? v.tuvMaxLoad ?? previous.tuv_max_load ?? previous.tuvMaxLoad ?? null,
          };
        });

        this._inventory = {};
        for (const v of data.variants) {
          const key = this._variantKey(v);
          this._inventory[key] = {
            id: key,
            qty: v.stock ?? 0,
            managed: v.available ? 'shopify' : 'not_managed',
            available: v.available,
            inventoryPolicy: v.inventory_policy ?? 'deny',
            max_load: v.max_load ?? v.maxLoad ?? null,
            tuv_max_load: v.tuv_max_load ?? v.tuvMaxLoad ?? null,
          };
        }

        this._buildOptionSets();
        this._applyVehicleFilter();
        this._render();
        if (this._vehicle?.specs && !this._quickBuyMode) {
          this._preselectBestVariant();
        }
      }

      // Bore data
      if (data.bore?.length) {
        for (const row of data.bore) {
          this._boreByPcd[row.pcd.trim().toLowerCase()] = {
            center_bore: row.center_bore || 0,
            center_bore_max: row.center_bore_max || 0,
          };
        }
        this._renderCompatInfo();
        // Dispatch bore:loaded so vehicle-compatibility.js + wheel-specs.js
        // don't need to make their own fetch
        window.dispatchEvent(new CustomEvent('bore:loaded', {
          detail: { boreByPcd: this._boreByPcd }
        }));
      }
    } catch (e) {
      console.error('Failed to fetch product data', e);
    }
  }

  /**
   * Hybrid-Forged Shopify products have fixed, live variants.
   * The wheel API can temporarily return zero prices or missing Shopify IDs
   * after sync changes. Fetch the storefront product JSON and merge the live
   * variant ID, price, compare-at price, availability and inventory policy.
   */
  _getHybridForgedPriceBySize(sizeValue) {
    const size = String(sizeValue || '').replace(/\s+/g, '').toLowerCase();

    if (size.includes('20x8.5')) {
      return {
        price: 340000,
        compareAtPrice: 619900,
      };
    }

    if (size.includes('20x10')) {
      return {
        price: 355000,
        compareAtPrice: 679900,
      };
    }

    return null;
  }

  async _hydrateHybridShopifyVariants() {
    if (!this._isHybridForged) return;

    const handle = String(this.dataset.productHandle || '')
      .trim()
      .replace(/^\/?products\//, '')
      .split('?')[0];

    if (!handle) return;

    try {
      const response = await fetch(
        `/products/${encodeURIComponent(handle)}.js`,
        {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        }
      );

      if (!response.ok) {
        throw new Error(`Shopify product JSON returned ${response.status}`);
      }

      const product = await response.json();
      const liveVariants = Array.isArray(product?.variants)
        ? product.variants
        : [];

      if (!liveVariants.length) {
        throw new Error('No live Shopify variants found');
      }

      const normalize = value =>
        String(value || '')
          .trim()
          .toLowerCase()
          .replace(/\s+/g, '')
          .replace(/[″"]/g, '');

      const parseSize = value => {
        const match = String(value || '').match(/(\d{2}(?:\.\d+)?)\s*[xX]\s*(\d+(?:\.\d+)?)/);
        return match
          ? `${Number(match[1])}x${Number(match[2])}`
          : '';
      };

      const parseEt = value => {
        const match = String(value || '').match(/ET\s*(-?\d+)/i);
        return match ? `et${Number(match[1])}` : '';
      };

      const liveRecords = liveVariants.map(variant => {
        const options = Array.isArray(variant.options)
          ? variant.options
          : [variant.option1, variant.option2, variant.option3].filter(Boolean);

        const combined = [
          ...options,
          variant.title,
          variant.name,
        ].filter(Boolean).join(' | ');

        return {
          variant,
          exactKey: options.slice(0, 3).map(normalize).join('|'),
          size: parseSize(combined),
          et: parseEt(combined),
          normalizedText: normalize(combined),
        };
      });

      const findLiveVariant = existing => {
        const exactKey = [
          existing.option1,
          existing.option2,
          existing.option3,
        ].map(normalize).join('|');

        let record = liveRecords.find(item => item.exactKey === exactKey);
        if (record) return record.variant;

        const existingSize = parseSize(existing.option2 || existing.title || '');
        const existingEt = parseEt(existing.option3 || existing.title || '');

        if (existingSize && existingEt) {
          record = liveRecords.find(
            item => item.size === existingSize && item.et === existingEt
          );
          if (record) return record.variant;
        }

        if (existingSize) {
          const sameSize = liveRecords.filter(item => item.size === existingSize);
          if (sameSize.length === 1) return sameSize[0].variant;
        }

        const existingText = normalize(
          `${existing.option1 || ''}|${existing.option2 || ''}|${existing.option3 || ''}`
        );

        record = liveRecords.find(
          item =>
            item.normalizedText.includes(existingText) ||
            existingText.includes(item.normalizedText)
        );

        return record?.variant || null;
      };

      let mergedCount = 0;

      this._allVariants = (this._allVariants || []).map(existing => {
        const live = findLiveVariant(existing);
        if (!live) return existing;

        mergedCount += 1;

        const livePrice = Number(live.price);
        const liveCompare = Number(live.compare_at_price);
        const liveOptions = Array.isArray(live.options)
          ? live.options
          : [live.option1, live.option2, live.option3];

        const liveOption1 = liveOptions[0] || existing.option1 || 'Custom';
        const liveOption2 = liveOptions[1] || existing.option2 || '';
        const liveOption3 = liveOptions[2] || existing.option3 || '';

        const agreedPrice = this._getHybridForgedPriceBySize(
          liveOption2 || existing.option2 || live.title
        );
        const hasShopifyContext = existing._hasShopifyContext === true;
        const contextualPrice = Number(existing._shopifyContextualPrice);
        const contextualCompareAtPrice = Number(existing._shopifyContextualCompareAtPrice);

        return {
          ...existing,
          id: Number(live.id) || existing.id || null,
          title: live.title || `${liveOption1} / ${liveOption2} / ${liveOption3}`,
          option1: liveOption1,
          option2: liveOption2,
          option3: liveOption3,

          // Preserve Shopify's buyer-context price (B2B catalog included).
          // Ajax/live price is the normal fallback; the old agreed price is only
          // used if Shopify supplies no usable price at all.
          price: hasShopifyContext && contextualPrice > 0
            ? contextualPrice
            : (Number.isFinite(livePrice) && livePrice > 0
              ? livePrice
              : (Number(existing.price) > 0
                ? Number(existing.price)
                : (agreedPrice?.price || 0))),
          compare_at_price: hasShopifyContext
            ? (Number.isFinite(contextualCompareAtPrice) ? contextualCompareAtPrice : 0)
            : (Number.isFinite(liveCompare) && liveCompare > 0
              ? liveCompare
              : (Number(existing.compare_at_price) > 0
                ? Number(existing.compare_at_price)
                : (agreedPrice?.compareAtPrice || 0))),
          available: live.available !== false,
          inventory_policy:
            live.inventory_policy ||
            existing.inventory_policy ||
            'continue',
        };
      });

      // If the wheel API returned no usable rows, build directly from Shopify.
      if (!mergedCount) {
        this._allVariants = liveVariants.map(live => {
          const options = Array.isArray(live.options)
            ? live.options
            : [live.option1, live.option2, live.option3];

          const agreedPrice = this._getHybridForgedPriceBySize(
            options[1] || live.title
          );

          return {
            id: Number(live.id) || null,
            title: live.title || options.filter(Boolean).join(' / '),
            option1: options[0] || '5x112',
            option2: options[1] || '',
            option3: options[2] || '',
            price:
              (Number(live.price) > 0
                ? Number(live.price)
                : (agreedPrice?.price || 0)),
            compare_at_price:
              (Number(live.compare_at_price) > 0
                ? Number(live.compare_at_price)
                : (agreedPrice?.compareAtPrice || 0)),
            available: live.available !== false,
            inventory_policy: live.inventory_policy || 'continue',
            max_load: 900,
          };
        });
      }

      this._inventory = {};
      for (const variant of this._allVariants) {
        const key = this._variantKey(variant);
        this._inventory[key] = {
          id: key,
          qty: variant.available ? 1 : 0,
          managed: variant.available ? 'shopify' : 'not_managed',
          available: variant.available !== false,
          inventoryPolicy:
            variant.inventory_policy ||
            variant.inventoryPolicy ||
            'continue',
          max_load: variant.max_load ?? variant.maxLoad ?? 900,
          tuv_max_load: variant.tuv_max_load ?? variant.tuvMaxLoad ?? null,
        };
      }

      this._buildOptionSets();

      // Clear stale selections based on API PCD values such as 5x112.
      // The live Hybrid-Forged Shopify product uses PCD = Custom.
      this._selectedPcd = null;
      this._selectedSize = null;
      this._selectedEt = null;
      this._selectedExactEt = null;
      this._qbRearSelection = {
        pcd: null,
        size: null,
        et: null,
        exactEt: null,
      };

      this._applyVehicleFilter();
      this._initQuickBuyMode();

      if (this._quickBuyMode && this._vehicle?.specs) {
        this._qbAutoSelect();
      }

      this._render();
    } catch (error) {
      console.error('Could not hydrate live Hybrid-Forged variants', error);

      // Never leave the customer on a permanent loading state.
      // Fall back to existing API prices when they are available.
      const pricedVariant = (this._allVariants || []).find(
        variant => Number(variant.price) > 0
      );

      if (pricedVariant && this._priceEl) {
        this._render();
      } else if (this._priceEl) {
        this._priceEl.innerHTML = `
          <div class="wc__price-current">
            <span class="wc__price-current-label">Pris for komplett sett:</span>
            <strong class="wc__price-current-value">Pris midlertidig utilgjengelig</strong>
          </div>`;
      }

      if (this._addBtn) {
        this._addBtn.disabled = true;
        const buttonText =
          this._addBtn.querySelector('.wc__add-btn-top') ||
          this._addBtn.querySelector('[data-role="wc-add-btn-text"]');
        if (buttonText) buttonText.textContent = 'PRØV Å LASTE SIDEN PÅ NYTT';
      }
    }
  }

  _buildOptionSets() {
    // Option1 = PCD, Option2 = Size, Option3 = ET
    this._pcdSet = new Map();   // pcd → { count, sizes }
    this._sizeSet = new Map();  // "pcd|size" → { variants }
    this._etSet = new Map();    // "pcd|size|et" → variant

    for (const v of this._allVariants) {
      const pcd = (v.option1 || '').trim();
      const size = (v.option2 || '').trim();
      const et = (v.option3 || '').trim();
      if (!pcd) continue;

      if (!this._pcdSet.has(pcd)) {
        this._pcdSet.set(pcd, { count: 0, sizes: new Set() });
      }
      const pcdEntry = this._pcdSet.get(pcd);
      pcdEntry.count++;
      if (size) pcdEntry.sizes.add(size);

      if (size) {
        const sizeKey = `${pcd}|${size}`;
        if (!this._sizeSet.has(sizeKey)) {
          this._sizeSet.set(sizeKey, { variants: [] });
        }
        this._sizeSet.get(sizeKey).variants.push(v);
      }

      if (et) {
        const etKey = `${pcd}|${size}|${et}`;
        this._etSet.set(etKey, v);
      }
    }
  }

  _detectEtMode() {
    // Global fallback — used for products where ALL variants share the same mode
    const firstEt = (this._allVariants[0]?.option3 || '').trim();
    const stripped = firstEt.replace(/\s*\(.*\)/, '').replace('ET', '');
    this._etMode = stripped.includes('-') ? 'range' : 'single';
  }

  _parseEtBounds(etStr) {
    const cleaned = String(etStr || '').replace(/\s*\(.*\)\s*$/, '').trim();
    if (!cleaned) return null;
    const m = cleaned.match(/^(?:ET)?\s*(-?\d+)(?:\s*-\s*(-?\d+))?$/i);
    if (!m) return null;
    const min = parseInt(m[1], 10);
    const max = m[2] !== undefined ? parseInt(m[2], 10) : min;
    if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
    return {
      min: Math.min(min, max),
      max: Math.max(min, max),
    };
  }

  /** Check if a specific ET string is a range (e.g. "ET20-38") */
  _isEtRange(etStr) {
    const bounds = this._parseEtBounds(etStr);
    return !!(bounds && bounds.min !== bounds.max);
  }

  /** Check if the currently selected ET is a range */
  get _isSelectedEtRange() {
    return this._selectedEt ? this._isEtRange(this._selectedEt) : false;
  }

  /** Parse ET string → { lo, hi } or null for non-ranges */
  _parseEtRange(etStr) {
    const bounds = this._parseEtBounds(etStr);
    if (!bounds || bounds.min === bounds.max) return null;
    return { lo: bounds.min, hi: bounds.max };
  }

  /**
   * Mutates etOptions in-place: removes range entries whose [lo,hi] is fully
   * contained within another range. Single ET values (non-ranges) are kept.
   */
  _removeSubsetRanges(etOptions) {
    const ranges = etOptions
      .map((o, i) => ({ idx: i, ...this._parseEtRange(o.et) }))
      .filter(r => r.lo !== undefined && r.lo !== null && r.hi !== undefined);
    if (ranges.length < 2) return;

    const toRemove = new Set();
    for (const a of ranges) {
      for (const b of ranges) {
        if (a.idx === b.idx) continue;
        // a is a subset of b if b.lo <= a.lo && a.hi <= b.hi and b is strictly wider
        if (b.lo <= a.lo && a.hi <= b.hi && (b.hi - b.lo) > (a.hi - a.lo)) {
          toRemove.add(a.idx);
        }
      }
    }
    if (toRemove.size === 0) return;
    for (const idx of [...toRemove].sort((a, b) => b - a)) {
      etOptions.splice(idx, 1);
    }
  }


  /* ================================================================
   *  Load capacity filtering
   * ================================================================ */

  _firstPositiveNumber(values) {
    for (const value of values) {
      const parsed = Number.parseFloat(value);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
    return 0;
  }

  _getRequiredWheelLoad(axle = 'both') {
    const vehicle = this._vehicle || {};
    const specs = vehicle.specs || {};

    // Manual vehicle selections can contain tyre load-index/load fields that
    // are not the car's required wheel load. Only enforce the wheel-load gate
    // when the car came from the Norwegian registration lookup.
    const hasAuthoritativeRegistrationLoad =
      !!String(vehicle.regnr || '').trim() ||
      vehicle.regnrSource === 'vegvesen' ||
      String(vehicle.trimId || '').startsWith('regnr:');

    if (!hasAuthoritativeRegistrationLoad) {
      return 0;
    }

    const frontPerWheel = this._firstPositiveNumber([
      specs.maxLoadFront,
      specs.max_load_front,
      specs.requiredLoadFront,
      specs.required_load_front,
      specs.wheelLoadFront,
      specs.wheel_load_front,
      specs.load_front,
      specs.front_load,
    ]);

    const rearPerWheel = this._firstPositiveNumber([
      specs.maxLoadRear,
      specs.max_load_rear,
      specs.requiredLoadRear,
      specs.required_load_rear,
      specs.wheelLoadRear,
      specs.wheel_load_rear,
      specs.load_rear,
      specs.rear_load,
    ]);

    const frontAxle = this._firstPositiveNumber([
      specs.maxAxleLoadFront,
      specs.max_axle_load_front,
      specs.axleLoadFront,
      specs.axle_load_front,
      specs.front_axle_load,
      specs.tillatt_foraksel_last,
    ]);

    const rearAxle = this._firstPositiveNumber([
      specs.maxAxleLoadRear,
      specs.max_axle_load_rear,
      specs.axleLoadRear,
      specs.axle_load_rear,
      specs.rear_axle_load,
      specs.tillatt_bakaksel_last,
    ]);

    const commonPerWheel = this._firstPositiveNumber([
      specs.requiredLoad,
      specs.required_load,
      specs.maxLoad,
      specs.max_load,
      specs.wheelLoad,
      specs.wheel_load,
    ]);

    const front = frontPerWheel || (frontAxle > 0 ? frontAxle / 2 : 0) || commonPerWheel;
    const rear = rearPerWheel || (rearAxle > 0 ? rearAxle / 2 : 0) || commonPerWheel;

    if (axle === 'front') return front || rear || commonPerWheel || 0;
    if (axle === 'rear') return rear || front || commonPerWheel || 0;
    return Math.max(front || 0, rear || 0, commonPerWheel || 0);
  }

  _getVariantMaxLoad(variant) {
    if (!variant) return 0;
    const inventory = this._inventory?.[this._variantKey(variant)] || {};

    return this._firstPositiveNumber([
      variant.max_load,
      variant.maxLoad,
      variant.tuv_max_load,
      variant.tuvMaxLoad,
      variant.wheel_load,
      variant.wheelLoad,
      variant.load_capacity,
      variant.loadCapacity,
      variant.capacity,
      variant.load,
      inventory.max_load,
      inventory.maxLoad,
      inventory.tuv_max_load,
      inventory.tuvMaxLoad,
    ]);
  }

  _isVariantLoadCompatible(variant, axle = 'both') {
    if (!this._vehicle?.specs || !variant) return true;

    // Fully Forged Aventador configurations are produced for the chosen car.
    // Their Shopify variants are pricing/manufacturing variants, not fixed fitments.
    // Hybrid-Forged is excluded by _isAventadorForged detection.
    if (this._isAventadorForged) return true;

    const required = this._getRequiredWheelLoad(axle);
    const capacity = this._getVariantMaxLoad(variant);

    // Do not reject products where the API has not supplied load data.
    if (!(required > 0) || !(capacity > 0)) return true;
    return capacity + 0.001 >= required;
  }

  _getVariantLoadFailure(variant, axle = 'both') {
    // Fully Forged Aventador Wheels are produced specifically for the selected
    // vehicle. The generic Shopify variant load value must therefore not block
    // the OEM made-to-order configuration or disable the purchase button.
    // Hybrid-Forged is excluded because _isAventadorForged is false for it.
    if (this._isAventadorForged) return null;

    const required = this._getRequiredWheelLoad(axle);
    const capacity = this._getVariantMaxLoad(variant);

    if (!(required > 0) || !(capacity > 0) || capacity + 0.001 >= required) {
      return null;
    }

    return { required, capacity, axle };
  }

  _showLoadError(failure) {
    if (!failure || !this._cartError) return;

    const required = Math.ceil(failure.required);
    const capacity = Math.floor(failure.capacity);
    const axleText = failure.axle === 'front'
      ? 'foran'
      : failure.axle === 'rear'
        ? 'bak'
        : 'på bilen';

    this._cartError.hidden = false;
    this._cartError.textContent =
      `Denne felgen er godkjent for maks ${capacity} kg, men bilen krever minst ${required} kg ${axleText}. Velg en annen størrelse.`;
  }

  /* ================================================================
   *  Vehicle filtering
   * ================================================================ */

  /** Return OEM specs for the active axle (front or rear). */
  _getAxleSpecs() {
    const s = this._vehicle?.specs;
    if (!s) return { diameter: 0, diameterMin: 0, diameterMax: 0, width: 0, widthMax: 0, et: null };
    if (this._activeAxle === 'rear') {
      return {
        diameter: s.diameter_rear ?? s.diameter_front ?? 0,
        diameterMin: s.diameter_min ?? s.diameter_rear ?? s.diameter_front ?? 0,
        diameterMax: s.diameter_max ?? 0,
        width: s.width_rear ?? s.width_front ?? 0,
        widthMax: s.width_max_rear ?? s.width_rear ?? s.width_front ?? 0,
        et: s.et_rear ?? s.et_front ?? null,
      };
    }
    return {
      diameter: s.diameter_front ?? s.oem_diameter ?? 0,
      diameterMin: s.diameter_min ?? s.diameter_front ?? 0,
      diameterMax: s.diameter_max ?? 0,
      width: s.width_front ?? 0,
      widthMax: s.width_max_front ?? s.width_front ?? 0,
      et: s.et_front ?? s.et ?? null,
    };
  }

  _isVehicleStaggered() {
    const specs = this._vehicle?.specs;
    if (specs?.is_staggered) return true;

    // Også staggered hvis front og bak har forskjellig diameter
    const fDiam = Math.round(specs?.diameter_front || specs?.oem_diameter || 0);
    const rDiam = Math.round(specs?.diameter_rear || specs?.rim_diameter_r || 0);
    if (fDiam && rDiam && fDiam !== rDiam) return true;

if (!this._diamSpecs || this._diamSpecs.size === 0) return false;

    for (const entry of this._diamSpecs.values()) {
      const f = entry?.front;
      const r = entry?.rear;
      if (!f || !r) continue;

      const fWidths = [...(f.widths || [])].filter(n => Number.isFinite(n));
      const rWidths = [...(r.widths || [])].filter(n => Number.isFinite(n));
      if (fWidths.length && rWidths.length) {
        const fSet = new Set(fWidths.map(n => Number(n.toFixed(3))));
        const rSet = new Set(rWidths.map(n => Number(n.toFixed(3))));
        const sameWidths = fSet.size === rSet.size && [...fSet].every(v => rSet.has(v));
        if (!sameWidths) return true;
      }

      const fEt = Number.isFinite(f.et) ? f.et : null;
      const rEt = Number.isFinite(r.et) ? r.et : null;
      if (fEt !== null && rEt !== null && Math.abs(fEt - rEt) > 0.001) return true;
    }

    return false;
  }

  /** Switch active axle (staggered vehicles only). */
  _onAxleSwitch(axle) {
    if (axle === this._activeAxle) return;
    // Save current selections
    this._axleSelections[this._activeAxle] = {
      pcd: this._selectedPcd,
      diameter: this._selectedDiameter,
      size: this._selectedSize,
      et: this._selectedEt,
      exactEt: this._selectedExactEt,
      finish: this._selectedFinish,
      savedByDiam: this._savedByDiam || {},
    };
    this._activeAxle = axle;
    // Restore or preselect
    const saved = this._axleSelections[axle];
    if (saved && saved.pcd) {
      this._selectedPcd = saved.pcd;
      this._selectedDiameter = saved.diameter;
      this._selectedSize = saved.size;
      this._selectedEt = saved.et;
      this._selectedExactEt = saved.exactEt;
      this._selectedFinish = saved.finish || this._selectedFinish;
      this._savedByDiam = saved.savedByDiam || {};
    } else {
      this._selectedPcd = null;
      this._selectedDiameter = null;
      this._selectedSize = null;
      this._selectedEt = null;
      this._selectedExactEt = null;
      this._savedByDiam = {};
    }
    this._applyVehicleFilter();
    this._render();
    if (!saved?.pcd && this._vehicle?.specs) {
      if (!this._quickBuyMode) {
        this._preselectBestVariant();
      }
    } else if (this._selectedEt) {
      this._driveHorizonPicker();
    }
    if (this._qtyInput) {
      this._qtyInput.value = 2;
      this._updatePrice();
    }
  }

  _applyVehicleFilter() {
    const vehicle = this._vehicle;
    if (!vehicle?.specs) {
      this._filteredVariants = this._allVariants;
      this._hiddenPcds = new Set();
      this._hiddenSizes = new Set();
      window.dispatchEvent(new CustomEvent('vehicle-filter:cleared'));
      return;
    }

    const specs = vehicle.specs;
    const compatible = this._manualMode
      ? this._allVariants.filter(v => this._isVariantLoadCompatible(v, this._activeAxle))
      : this._allVariants.filter(v => this._isVariantCompatible(v, specs));

    if (compatible.length === 0) {
      this._filteredVariants = this._allVariants;
      this._hiddenPcds = new Set();
      this._hiddenSizes = new Set();
      window.dispatchEvent(new CustomEvent('vehicle-filter:no-match'));
      return;
    }

    window.dispatchEvent(new CustomEvent('vehicle-filter:match'));
    this._filteredVariants = compatible;

    // Determine which PCDs and sizes are compatible
    const okPcds = new Set();
    const okSizes = new Set();
    for (const v of compatible) {
      if (v.option1) okPcds.add(v.option1.trim());
      if (v.option2) okSizes.add(v.option2.trim());
    }

    this._hiddenPcds = new Set();
    for (const pcd of this._pcdSet.keys()) {
      if (!okPcds.has(pcd)) this._hiddenPcds.add(pcd);
    }
    this._hiddenSizes = new Set();
    // Sizes depend on PCD context, computed during render
  }

  _isVariantCompatible(variant, specs) {
    // Load capacity must be sufficient for the active axle.
    if (!this._isVariantLoadCompatible(variant, this._activeAxle)) return false;

    // PCD: exact match (or "custom" matches everything)
    if (specs.pcd) {
      const vPcd = (variant.option1 || '').trim().toLowerCase();
      const vehiclePcd = specs.pcd.trim().toLowerCase();
      if (vPcd && vPcd !== vehiclePcd && vPcd !== 'custom') return false;
    }

    // Diameter: must be within vehicle's full spec range (fallback: OEM ± tolerance)
    // Expand range to include URL diameter from vehicle search if present
    const sizeStr = variant.option2 || '';
    const diamMatch = sizeStr.match(/^(\d+(?:\.\d+)?)/);
    if (diamMatch) {
      const diameter = parseFloat(diamMatch[1]);
      const axle = this._getAxleSpecs();
      const minDiam = axle.diameterMin || axle.diameter;
      let maxDiam = axle.diameterMax || (minDiam + DIAMETER_TOLERANCE);

      /*
       * Hybrid-Forged leveres i faste oppgraderingsstørrelser.
       * Tillat derfor opptil 2 tommer over bilens største OEM-diameter,
       * også når kunden bytter farge og diameter ikke lenger ligger i URL-en.
       *
       * Dette gjelder kun Hybrid-Forged. Andre faste felger beholder
       * det vanlige diameterfilteret.
       */
      if (this._isHybridForged && maxDiam > 0) {
        maxDiam += 2;
      }

      if (this._urlDiameter > maxDiam) maxDiam = this._urlDiameter;

      if (minDiam > 0 && diameter > 0) {
        if (diameter < minDiam || diameter > maxDiam) return false;
      }
    }

    // ET is a soft filter — don't hide, just warn
    return true;
  }

  /* ================================================================
   *  Rendering
   * ================================================================ */

  /**
   * Fully Forged Aventador Wheels are produced to order. The automatic
   * vehicle matcher must therefore not show a false "no configuration"
   * state merely because the API lacks an exact finished OEM variant.
   */
  _hasAventadorManufacturableVariant() {
    if (!this._isAventadorForged || !(this._allVariants || []).length) return false;

    const requestedDiameter = Math.round(
      Number(this._selectedDiameter) ||
      Number(this._urlDiameter) ||
      Number(this._getVehicleMaxOemDiameter?.()) ||
      0
    );

    return (this._allVariants || []).some(variant => {
      const diameter = Math.round(this._variantDiameter(variant) || 0);
      const diameterMatches = !requestedDiameter || diameter === requestedDiameter;
      return diameterMatches && this._isVariantLoadCompatible(variant, 'both');
    });
  }

  _render() {
    this._noMatchActive = false;
    if (this._quickBuyMode) {
      this._renderQuickBuyMode();
      return;
    }
    // Ensure quick buy containers are hidden in manual mode
    if (this._quickBuyEl) this._quickBuyEl.hidden = true;
    if (this._qbNoVehicleEl) this._qbNoVehicleEl.hidden = true;
    // Show qty selector again in manual mode
    if (this._qtyWrap) this._qtyWrap.hidden = false;
    // Ensure manual steps are visible (QB mode hides them)
    const hasVehicle = !!this._vehicle?.specs;
    const hasMatch = this._manualMode ? true : (hasVehicle
      ? (
          this._allVariants.some(v => this._isVariantCompatible(v, this._vehicle.specs)) ||
          this._hasAventadorManufacturableVariant()
        )
      : true);

    // When vehicle selected but nothing matches, hide all steps + cart
    if (hasVehicle && !hasMatch) {
      if (this._manualModeCard) this._manualModeCard.hidden = true;
      this.querySelectorAll('[data-step]').forEach(s => s.hidden = true);
      if (this._stepsBar) this._stepsBar.hidden = true;
      if (this._axleToggleEl) this._axleToggleEl.hidden = true;
      if (this._cartSection) this._cartSection.hidden = true;
      if (this._compatCard) this._compatCard.hidden = false;
      const compatEl = this.querySelector('[data-role="wc-compat"]');
      if (compatEl) {
        compatEl.innerHTML = this._isAventadorForged
          ? `<div class="wc__card-row wc__card-row--ok">${CHECK_SVG}<span><strong style="color:#d4a843">OEM-tilpasses bilen din</strong></span></div>`
          : `<div class="wc__card-row wc__card-row--fail">${X_SVG}<span>Ingen konfigurasjon passer</span></div>`;
      }
      this._renderVehicleBanner();
      this._renderOemSpecCard();
      this._renderNoMatchCards();
      this._placeAventadorFlowButtons();
      this._renderCenterCapAddon();
      return;
    }

    if (this._noMatchCards) this._noMatchCards.hidden = true;
    if (this._cartSection) this._cartSection.hidden = false;
    if (this._compatCard) this._compatCard.hidden = false;
    this.querySelectorAll('[data-step]').forEach(s => s.hidden = false);
    // Hide PCD step when vehicle is selected (auto-selected by filter)
    // Also hide for Aventador forged — PCD is always "custom", no choice needed
    const pcdStep = this.querySelector('[data-step="pcd"]');
    if (pcdStep) pcdStep.hidden = hasVehicle
      || this._isAventadorForged
      || (this._manualMode && this._hidePcdInManualMode);
    if (this._manualMode && (this._hidePcdInManualMode || hasVehicle)) {
      this._autoSelectManualPcd();
    }
    if (this._stepsBar) this._stepsBar.hidden = false;

    this._renderVehicleBanner();
    this._renderOemSpecCard();
    this._renderManualModeCard();
    this._renderAxleToggle();
    this._renderPcdStep();
    this._renderSizeStep();
    this._renderEtStep();
    this._renderFinishStep();
    // Renumber visible step headers after all visibility is resolved
    this._renumberStepHeaders();
    this._renderStepsBar();
    this._placeAventadorFlowButtons();
    this._updatePrice();
    this._updateAddButton();
    this._renderCompatInfo();
    this._renderCenterCapAddon();
  }

  /* ================================================================
   *  Quick Buy Mode (Aventador forged only)
   * ================================================================ */

  _getVehicleMaxOemDiameter() {
    const specs = this._vehicle?.specs;
    if (!specs) return 0;

    const values = [
      specs.diameter_max,
      specs.diameter_front,
      specs.diameter_rear,
      specs.rim_diameter_r,
      specs.oem_diameter,
    ]
      .map(value => Number(value))
      .filter(value => Number.isFinite(value) && value > 0);

    return values.length ? Math.max(...values) : 0;
  }

  _getAvailableProductDiameters() {
    const diameters = new Set();
    for (const variant of this._allVariants || []) {
      const diameter = this._variantDiameter(variant);
      if (Number.isFinite(diameter) && diameter > 0) {
        diameters.add(Math.round(diameter));
      }
    }
    return [...diameters].sort((a, b) => a - b);
  }

  _shouldAutoOpenAventadorCustomFit() {
    if (!this._isAventadorForged || !this._vehicle?.specs) return false;

    const maxOemDiameter = this._getVehicleMaxOemDiameter();
    if (!maxOemDiameter || maxOemDiameter > 18) return false;

    // Only trigger when this Aventador product actually has a larger size
    // than the vehicle's largest OEM/vognkort size.
    return this._getAvailableProductDiameters().some(
      diameter => diameter > Math.round(maxOemDiameter)
    );
  }

  _getPreferredAventadorCustomDiameter() {
    const maxOemDiameter = Math.round(this._getVehicleMaxOemDiameter());
    const available = this._getAvailableProductDiameters().filter(
      diameter => diameter > maxOemDiameter
    );

    // 19" is the preferred first Custom Fit size when available.
    if (available.includes(19)) return 19;
    return available[0] || 0;
  }

  _initQuickBuyMode() {
    // Aventador Wheels: when the vehicle's largest OEM/vognkort size is 18",
    // skip the incompatible OEM flow and open Custom Fit directly. This lets
    // made-to-order 19"/20" Aventador variants be configured without showing
    // a false "no configuration fits" warning.
    this._autoCustomFitForSmallOem = this._shouldAutoOpenAventadorCustomFit();

    if (this._autoCustomFitForSmallOem) {
      this._quickBuyMode = false;
      this._manualMode = true;
      this._hidePcdInManualMode = true;
      this._selectedDiameter = this._getPreferredAventadorCustomDiameter();
      this._selectedSize = null;
      this._selectedEt = null;
      this._selectedExactEt = null;
      this._filteredVariants = this._allVariants;
      this._hiddenPcds = new Set();
      this._hiddenSizes = new Set();
      this._autoSelectManualPcd();
      return;
    }

    // The simple complete-set flow is the default whenever a vehicle with
    // specifications is selected.
    this._quickBuyMode = !!this._vehicle?.specs && !this._manualMode;
  }

  _renderQuickBuyMode() {
    // The center-cap service is available in both Aventador Wheels OEM Fit and Custom Fit.
    this._renderCenterCapAddon();

    const oemSpecCard = this.querySelector('[data-role="oem-spec-card"]');
    if (oemSpecCard) oemSpecCard.hidden = true;
    if (this._manualModeCard) this._manualModeCard.hidden = true;
    // Hide manual steps (they show when custom card is expanded)
    this.querySelectorAll('[data-step]').forEach(s => s.hidden = true);
    if (this._stepsBar) this._stepsBar.hidden = true;

    // Always render vehicle banner; hide axle toggle in QB (handled by cards)
    this._renderVehicleBanner();
    if (this._axleToggleEl) this._axleToggleEl.hidden = true;

    if (!this._vehicle?.specs) {
      if (this._quickBuyEl) this._quickBuyEl.hidden = true;
      if (this._qbNoVehicleEl) this._qbNoVehicleEl.hidden = false;
      if (this._noMatchCards) this._noMatchCards.hidden = true;
      if (this._cartSection) this._cartSection.hidden = false;
      if (this._compatCard) this._compatCard.hidden = false;
      if (this._vehicleBanner) this._vehicleBanner.innerHTML = '';
      if (this._titleBackEl) this._titleBackEl.innerHTML = '';
      // Reset selections so price/cart reflect no-vehicle state
      this._selectedPcd = null;
      this._selectedSize = null;
      this._selectedEt = null;
      this._selectedExactEt = null;
      this._selectedFinish = null;
      if (this._qtyWrap) this._qtyWrap.hidden = false;
      const qtyInput = this.querySelector('[data-role="wc-qty-input"]');
      if (qtyInput) qtyInput.value = '1';
      this._updatePrice();
      this._updateAddButton();
      this._renderCompatInfo();
      this._placeAventadorFlowButtons();
      return;
    }

    // No compatible variants: hide quick buy, show only banner with warning + fallback cards.
    // Hybrid-Forged has its own physical PCD/width/ET recommendation logic,
    // so the generic variant matcher must not block it first.
    const hybridPairs = this._isHybridForged
      ? (this._getQbPairCandidates?.() || [])
      : [];
    const hybridRecommendedMatch =
      this._isHybridForged &&
      Array.isArray(hybridPairs) &&
      hybridPairs.length > 0;

    const hasMatch =
      hybridRecommendedMatch ||
      this._allVariants.some(v => this._isVariantCompatible(v, this._vehicle.specs)) ||
      this._hasAventadorManufacturableVariant();
    if (!hasMatch) {
      if (this._quickBuyEl) this._quickBuyEl.hidden = true;
      if (this._qbNoVehicleEl) this._qbNoVehicleEl.hidden = true;
      if (this._cartSection) this._cartSection.hidden = true;
      if (this._compatCard) this._compatCard.hidden = false;
      const compatEl = this.querySelector('[data-role="wc-compat"]');
      if (compatEl) {
        compatEl.innerHTML = this._isAventadorForged
          ? `<div class="wc__card-row wc__card-row--ok">${CHECK_SVG}<span><strong style="color:#d4a843">OEM-tilpasses bilen din</strong></span></div>`
          : `<div class="wc__card-row wc__card-row--fail">${X_SVG}<span>Ingen konfigurasjon passer</span></div>`;
      }
      this._renderNoMatchCards();
      this._placeAventadorFlowButtons();
      return;
    }

    if (this._noMatchCards) this._noMatchCards.hidden = true;
    if (this._cartSection) this._cartSection.hidden = false;
    if (this._compatCard) this._compatCard.hidden = false;
    if (this._qbNoVehicleEl) this._qbNoVehicleEl.hidden = true;
    if (this._quickBuyEl) this._quickBuyEl.hidden = false;

    // Hide qty selector — quantity is automatic when vehicle is set
    if (this._qtyWrap) this._qtyWrap.hidden = true;

    // Auto-fetch specs for per-diameter data if not already fetched
    if (!this._specsFetched) this._fetchAndRenderSpecs();

    this._renderQbDiameters();
    this._renderQbOemCard();
    this._renderQbFinish();
    this._placeAventadorFlowButtons();
    this._updatePrice();
    this._updateAddButton();
    this._renderCompatInfo();
  }

  _renderQbDiameters() {
    if (!this._qbDiamRow) return;

    this._qbEnsureDefaults();

    // Gather available diameters from filtered variants
    const diamSet = new Set();
    for (const v of this._filteredVariants) {
      const m = (v.option2 || '').match(/^(\d+)/);
      if (m) diamSet.add(parseInt(m[1], 10));
    }
    const allDiameters = [...diamSet].sort((a, b) => a - b);
    const diameters = this._getQbAllowedDiameters(allDiameters);

    if (diameters.length === 0) {
      this._qbDiamRow.innerHTML = '<div style="color:#888;font-size:0.85rem">Ingen diametere tilgjengelig</div>';
      return;
    }

    let html = '';
    for (const d of diameters) {
      const sel = d === this._selectedDiameter ? ' wc__qb-diam-pill--selected' : '';
      const check = d === this._selectedDiameter ? '<span class="wc__qb-diam-check">✓</span>' : '';
      html += `<button type="button" class="wc__qb-diam-pill wc__qb-diam-pill--oem${sel}" data-qb-diam="${d}"><span class="wc__qb-diam-top">${d}"${check}</span></button>`;
    }
    this._qbDiamRow.innerHTML = html;

    // Bind clicks
    this._qbDiamRow.querySelectorAll('[data-qb-diam]').forEach(btn => {
      btn.addEventListener('click', () => {
        this._selectedDiameter = parseInt(btn.dataset.qbDiam, 10);
        this._selectedSize = null;
        this._selectedEt = null;
        this._selectedExactEt = null;
        this._qbSelectedPairKey = null;
        this._qbActivePair = null;
        this._qbAutoSelect();
        this._render();
      });
    });

    // Auto-select best variant for current diameter
    this._qbAutoSelect();
  }

  _getQbAllowedDiameters(sourceDiameters) {
    const list = Array.isArray(sourceDiameters) ? sourceDiameters : [];
    if (!list.length) return list;
    if (!this._vehicle?.specs) return list;
    if (!this._diamSpecs || this._diamSpecs.size === 0) return list;

    const needRear = this._isVehicleStaggered();
    const filtered = list.filter(d => {
      const entry = this._diamSpecs.get(d);
      if (!entry || !entry.front) return false;
      if (needRear && !entry.rear) return false;

      const diameterVariants = (this._allVariants || []).filter(
        variant => Math.round(this._variantDiameter(variant) || 0) === Math.round(d)
      );

      return diameterVariants.some(
        variant => this._isVariantLoadCompatible(variant, 'both')
      );
    });

    return filtered.length ? filtered : list;
  }

  /** Auto-select PCD and OEM diameter for Quick Buy. */
  _qbEnsureDefaults() {
    if (!this._selectedPcd) {
      if (this._isAventadorForged) {
        for (const [pcd] of this._pcdSet) {
          if (pcd.toLowerCase() === 'custom') {
            this._selectedPcd = pcd;
            break;
          }
        }
      } else {
        this._selectedPcd = this._getVehicleMatchedPcd();
      }

      if (!this._selectedPcd && this._pcdSet?.has('custom')) {
        this._selectedPcd = 'custom';
      }
      if (!this._selectedPcd) {
        this._selectedPcd = this._pcdSet.keys().next().value;
      }
    }

    // Gather available diameters for the selected PCD.
    const diamSet = new Set();
    for (const v of this._filteredVariants) {
      if (this._selectedPcd && (v.option1 || '').trim() !== this._selectedPcd) continue;
      const m = (v.option2 || '').match(/^(\d+)/);
      if (m) diamSet.add(parseInt(m[1], 10));
    }

    const allDiameters = [...diamSet].sort((a, b) => a - b);
    const diameters = this._getQbAllowedDiameters(allDiameters);
    if (diameters.length === 0) return;

    const specs = this._vehicle?.specs;
    const frontOemDiam = Math.round(specs?.diameter_front || specs?.oem_diameter || 0);

    if (!this._selectedDiameter || !diameters.includes(this._selectedDiameter)) {
      if (this._urlDiameter && diameters.includes(this._urlDiameter)) {
        this._selectedDiameter = this._urlDiameter;
      } else if (frontOemDiam && diameters.includes(frontOemDiam)) {
        this._selectedDiameter = frontOemDiam;
      } else {
        this._selectedDiameter = diameters[0];
      }
    }
  }

  _variantWidth(variant) {
    return this._extractWidthFromSize(variant?.option2);
  }

  _variantDiameter(variant) {
    const m = String(variant?.option2 || '').match(/^(\d+(?:\.\d+)?)/);
    return m ? parseFloat(m[1]) : null;
  }

  _variantResolvedEt(variant, preferredEt) {
    if (!variant) return null;
    const selection = this._qbExtractSelection(variant, preferredEt);
    return Number.isFinite(selection.exactEt) ? selection.exactEt : null;
  }

  _isSafeAxleAlternative(oemWidth, oemEt, candidateWidth, candidateEt) {
    if (![oemWidth, oemEt, candidateWidth, candidateEt].every(Number.isFinite)) return false;

    const widthChangeInch = candidateWidth - oemWidth;

    // Allow up to 1.0" narrower when the OEM width is unavailable,
    // but no more than 0.5" wider.
    if (widthChangeInch < -MAX_WIDTH_NARROWER_INCH - 0.001) return false;
    if (widthChangeInch > MAX_WIDTH_WIDER_INCH + 0.001) return false;

    const halfWidthChangeMm = widthChangeInch * 25.4 / 2;
    const etChangeMm = candidateEt - oemEt;

    // Positive outerChangeMm means the wheel moves farther out.
    // Positive innerChangeMm means it moves farther inward.
    const outerChangeMm = halfWidthChangeMm - etChangeMm;
    const innerChangeMm = halfWidthChangeMm + etChangeMm;

    // Do not recommend a wheel that sits excessively farther inside the arch.
    // Example: OEM 20x9 ET34 vs 20x8.5 ET45 = 17.35 mm farther inward
    // at the outer face, which is physically mountable but not an OEM-quality fit.
    return outerChangeMm <= MAX_OUTER_CHANGE_MM + 0.001
      && outerChangeMm >= -MAX_OUTER_INSET_MM - 0.001
      && innerChangeMm <= MAX_INNER_CHANGE_MM + 0.001;
  }

  _isSafeRearAlternative(oemWidth, oemEt, candidateWidth, candidateEt) {
    return this._isSafeAxleAlternative(oemWidth, oemEt, candidateWidth, candidateEt);
  }

  _pickSafeAxleVariant(variants, oemWidth, oemEt) {
    const safeCandidates = variants.filter(v => {
      const width = this._variantWidth(v);
      const et = this._variantResolvedEt(v, oemEt);
      return Number.isFinite(width)
        && Number.isFinite(et)
        && this._isSafeAxleAlternative(oemWidth, oemEt, width, et);
    });

    if (!safeCandidates.length) return null;

    // Priority:
    // 1. Exact OEM width with safe ET
    // 2. 0.5" narrower
    // 3. 1.0" narrower
    // 4. Up to 0.5" wider, only when still geometrically safe
    return safeCandidates
      .map(v => {
        const width = this._variantWidth(v);
        const et = this._variantResolvedEt(v, oemEt);
        const widthDiff = width - oemWidth;

        let widthPriority;
        if (Math.abs(widthDiff) <= 0.001) widthPriority = 0;
        else if (widthDiff < 0) widthPriority = Math.abs(widthDiff) * 100;
        else widthPriority = 1000 + widthDiff * 100;

        const halfWidthChangeMm = widthDiff * 25.4 / 2;
        const etChangeMm = et - oemEt;
        const outerChangeMm = halfWidthChangeMm - etChangeMm;
        const innerChangeMm = halfWidthChangeMm + etChangeMm;
        const geometryPenalty = Math.max(0, outerChangeMm) * 10
          + Math.max(0, innerChangeMm) * 5;
        const etPenalty = Math.abs(et - oemEt);
        const stockBonus = v.available ? -10 : 0;

        return {
          variant: v,
          score: widthPriority + geometryPenalty + etPenalty + stockBonus
        };
      })
      .sort((a, b) => a.score - b.score)[0].variant;
  }

  _pickExactFrontVariant(variants, oemWidth, oemEt) {
    return this._pickSafeAxleVariant(variants, oemWidth, oemEt);
  }

  _pickRearVariant(variants, oemWidth, oemEt) {
    return this._pickSafeAxleVariant(variants, oemWidth, oemEt);
  }

  /** Score variants against target width/ET specs and return best pick. */
  _qbPickBest(diamVariants, targetWidth, targetEt) {
    const variants = Array.isArray(diamVariants)
      ? diamVariants.filter(Boolean)
      : [];

    if (!variants.length) return null;

    /*
     * IMPORTANT — Fully Forged OEM Quick Buy:
     * If Shopify has the exact OEM width, that width must always be used.
     *
     * Previously all widths competed in the same score. A nearby width such as
     * 20x12.5 could therefore beat the correct 20x12 because its ET range or
     * availability produced a higher total score. The OEM card would still show
     * 20x12 ET59, while the cart received the 20x12.5 Shopify variant.
     *
     * We now restrict the scoring pool to exact-width variants whenever an exact
     * width exists. ET range/closeness and availability still choose the best
     * variant WITHIN that width. If Shopify has no exact width, the original
     * fallback behavior is preserved and the closest/best variant may be used.
     */
    let candidates = variants;

    const numericTargetWidth = Number(targetWidth);
    if (Number.isFinite(numericTargetWidth) && numericTargetWidth > 0) {
      const exactWidthCandidates = variants.filter(v => {
        const widthMatch = String(v.option2 || '')
          .match(/^\d+(?:\.\d+)?x(\d+(?:\.\d+)?)/);

        if (!widthMatch) return false;

        const variantWidth = parseFloat(widthMatch[1]);
        return Number.isFinite(variantWidth)
          && Math.abs(variantWidth - numericTargetWidth) <= 0.001;
      });

      if (exactWidthCandidates.length) {
        candidates = exactWidthCandidates;
      }
    }

    let bestVariant = candidates[0];
    let bestScore = -Infinity;

    for (const v of candidates) {
      let score = 0;

      // Width closeness to OEM.
      // When exact-width candidates exist this is always 0, but keeping the
      // original score here preserves the old fallback behavior unchanged.
      const widthMatch = String(v.option2 || '')
        .match(/^\d+(?:\.\d+)?x(\d+(?:\.\d+)?)/);

      if (widthMatch && numericTargetWidth > 0) {
        score -= Math.abs(parseFloat(widthMatch[1]) - numericTargetWidth) * 10;
      }

      // ET closeness to OEM
      const etStr = String(v.option3 || '').replace(/\s*\(.*\)/, '');
      const etMatch = etStr.match(/ET(-?\d+)(?:-(-?\d+))?/);

      if (etMatch) {
        const etMin = parseInt(etMatch[1], 10);
        const etMax = etMatch[2] ? parseInt(etMatch[2], 10) : etMin;
        const etMid = (etMin + etMax) / 2;

        score -= Math.abs(etMid - targetEt) * 0.5;

        if (targetEt >= etMin && targetEt <= etMax) {
          score += 5;
        }
      }

      // In-stock bonus
      if (v.available) score += 3;

      if (score > bestScore) {
        bestScore = score;
        bestVariant = v;
      }
    }

    return bestVariant;
  }

  /** Extract { pcd, size, et, exactEt } from a best-variant pick. */
  _qbExtractSelection(bestVariant, targetEt) {
    const pcd = (bestVariant.option1 || '').trim();
    const size = (bestVariant.option2 || '').trim();
    const et = (bestVariant.option3 || '').trim();
    let exactEt = null;

    if (this._isEtRange(et)) {
      const bounds = this._parseEtBounds(et);
      if (bounds) {
        exactEt = Math.max(bounds.min, Math.min(bounds.max, Math.round(targetEt)));
      }
    } else {
      const m = et.match(/ET(-?\d+)/);
      if (m) exactEt = parseInt(m[1], 10);
    }

    return { pcd, size, et, exactEt };
  }

  _qbResolveTargetWidth(diamEntry, axleKey, preferredWidth) {
    const axle = diamEntry?.[axleKey];
    if (!axle) return preferredWidth || 0;
    const options = [...(axle.widths || [])]
      .map(v => parseFloat(v))
      .filter(v => Number.isFinite(v) && v > 0);
    if (options.length === 0) return axle.width || preferredWidth || 0;
    if (!preferredWidth || preferredWidth <= 0) return options[0];
    return options.reduce((best, cur) => Math.abs(cur - preferredWidth) < Math.abs(best - preferredWidth) ? cur : best, options[0]);
  }

  _qbResolveTargetEt(diamEntry, axleKey, preferredEt, targetWidth = null) {
    const axle = diamEntry?.[axleKey];
    if (!axle) return preferredEt || 0;
    let options = [];

    if (Number.isFinite(targetWidth) && axle.etByWidth instanceof Map && axle.etByWidth.size) {
      let bestKey = null;
      let bestDist = Infinity;
      for (const key of axle.etByWidth.keys()) {
        const w = parseFloat(key);
        if (!Number.isFinite(w)) continue;
        const d = Math.abs(w - targetWidth);
        if (d < bestDist) {
          bestDist = d;
          bestKey = key;
        }
      }
      if (bestKey && axle.etByWidth.get(bestKey)) {
        options = [...axle.etByWidth.get(bestKey)]
          .map(v => parseFloat(v))
          .filter(v => Number.isFinite(v));
      }
    }

    if (!options.length) {
      options = [...(axle.ets || [])]
        .map(v => parseFloat(v))
        .filter(v => Number.isFinite(v));
    }

    if (options.length === 0) return axle.et ?? preferredEt ?? 0;
    if (!Number.isFinite(preferredEt)) return options[0];
    return options.reduce((best, cur) => Math.abs(cur - preferredEt) < Math.abs(best - preferredEt) ? cur : best, options[0]);
  }

  _extractWidthFromSize(sizeLabel) {
    const m = String(sizeLabel || '').match(/^\d+(?:\.\d+)?x(\d+(?:\.\d+)?)/);
    return m ? parseFloat(m[1]) : null;
  }

  _getOemEtForSelection(diamEntry, axleKey, preferredEt, sizeLabel = null) {
    const axle = diamEntry?.[axleKey];
    if (!axle) return Number.isFinite(preferredEt) ? preferredEt : null;

    const candidates = [];
    const byWidth = axle.etByWidth instanceof Map ? axle.etByWidth : null;
    const width = this._extractWidthFromSize(sizeLabel);
    if (byWidth && width !== null) {
      const key = width.toFixed(3);
      const wSet = byWidth.get(key);
      if (wSet) {
        for (const et of wSet) {
          const val = parseFloat(et);
          if (Number.isFinite(val)) candidates.push(val);
        }
      }
    }

    if (!candidates.length && axle.ets instanceof Set) {
      for (const et of axle.ets) {
        const val = parseFloat(et);
        if (Number.isFinite(val)) candidates.push(val);
      }
    }

    if (!candidates.length) {
      if (Number.isFinite(axle.et)) return axle.et;
      return Number.isFinite(preferredEt) ? preferredEt : null;
    }

    if (!Number.isFinite(preferredEt)) return candidates[0];
    return candidates.reduce((best, cur) => (
      Math.abs(cur - preferredEt) < Math.abs(best - preferredEt) ? cur : best
    ), candidates[0]);
  }

  _qbPairKey(pair) {
    if (!pair) return '';
    const n = value => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? String(parsed) : '';
    };
    return [
      n(pair.frontDiameter),
      n(pair.frontWidth),
      n(pair.frontEt),
      n(pair.rearDiameter),
      n(pair.rearWidth),
      n(pair.rearEt),
    ].join('|');
  }

  _variantMatchesExactAxle(variant, diameter, width, targetEt) {
    if (!variant) return false;

    const variantPcd = String(variant.option1 || '').trim().toLowerCase();
    const selectedPcd = String(this._selectedPcd || '').trim().toLowerCase();
    if (selectedPcd && variantPcd && variantPcd !== selectedPcd && variantPcd !== 'custom') {
      return false;
    }

    const variantDiameter = this._variantDiameter(variant);
    const variantWidth = this._variantWidth(variant);
    if (!Number.isFinite(variantDiameter) || Math.round(variantDiameter) !== Math.round(Number(diameter))) {
      return false;
    }
    if (!Number.isFinite(variantWidth) || Math.abs(variantWidth - Number(width)) > 0.001) {
      return false;
    }

    // An OEM card may only be shown when the product has an ET that can
    // actually deliver the advertised OEM specification.
    const wantedEt = Number(targetEt);
    if (!Number.isFinite(wantedEt)) return false;
    const bounds = this._parseEtBounds(variant.option3);
    if (!bounds) return false;
    return wantedEt >= bounds.min - 0.001 && wantedEt <= bounds.max + 0.001;
  }

  _hasExactQbAxleVariant(diameter, width, et, axle = 'both') {
    return (this._allVariants || []).some(variant =>
      this._variantMatchesExactAxle(variant, diameter, width, et) &&
      this._isVariantLoadCompatible(variant, axle)
    );
  }

  _getQbPairCandidates() {
    const diamEntry = this._diamSpecs?.get(this._selectedDiameter);
    const pairs = Array.isArray(diamEntry?.pairs) ? diamEntry.pairs : [];
    const selectedDiameter = Math.round(this._selectedDiameter || 0);

    const isFiniteValue = value =>
      value !== null &&
      value !== undefined &&
      String(value).trim() !== '' &&
      Number.isFinite(Number(value));

    const candidates = pairs.filter(pair =>
      Math.round(Number(pair?.frontDiameter) || 0) === selectedDiameter &&
      isFiniteValue(pair?.frontWidth) &&
      isFiniteValue(pair?.frontEt) &&
      isFiniteValue(pair?.rearDiameter) &&
      isFiniteValue(pair?.rearWidth) &&
      isFiniteValue(pair?.rearEt)
    );

    const unique = [];
    const seen = new Set();

    for (const pair of candidates) {
      const key = this._qbPairKey(pair);
      if (!key || seen.has(key)) continue;

      seen.add(key);
      unique.push(pair);
    }

    // Fully Forged Aventador Wheels are made to order.
    // Normalize incomplete OEM rows before deciding that no OEM setup exists.
    // This is especially important for new vehicles such as Porsche Cayenne 2027,
    // where some API rows omit rear ET even though the approved dimensions exist.
    if (this._isAventadorForged) {
      const specs = this._vehicle?.specs || {};
      const front = diamEntry?.front || {};
      const rear = diamEntry?.rear || {};

      const finite = (...values) => {
        for (const value of values) {
          if (value === null || value === undefined || String(value).trim() === '') continue;
          const parsed = Number(value);
          if (Number.isFinite(parsed)) return parsed;
        }
        return null;
      };

      const normalizePair = pair => {
        const frontDiameter = finite(
          pair?.frontDiameter,
          front.diameter,
          selectedDiameter,
          specs.diameter_front,
          specs.oem_diameter
        );
        const rearDiameter = finite(
          pair?.rearDiameter,
          rear.diameter,
          diamEntry?.rearDiameter,
          specs.diameter_rear,
          frontDiameter
        );
        const frontWidth = finite(
          pair?.frontWidth,
          front.width,
          specs.width_front
        );
        const rearWidth = finite(
          pair?.rearWidth,
          rear.width,
          specs.width_rear,
          frontWidth
        );
        const frontEt = finite(
          pair?.frontEt,
          this._qbResolveTargetEt?.(
            diamEntry,
            'front',
            specs.et_front ?? specs.et ?? null,
            frontWidth
          ),
          front.et,
          specs.et_front,
          specs.et
        );
        const rearEt = finite(
          pair?.rearEt,
          this._qbResolveTargetEt?.(
            diamEntry,
            'rear',
            specs.et_rear ?? specs.et_front ?? specs.et ?? null,
            rearWidth
          ),
          rear.et,
          specs.et_rear,
          frontEt
        );

        if (
          !(frontDiameter > 0) ||
          !(rearDiameter > 0) ||
          !(frontWidth > 0) ||
          !(rearWidth > 0) ||
          frontEt === null ||
          rearEt === null
        ) {
          return null;
        }

        return {
          frontDiameter: Math.round(frontDiameter),
          frontWidth,
          frontEt,
          rearDiameter: Math.round(rearDiameter),
          rearWidth,
          rearEt,
          source: pair?.source || 'vehicle-oem-normalized'
        };
      };

      const sourcePairs = unique.length ? unique : (pairs.length ? pairs : [{}]);
      const normalized = [];
      const normalizedSeen = new Set();

      for (const sourcePair of sourcePairs) {
        const pair = normalizePair(sourcePair);
        if (!pair) continue;
        if (selectedDiameter && Math.round(pair.frontDiameter) !== selectedDiameter) continue;

        const key = this._qbPairKey(pair);
        if (!key || normalizedSeen.has(key)) continue;
        normalizedSeen.add(key);
        normalized.push(pair);
      }

      if (normalized.length) return normalized;

      // Last-resort made-to-order fallback: use the selected product diameter
      // with the vehicle's known axle data. This keeps Fully Forged available
      // instead of showing the false "cannot be supplied with OEM setup" warning.
      const productVariant = (this._allVariants || []).find(variant =>
        Math.round(this._variantDiameter(variant) || 0) === selectedDiameter
      );

      const productWidth = productVariant
        ? this._variantWidth(productVariant)
        : null;
      const productEt = productVariant
        ? this._variantResolvedEt(
            productVariant,
            specs.et_front ?? specs.et ?? 0
          )
        : null;

      const fallback = normalizePair({
        frontDiameter: selectedDiameter,
        rearDiameter: selectedDiameter,
        frontWidth: finite(specs.width_front, productWidth),
        rearWidth: finite(specs.width_rear, specs.width_front, productWidth),
        frontEt: finite(specs.et_front, specs.et, productEt),
        rearEt: finite(specs.et_rear, specs.et_front, specs.et, productEt),
        source: 'aventador-made-to-order'
      });

      return fallback ? [fallback] : [];
    }

    /*
     * Hybrid-Forged har faste bredder, ET-verdier og boltsirkler.
     * Bygg derfor valgene fra variantene produktet faktisk har.
     *
     * ET-avviket på maksimalt 7,5 mm gjelder kun Hybrid-Forged.
     */
    if (this._isHybridForged) {
      const specs = this._vehicle?.specs || {};
      const selectedPcd = String(this._selectedPcd || '')
        .trim()
        .toLowerCase();

      // Hybrid-Forged must evaluate all product variants here.
      // _filteredVariants may be based on the active front axle and can
      // otherwise remove a valid wider rear option such as 20x10 ET40.
      const productVariants = (this._allVariants || []).filter(variant => {
        const variantPcd = String(variant.option1 || '')
          .trim()
          .toLowerCase();

        const pcdMatches =
          !selectedPcd ||
          variantPcd === selectedPcd ||
          variantPcd === 'custom';

        const diameterMatches =
          Math.round(this._variantDiameter(variant) || 0) ===
          selectedDiameter;

        return pcdMatches && diameterMatches;
      });

      const frontTargetWidth = Number(
        diamEntry?.front?.width ??
        specs.width_front ??
        0
      );

      const frontTargetEt = Number(
        diamEntry?.front?.et ??
        specs.et_front ??
        specs.et ??
        0
      );

      const rearTargetWidth = Number(
        diamEntry?.rear?.width ??
        specs.width_rear ??
        frontTargetWidth
      );

      const rearTargetEt = Number(
        diamEntry?.rear?.et ??
        specs.et_rear ??
        specs.et_front ??
        specs.et ??
        frontTargetEt
      );

      const getVariantValues = variant => ({
        variant,
        width: this._variantWidth(variant),
        et: this._variantResolvedEt(variant, frontTargetEt)
      });

      const frontCandidates = productVariants
        .map(getVariantValues)
        .filter(item =>
          Number.isFinite(item.width) &&
          Number.isFinite(item.et) &&
          Math.abs(item.et - frontTargetEt) <= 7.5 &&
          this._isVariantLoadCompatible(item.variant, 'front')
        )
        .sort((a, b) => {
          const widthDiff =
            Math.abs(a.width - frontTargetWidth) -
            Math.abs(b.width - frontTargetWidth);

          if (Math.abs(widthDiff) > 0.001) {
            return widthDiff;
          }

          return (
            Math.abs(a.et - frontTargetEt) -
            Math.abs(b.et - frontTargetEt)
          );
        });

      const frontBest = frontCandidates[0];

      if (frontBest) {
        /*
         * Behold beste bakvariant per bredde.
         *
         * For BMW i4 gir dette:
         *  - 20x8.5 ET45
         *  - 20x10 ET40
         *
         * ET32 bak fjernes fordi den er mer enn 7,5 mm fra bilens
         * bakre referanse-ET.
         */
        const rearByWidth = new Map();

        for (const variant of productVariants) {
          const width = this._variantWidth(variant);
          const et = this._variantResolvedEt(variant, rearTargetEt);

          if (!Number.isFinite(width) || !Number.isFinite(et)) {
            continue;
          }

          // Rear options are checked against the rear axle load separately.
          if (!this._isVariantLoadCompatible(variant, 'rear')) {
            continue;
          }

          if (Math.abs(et - rearTargetEt) > 7.5) {
            continue;
          }

          // Bakhjulet skal ikke være smalere enn valgt forhjul.
          if (width < frontBest.width - 0.001) {
            continue;
          }

          // Unngå urimelig brede automatiske forslag.
          if (width > frontBest.width + 1.5 + 0.001) {
            continue;
          }

          const widthKey = width.toFixed(3);
          const current = rearByWidth.get(widthKey);

          const candidateScore =
            Math.abs(et - rearTargetEt) +
            Math.abs(width - rearTargetWidth) * 0.01;

          const currentScore = current
            ? Math.abs(current.et - rearTargetEt) +
              Math.abs(current.width - rearTargetWidth) * 0.01
            : Infinity;

          if (!current || candidateScore < currentScore) {
            rearByWidth.set(widthKey, {
              variant,
              width,
              et
            });
          }
        }

        const hybridPairs = [...rearByWidth.values()]
          .sort((a, b) => a.width - b.width)
          .map(rear => ({
            frontDiameter: selectedDiameter,
            frontWidth: frontBest.width,
            frontEt: frontBest.et,
            rearDiameter: selectedDiameter,
            rearWidth: rear.width,
            rearEt: rear.et,
            source: 'hybrid-forged-variant'
          }));

        const uniqueHybridPairs = [];
        const hybridSeen = new Set();

        for (const pair of hybridPairs) {
          const key = this._qbPairKey(pair);

          if (!key || hybridSeen.has(key)) {
            continue;
          }

          hybridSeen.add(key);
          uniqueHybridPairs.push(pair);
        }

        if (uniqueHybridPairs.length) {
          return uniqueHybridPairs;
        }
      }
    }

    // JR, Concaver og andre faste produkter må ha eksakte varianter.
    return unique.filter(pair =>
      this._hasExactQbAxleVariant(
        pair.frontDiameter,
        pair.frontWidth,
        pair.frontEt,
        'front'
      ) &&
      this._hasExactQbAxleVariant(
        pair.rearDiameter,
        pair.rearWidth,
        pair.rearEt,
        'rear'
      )
    );
  }

  _selectQbPair(pairKey) {
    if (!pairKey || pairKey === this._qbSelectedPairKey) return;
    this._qbSelectedPairKey = pairKey;
    this._qbActivePair = null;

    // Force a complete recalculation of front/rear variants and price.
    this._selectedSize = null;
    this._selectedEt = null;
    this._selectedExactEt = null;
    this._qbRearSelection = { pcd: null, size: null, et: null, exactEt: null };

    this._qbAutoSelect();
    this._render();
  }

  _qbAutoSelect() {
    this._qbEnsureDefaults();
    if (!this._selectedDiameter || !this._vehicle?.specs || !this._selectedPcd) return;

    const specs = this._vehicle.specs;
    const diamEntry = this._diamSpecs?.get(this._selectedDiameter);

    const pcdVariants = this._filteredVariants.filter(v =>
      (v.option1 || '').trim() === this._selectedPcd
    );

    const frontDiameter = this._selectedDiameter;
    const frontVariants = pcdVariants.filter(v =>
      Math.round(this._variantDiameter(v) || 0) === Math.round(frontDiameter)
    );
    if (!frontVariants.length) return;

    let selectedPair = null;

    // Resolve the OEM combination for the selected diameter for every vehicle,
    // not only staggered vehicles. This is important for made-to-order
    // Aventador Wheels: the Shopify variant may only be a pricing variant,
    // while the card must show the vehicle's real OEM width and ET.
    const pairCandidates = this._getQbPairCandidates()
      .sort((a, b) => {
        const aStaggered =
          Math.round(a.rearDiameter || 0) !== Math.round(a.frontDiameter || 0) ||
          Math.abs((a.rearWidth || 0) - (a.frontWidth || 0)) > 0.001;
        const bStaggered =
          Math.round(b.rearDiameter || 0) !== Math.round(b.frontDiameter || 0) ||
          Math.abs((b.rearWidth || 0) - (b.frontWidth || 0)) > 0.001;
        if (this._isVehicleStaggered() && aStaggered !== bStaggered) {
          return aStaggered ? -1 : 1;
        }

        const frontDiffA = Math.abs((a.frontWidth || 0) - (specs.width_front || 0));
        const frontDiffB = Math.abs((b.frontWidth || 0) - (specs.width_front || 0));
        if (frontDiffA !== frontDiffB) return frontDiffA - frontDiffB;

        return Math.abs((a.rearWidth || 0) - (specs.width_rear || specs.width_front || 0))
          - Math.abs((b.rearWidth || 0) - (specs.width_rear || specs.width_front || 0));
      });

    if (pairCandidates.length) {
      const requestedFront = Math.round(Number(this._urlDiameter) || 0);
      const requestedRear = Math.round(Number(this._urlRearDiameter) || 0);
      const requestedPair = requestedFront > 0
        ? pairCandidates.find(pair =>
            Math.round(Number(pair.frontDiameter) || 0) === requestedFront &&
            (!requestedRear || Math.round(Number(pair.rearDiameter) || 0) === requestedRear)
          )
        : null;

      selectedPair = pairCandidates.find(
        pair => this._qbPairKey(pair) === this._qbSelectedPairKey
      ) || requestedPair || pairCandidates[0];
      this._qbSelectedPairKey = this._qbPairKey(selectedPair);
      this._qbActivePair = selectedPair;
    } else {
      this._qbSelectedPairKey = null;
      this._qbActivePair = null;
    }

    const frontOemWidth = Number(
      selectedPair?.frontWidth ??
      diamEntry?.front?.width ??
      specs.width_front ??
      0
    );
    const frontOemEt = Number(
      selectedPair?.frontEt ??
      diamEntry?.front?.et ??
      specs.et_front ??
      specs.et ??
      0
    );

    const frontBest = this._isAventadorForged
      ? this._qbPickBest(frontVariants, frontOemWidth, frontOemEt)
      : this._pickExactFrontVariant(frontVariants, frontOemWidth, frontOemEt);

    if (!frontBest) {
      this._selectedSize = null;
      this._selectedEt = null;
      this._selectedExactEt = null;
      this._qbRearSelection = { pcd: null, size: null, et: null, exactEt: null };
      this._updateAddButton();
      return;
    }

    const frontSel = this._qbExtractSelection(frontBest, frontOemEt);
    this._selectedPcd = frontSel.pcd;
    this._selectedSize = frontSel.size;
    this._selectedEt = frontSel.et;
    this._selectedExactEt = frontSel.exactEt;

    if (this._isVehicleStaggered()) {
      // Without a valid OEM pair, a fixed wheel product must stay on the
      // diameter the customer selected. Falling back to specs.diameter_rear
      // caused 20-inch BMW configurations to incorrectly show 19 inches rear.
      const rearDiameter = Math.round(
        selectedPair?.rearDiameter ??
        (this._isAventadorForged ? (diamEntry?.rearDiameter ?? specs.diameter_rear) : frontDiameter) ??
        frontDiameter
      );
      const rearVariants = pcdVariants.filter(v =>
        Math.round(this._variantDiameter(v) || 0) === rearDiameter
      );

      const rearOemWidth = Number(
        selectedPair?.rearWidth ??
        diamEntry?.rear?.width ??
        specs.width_rear ??
        frontOemWidth
      );
      const rearOemEt = Number(
        selectedPair?.rearEt ??
        diamEntry?.rear?.et ??
        specs.et_rear ??
        frontOemEt
      );

      const rearBest = this._isAventadorForged
        ? this._qbPickBest(rearVariants, rearOemWidth, rearOemEt)
        : this._pickRearVariant(rearVariants, rearOemWidth, rearOemEt);

      if (!rearBest) {
        this._qbRearSelection = { pcd: null, size: null, et: null, exactEt: null };
        this._updateAddButton();
        return;
      }

      this._qbRearSelection = this._qbExtractSelection(rearBest, rearOemEt);
    } else {
      this._qbRearSelection = { pcd: null, size: null, et: null, exactEt: null };
    }

    this._driveHorizonPicker();
  }

  /** Render one clickable card for every OEM combination at the selected diameter. */
  _renderQbOemCard() {
    if (!this._qbOemCard) return;

    if (!this._selectedSize) {
      this._qbOemCard.hidden = true;
      return;
    }
    this._qbOemCard.hidden = false;

    const isStaggered = this._isVehicleStaggered() && this._qbRearSelection.size;

    // Hybrid-Forged can have several valid front/rear combinations even when
    // the vehicle data itself is not flagged as staggered. Always build the
    // pair list for Hybrid-Forged so alternatives such as 20x8.5 / 20x10
    // are rendered instead of collapsing to one standard card.
    const shouldRenderPairs = isStaggered || this._isHybridForged;
    const pairCandidates = shouldRenderPairs ? this._getQbPairCandidates() : [];

    // A vehicle may have several valid combinations for the same diameter.
    // Each combination becomes its own clickable box.
    if (shouldRenderPairs && pairCandidates.length > 1) {
      const selectedKey = this._qbSelectedPairKey || this._qbPairKey(pairCandidates[0]);

      // Remove the parent card chrome so only the clicked option gets a green border.
      this._qbOemCard.style.border = '0';
      this._qbOemCard.style.background = 'transparent';
      this._qbOemCard.style.boxShadow = 'none';
      this._qbOemCard.style.padding = '0';

      this._qbOemCard.innerHTML = pairCandidates.map((pair, index) => {
        const key = this._qbPairKey(pair);
        const selected = key === selectedKey;
        const frontDiameter = Number(pair.frontDiameter) || this._selectedDiameter;
        const rearDiameter = Number(pair.rearDiameter) || frontDiameter;
        const frontWidth = Number(pair.frontWidth);
        const rearWidth = Number(pair.rearWidth);
        const frontEt = Math.round(Number(pair.frontEt));
        const rearEt = Math.round(Number(pair.rearEt));

        const border = selected ? '#18c96e' : 'rgba(212,170,67,.72)';
        const background = selected ? 'rgba(24,201,110,.08)' : 'rgba(255,255,255,.035)';
        const iconBorder = selected ? '#18c96e' : '#d4aa43';
        const iconColor = selected ? '#fff' : 'transparent';
        const iconBackground = selected ? '#18c96e' : 'transparent';
        let title = index === 0
          ? `${frontDiameter}" – Original OEM-spesifikasjon`
          : `${frontDiameter}" – Alternativ OEM-spesifikasjon`;

        // For the active card, label it as adapted when the wheel variants
        // do not match the original OEM width/ET values exactly.
        if (selected && this._isAventadorForged) {
          title = `${frontDiameter}" – Original OEM-spesifikasjon`;
        } else if (selected) {
          const selectedFrontWidth = this._extractWidthFromSize(this._selectedSize);
          const selectedRearWidth = this._extractWidthFromSize(this._qbRearSelection.size);
          const selectedFrontEt = this._resolveEtValue(this._selectedEt, this._selectedExactEt);
          const selectedRearEt = this._resolveEtValue(
            this._qbRearSelection.et,
            this._qbRearSelection.exactEt
          );
          const isExactOem = Number.isFinite(selectedFrontWidth)
            && Number.isFinite(selectedRearWidth)
            && selectedFrontEt !== null
            && selectedRearEt !== null
            && Math.abs(selectedFrontWidth - frontWidth) <= 0.001
            && Math.abs(selectedRearWidth - rearWidth) <= 0.001
            && selectedFrontEt === frontEt
            && selectedRearEt === rearEt;

          title = this._isHybridForged
            ? `${frontDiameter}" – Anbefalt oppsett`
            : `${frontDiameter}" – ${isExactOem ? 'Original OEM-spesifikasjon' : 'Anbefalt størrelse'}`;
        }

        if (this._isHybridForged) {
          title = `${frontDiameter}" – Anbefalt oppsett`;
        }

        return `
          <button type="button"
            data-qb-pair-key="${this._esc(key)}"
            aria-pressed="${selected ? 'true' : 'false'}"
            style="
              width:100%;
              display:flex;
              align-items:center;
              gap:12px;
              margin:${index === 0 ? '0' : '9px 0 0'};
              padding:13px 14px;
              border:1px solid ${border};
              border-radius:8px;
              background:${background};
              color:inherit;
              text-align:left;
              cursor:pointer;
              font:inherit;
              transition:border-color .18s ease,background .18s ease,transform .18s ease;
            ">
            <span aria-hidden="true" style="
              width:22px;
              height:22px;
              flex:0 0 22px;
              display:inline-flex;
              align-items:center;
              justify-content:center;
              border:1.5px solid ${iconBorder};
              border-radius:50%;
              color:${iconColor};
              background:${iconBackground};
              font-size:14px;
              font-weight:800;
            ">✓</span>
            <span style="display:flex;flex-direction:column;gap:4px;min-width:0">
              <span class="wc__qb-card-title">${this._esc(title)}</span>
              <span class="wc__qb-card-sub">
                Foran: ${frontDiameter}x${frontWidth}" ET${frontEt}
                &nbsp; │ &nbsp;
                Bak: ${rearDiameter}x${rearWidth}" ET${rearEt}
              </span>
            </span>
          </button>`;
      }).join('');

      this._qbOemCard.querySelectorAll('[data-qb-pair-key]').forEach(button => {
        button.addEventListener('click', () => {
          this._selectQbPair(button.dataset.qbPairKey);
        });
      });
      return;
    }

    // Standard rendering when there is only one OEM combination.
    this._qbOemCard.style.border = '';
    this._qbOemCard.style.background = '';
    this._qbOemCard.style.boxShadow = '';
    this._qbOemCard.style.padding = '';

    const activePair = this._qbActivePair
      || pairCandidates.find(pair => this._qbPairKey(pair) === this._qbSelectedPairKey)
      || pairCandidates[0]
      || null;

    // Aventador Wheels is produced to the vehicle. The underlying Shopify
    // variant is used for price/cart only and must never replace the actual
    // OEM width/ET shown to the customer.
    if (this._isAventadorForged && activePair) {
      const frontDiameter = Number(activePair.frontDiameter) || this._selectedDiameter;
      const rearDiameter = Number(activePair.rearDiameter) || frontDiameter;
      const frontWidth = Number(activePair.frontWidth);
      const rearWidth = Number(activePair.rearWidth);
      const frontEt = Math.round(Number(activePair.frontEt));
      const rearEt = Math.round(Number(activePair.rearEt));
      const split = Math.round(frontDiameter) !== Math.round(rearDiameter)
        || Math.abs(frontWidth - rearWidth) > 0.001
        || frontEt !== rearEt;
      const specLine = split
        ? `Foran: ${frontDiameter}x${frontWidth}" ET${frontEt} &nbsp; │ &nbsp; Bak: ${rearDiameter}x${rearWidth}" ET${rearEt}`
        : `${frontDiameter}x${frontWidth}" ET${frontEt}`;

      this._qbOemCard.innerHTML = `
        <div class="wc__qb-card-row">
          <span class="wc__qb-card-icon wc__qb-card-icon--green">✓</span>
          <span class="wc__qb-card-body">
            <span class="wc__qb-card-title">${frontDiameter}" – Original OEM-spesifikasjon</span>
            <span class="wc__qb-card-sub">${specLine}</span>
          </span>
        </div>`;
      return;
    }

    const fWidth = this._extractWidthFromSize(this._selectedSize) ?? '?';

    if (isStaggered) {
      const rearSize = this._qbRearSelection.size || '';
      const rearDiameter = this._variantDiameter({ option2: rearSize }) || this._selectedDiameter;
      const rearWidth = this._extractWidthFromSize(rearSize) ?? '?';
      const frontEt = this._resolveEtValue(this._selectedEt, this._selectedExactEt);
      const rearEt = this._resolveEtValue(this._qbRearSelection.et, this._qbRearSelection.exactEt);
      const diamEntry = this._diamSpecs?.get(this._selectedDiameter);
      const specs = this._vehicle?.specs;
      const oemFrontWidth = Number(specs?.width_front ?? diamEntry?.front?.width ?? fWidth);
      const oemRearWidth = Number(specs?.width_rear ?? diamEntry?.rear?.width ?? rearWidth);
      const oemFrontEt = this._getOemEtForSelection(
        diamEntry,
        'front',
        specs?.et_front ?? specs?.et ?? null,
        this._selectedSize
      );
      const oemRearEt = this._getOemEtForSelection(
        diamEntry,
        'rear',
        specs?.et_rear ?? specs?.et_front ?? specs?.et ?? null,
        rearSize
      );
      const isExactOem = Number.isFinite(Number(fWidth))
        && Number.isFinite(Number(rearWidth))
        && Number.isFinite(oemFrontWidth)
        && Number.isFinite(oemRearWidth)
        && Number.isFinite(oemFrontEt)
        && Number.isFinite(oemRearEt)
        && frontEt !== null
        && rearEt !== null
        && Math.abs(Number(fWidth) - oemFrontWidth) <= 0.001
        && Math.abs(Number(rearWidth) - oemRearWidth) <= 0.001
        && frontEt === Math.round(oemFrontEt)
        && rearEt === Math.round(oemRearEt);
      const setupTitle = this._isHybridForged
        ? 'Anbefalt oppsett'
        : (isExactOem ? 'Original OEM-spesifikasjon' : 'Anbefalt størrelse');

      this._qbOemCard.innerHTML = `
        <div class="wc__qb-card-row">
          <span class="wc__qb-card-icon wc__qb-card-icon--green">✓</span>
          <span class="wc__qb-card-body">
            <span class="wc__qb-card-title">${this._selectedDiameter}" – ${setupTitle}</span>
            <span class="wc__qb-card-sub">
              Foran: ${this._selectedDiameter}x${fWidth}" ET${frontEt}
              &nbsp; │ &nbsp;
              Bak: ${rearDiameter}x${rearWidth}" ET${rearEt}
            </span>
          </span>
        </div>`;
    } else {
      const frontEt = this._resolveEtValue(this._selectedEt, this._selectedExactEt);
      const diamEntry = this._diamSpecs?.get(this._selectedDiameter);
      const specs = this._vehicle?.specs;
      const oemFrontWidth = Number(specs?.width_front ?? diamEntry?.front?.width ?? fWidth);
      const oemFrontEt = this._getOemEtForSelection(
        diamEntry,
        'front',
        specs?.et_front ?? specs?.et ?? null,
        this._selectedSize
      );
      const isExactOem = Number.isFinite(Number(fWidth))
        && Number.isFinite(oemFrontWidth)
        && Number.isFinite(oemFrontEt)
        && frontEt !== null
        && Math.abs(Number(fWidth) - oemFrontWidth) <= 0.001
        && frontEt === Math.round(oemFrontEt);
      const setupTitle = isExactOem ? 'Original OEM-spesifikasjon' : 'Anbefalt størrelse';

      this._qbOemCard.innerHTML = `
        <div class="wc__qb-card-row">
          <span class="wc__qb-card-icon wc__qb-card-icon--green">✓</span>
          <span class="wc__qb-card-body">
            <span class="wc__qb-card-title">${this._selectedDiameter}" – ${setupTitle}</span>
            <span class="wc__qb-card-sub">${this._selectedDiameter}x${fWidth}" ET${frontEt}</span>
          </span>
        </div>`;
    }
  }

  _renderQbFinish() {
    if (!this._qbFinish) return;

    if (!this._finishMode || !this._selectedSize) {
      this._qbFinish.hidden = true;
      return;
    }

    this._qbFinish.hidden = false;
    const label = this._selectedFinish || 'Velg Custom Finish';
    const doneClass = this._selectedFinish ? ' wc__qb-finish-btn--done' : '';
    const previewImg = this._selectedFinishImage
      ? `<img src="${this._esc(this._selectedFinishImage)}" alt="" style="width:28px;height:28px;border-radius:50%;object-fit:cover;flex-shrink:0">`
      : '';
    this._qbFinish.innerHTML = `<button type="button" class="wc__qb-finish-btn${doneClass}" data-role="qb-open-drawer">
      ${previewImg}<span>${this._esc(label)}</span>
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 4l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
    </button>`;
    this._qbFinish.querySelector('[data-role="qb-open-drawer"]')
      ?.addEventListener('click', () => this._openFinishDrawer());
  }

  /* ================================================================
   *  Step-based rendering (manual mode)
   * ================================================================ */

  _renderStepsBar() {
    const hasVehicle = !!this._vehicle?.specs;
    const steps = [];
    let n = 1;
    if (!hasVehicle) {
      steps.push({ num: n++, step: 'pcd', label: 'Boltsirkel', done: !!this._selectedPcd });
    }
    steps.push({ num: n++, step: 'size', label: 'Størrelse', done: !!this._selectedSize });
    steps.push({ num: n++, step: 'et', label: 'ET Innpress', done: this._isSelectedEtRange ? !!this._selectedExactEt : !!this._selectedEt });
    if (this._finishMode) {
      steps.push({ num: n++, step: 'finish', label: 'Finish', done: !!this._selectedFinish });
    }

    // Determine active step
    let activeIdx = 0;
    for (let i = 0; i < steps.length; i++) {
      if (steps[i].done) activeIdx = i + 1;
    }
    if (activeIdx >= steps.length) activeIdx = steps.length - 1;

    let html = '';
    steps.forEach((s, i) => {
      if (i > 0) {
        const connDone = steps[i - 1].done ? ' wc__step-connector--done' : '';
        html += `<div class="wc__step-connector${connDone}"></div>`;
      }
      let cls = 'wc__step-pill';
      if (s.done) cls += ' wc__step-pill--done';
      else if (i === activeIdx) cls += ' wc__step-pill--active';
      html += `<button class="${cls}" data-goto-step="${s.step}">${s.num}. ${s.label}</button>`;
    });

    this._stepsBar.innerHTML = html;

    // Bind click-to-scroll
    this._stepsBar.querySelectorAll('[data-goto-step]').forEach(btn => {
      btn.addEventListener('click', () => {
        const stepName = btn.dataset.gotoStep;
        const stepEl = this.querySelector(`[data-step="${stepName}"]`);
        if (stepEl) stepEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  _renumberStepHeaders() {
    const visibleSteps = [...this.querySelectorAll('[data-step]')].filter(s => !s.hidden);
    visibleSteps.forEach((s, i) => {
      const numEl = s.querySelector('.wc__step-num');
      if (numEl) numEl.textContent = i + 1;
    });
  }

  _getVehicleDisplayName(vehicle = this._vehicle) {
    if (!vehicle) return '';
    const make = String(vehicle.makeName || '').trim();
    const model = String(vehicle.modelName || '').trim();
    const year = String(vehicle.year || '').trim();
    const trim = String(vehicle.trimName || '').trim();

    const modelNorm = model.toLowerCase();
    const trimNorm = trim.toLowerCase();
    const includeTrim = !!trim && trimNorm !== modelNorm && !trimNorm.includes(modelNorm);

    return [make, model, year, includeTrim ? trim : ''].filter(Boolean).join(' ');
  }


  _renderVehicleBanner() {
    const vehicle = this._vehicle;

    if (!vehicle?.specs) {
      // No vehicle — simple inline line with small button
      this._vehicleBanner.innerHTML = `
        <div class="wc__vb wc__vb--empty">
          <span>&#x1F697;</span>
          <span style="color:#999;font-size:0.82rem">Ingen bil valgt</span>
          <button type="button" class="wc__vb-add-btn" data-role="wc-add-vehicle">Legg til</button>
        </div>`;
      this._vehicleBanner.querySelector('[data-role="wc-add-vehicle"]')
        ?.addEventListener('click', () => this._openVehicleModal());
      return;
    }

    const name = this._getVehicleDisplayName(vehicle);
    const specs = vehicle.specs;

    // Check compatibility — _noMatchActive is set when the size step found 0 visible sizes
    const realMatch = this._noMatchActive
      ? false
      : this._allVariants.some(v => this._isVariantCompatible(v, specs));

    const cls = realMatch ? 'wc__vb wc__vb--accent' : 'wc__vb wc__vb--warning';
    let icon = '';

const make = (name || '').toLowerCase();

if (make.includes('audi')) {
  icon = `
<div class="wc__brand-wrap">
  <img src="https://cdn.shopify.com/s/files/1/0790/6131/9926/files/Audilogo.png?v=1778472364" class="wc__brand-logo wc__brand-logo--audi">
  <div class="wc__brand-model">${this._esc(name)}</div>
</div>
`;
} else if (make.includes('ferrari')) {
  icon = `
    <div class="wc__brand-wrap">
      <img src="https://cdn.shopify.com/s/files/1/0790/6131/9926/files/FERRARILOGO.png?v=1778540251" class="wc__brand-logo wc__brand-logo--ferrari">
      <div class="wc__brand-model">${this._esc(name)}</div>
    </div>
  `;
} else if (make.includes('porsche')) {
  icon = `
<div class="wc__brand-wrap">
  <img src="https://cdn.shopify.com/s/files/1/0790/6131/9926/files/Porschelogo1.png?v=1778470826" class="wc__brand-logo wc__brand-logo--porsche">
  <div class="wc__brand-model">${this._esc(name)}</div>
</div>
`;
} else if (make.includes('bmw')) {
  icon = `
<div class="wc__brand-wrap">
  <img src="https://cdn.shopify.com/s/files/1/0790/6131/9926/files/Bmwlogo.png?v=1778474988" class="wc__brand-logo wc__brand-logo--bmw">
  <div class="wc__brand-model">${this._esc(name)}</div>
</div>
`;
} else if (make.includes('mercedes')) {
  icon = `
<div class="wc__brand-wrap">
  <img src="https://cdn.shopify.com/s/files/1/0790/6131/9926/files/MERCEDESLOGO1.png?v=1778522997" class="wc__brand-logo wc__brand-logo--mercedes">
  <div class="wc__brand-model">${this._esc(name)}</div>
</div>
`;
} else if (make.includes('tesla')) {
  icon = `
    <div class="wc__brand-wrap">
      <img src="https://cdn.shopify.com/s/files/1/0790/6131/9926/files/Tesla_Logo.png?v=1778540243" class="wc__brand-logo wc__brand-logo--tesla">
      <div class="wc__brand-model">${this._esc(name)}</div>
    </div>
  `;
} else if (make.includes('lamborghini')) {
  icon = `
    <div class="wc__brand-wrap">
      <img src="https://cdn.shopify.com/s/files/1/0790/6131/9926/files/LAMBORGHINILOGO.png?v=1778541327" class="wc__brand-logo wc__brand-logo--lamborghini">
      <div class="wc__brand-model">${this._esc(name)}</div>
    </div>
  `;
} else {
  icon = `
<div class="wc__brand-wrap">
  <div class="wc__brand-model">${this._esc(name)}</div>
</div>
`;
}

    const linksHtml = `<button type="button" class="wc__vb-link" data-role="wc-specs-open">Spesifikasjoner</button>
          <span class="wc__vb-dot">&middot;</span>
          <button type="button" class="wc__vb-link" data-role="wc-clear-vehicle">Endre bil</button>
          <span class="wc__vb-dot">&middot;</span>
          <button type="button" class="wc__vb-link" data-role="wc-remove-vehicle">Fjern bil</button>`;

    this._vehicleBanner.innerHTML = `
      <div class="${cls}">
        <div class="wc__vb-name">
  ${icon}
</div>
        <div class="wc__vb-links">
          ${linksHtml}
        </div>
      </div>`;

    this._vehicleBanner.querySelector('[data-role="wc-remove-vehicle"]')
      ?.addEventListener('click', () => { clearVehicle(); });
    this._vehicleBanner.querySelector('[data-role="wc-clear-vehicle"]')
      ?.addEventListener('click', () => this._openVehicleModal());
    this._vehicleBanner.querySelector('[data-role="wc-specs-open"]')
      ?.addEventListener('click', () => this._openSpecsModal());

    // Title-bar back button
    this._renderTitleBack();
  }

  /** Render OEM spec summary on non-QB products when a vehicle is selected. */
  _renderOemSpecCard() {
    const el = this.querySelector('[data-role="oem-spec-card"]');
    if (!el) return;

    if (this._quickBuyMode || !this._vehicle?.specs) {
      el.hidden = true;
      return;
    }

    const specs = this._vehicle.specs;
    const makeName = this._vehicle?.makeName || 'OEM';
    const isStaggered = this._isVehicleStaggered();

    // Use selected diameter, fall back to OEM
const diam = this._selectedDiameter || Math.round(specs.diameter_front || specs.oem_diameter || 0);
    if (!diam) { el.hidden = true; return; }

    // Per-diameter specs from API (same source as QB OEM card)
    const diamEntry = this._diamSpecs?.get(diam);

    // No data for this diameter — hide card (e.g. 23" on a car with no 23" spec)
    if (!diamEntry?.front) { el.hidden = true; return; }

    const fWidth = diamEntry.front.width ?? specs.width_front ?? 0;
    const fEt = diamEntry?.front?.et ?? specs.et_front ?? specs.et ?? null;

    let specsHtml;
    if (isStaggered) {
      const frontWidthsForDiam = diamEntry?.front?.widths || new Set();
      const rearOnlyWidths = diamEntry?.rear?.widths
        ? [...diamEntry.rear.widths].filter(w => !frontWidthsForDiam.has(w))
        : [];
      const rWidth = rearOnlyWidths.length > 0
        ? rearOnlyWidths[0]
        : (diamEntry?.rear?.width ?? specs.width_rear ?? fWidth);
      const rWidthKey = rWidth.toFixed(3);
      const rEtFromWidth = diamEntry?.rear?.etByWidth?.get(rWidthKey);
      const rEt = rEtFromWidth?.size ? [...rEtFromWidth][0] : (diamEntry?.rear?.et ?? specs.et_rear ?? fEt);
      const pairs = Array.isArray(diamEntry?.pairs) ? diamEntry.pairs : [];
      const selectedPair = pairs
        .filter(pair => pair.frontDiameter === diam)
        .sort((a, b) => {
          const aStaggered = a.rearDiameter !== a.frontDiameter || Math.abs(a.rearWidth - a.frontWidth) > 0.001;
          const bStaggered = b.rearDiameter !== b.frontDiameter || Math.abs(b.rearWidth - b.frontWidth) > 0.001;
          if (aStaggered !== bStaggered) return aStaggered ? -1 : 1;
          return Math.abs(a.frontWidth - fWidth) - Math.abs(b.frontWidth - fWidth);
        })[0];
      const displayRearDiameter = selectedPair?.rearDiameter ?? diam;

      specsHtml = `
        <div style="font-size:0.8rem;color:#ccc;margin-top:0.3rem;line-height:1.6;">
          <div><span style="color:#fff">Foran:</span> <strong style="color:#fff">${diam}x${fWidth}" ET${fEt != null ? Math.round(fEt) : '?'}</strong></div>
          <div><span style="color:#c8a051">Bak:</span> &nbsp;&nbsp;&nbsp;<strong style="color:#c8a051">${displayRearDiameter}x${rWidth}" ET${rEt != null ? Math.round(rEt) : '?'}</strong></div>
        </div>`;
    } else {
      specsHtml = `
        <div style="font-size:0.8rem;color:#ccc;margin-top:0.3rem;">
          <strong style="color:#fff">${diam}x${fWidth}" ET${fEt != null ? Math.round(fEt) : '?'}</strong>
        </div>`;
    }

    const title = `Original ${this._esc(makeName)}-spesifikasjon`;
    const hasManualCard = !this._isAventadorForged && !this._manualMode;
    const customFitHint = hasManualCard
      ? `<div style="font-size:0.72rem;color:#888;margin-top:0.45rem;line-height:1.4;">Dette er anbefalt oppsett for din bil.<br>Du kan ogs\u00e5 velge <span style="color:#c8a051">custom fit</span> under.</div>`
      : '';

    el.hidden = false;
    el.innerHTML = `
      <div class="wc__qb-card" style="margin-top:0.5rem;margin-bottom:0.6rem;">
        <div class="wc__qb-card-row" style="align-items:flex-start;cursor:default;">
          <span class="wc__qb-card-icon wc__qb-card-icon--green" style="margin-top:2px;">\u2713</span>
          <span class="wc__qb-card-body">
            <span class="wc__qb-card-title">${title}</span>
            ${specsHtml}
            ${customFitHint}
          </span>
        </div>
      </div>`;
  }

  _renderTitleBack() {
    if (!this._titleBackEl) return;
    // Do not offer a back button into the broken OEM flow when Custom Fit
    // was opened automatically because the vehicle stops at 18" OEM.
    const showManualBack = this._manualMode && !this._autoCustomFitForSmallOem;
    const showQbBack = !!this._vehicle?.specs && !this._quickBuyMode && !this._manualMode;
    if (showManualBack || showQbBack) {
      this._titleBackEl.innerHTML = '<button type="button" class="wc__vb-link wc__vb-link--back" data-role="title-back-btn">&#x2190; Tilbake</button>';
      this._titleBackEl.querySelector('[data-role="title-back-btn"]')
        .addEventListener('click', () => {
          if (showManualBack) {
            this._manualMode = false;
            this._hidePcdInManualMode = false;
            this._selectedPcd = null;
            this._selectedDiameter = null;
            this._selectedSize = null;
            this._selectedEt = null;
            this._selectedExactEt = null;
            this._savedByDiam = {};
            this._applyVehicleFilter();
            if (this._vehicle?.specs) this._quickBuyMode = true;
          } else {
            this._quickBuyMode = true;
            this._selectedPcd = null;
            this._selectedDiameter = null;
            this._selectedSize = null;
            this._selectedEt = null;
            this._selectedExactEt = null;
          }
          this._render();
        });
    } else {
      this._titleBackEl.innerHTML = '';
    }
  }

  _renderManualModeCard() {
    if (!this._manualModeCard) return;
    const shouldShow = !this._isAventadorForged
      && !!this._vehicle?.specs
      && !this._manualMode
      && !this._quickBuyMode;
    this._manualModeCard.hidden = !shouldShow;
  }

  _renderNoMatchCards() {
    if (!this._noMatchCards) return;
    this._noMatchCards.hidden = false;
    if (this._manualModeCard) this._manualModeCard.hidden = true;

    const isAventador = this.hasAttribute('data-aventador');
    const manualPcd = this._getVehicleMatchedPcd();
    const showManualLink = isAventador || this._hasManualOptionsForVehiclePcd();

    let html;

    if (this._isAventadorForged) {
      html = `
      <div class="wc__nm-warn-banner wc__nm-warn-banner--aventador">
        <div class="wc__nm-warn-title">OEM-tilpasses bilen din</div>
        <div class="wc__nm-warn-desc">Felgene produseres med korrekt boltsirkel, navhull og innpress. Originale bolter benyttes.</div>
      </div>`;
    } else {
      html = `
      <div class="wc__nm-warn-banner">
        <div class="wc__nm-warn-title">⚠️ Montering krever tilpassede bolter og senterringer for korrekt passform.
</div>
        <div class="wc__nm-warn-desc">Du kan sette opp manuelt, – eller velge felger med OEM-garanti.</div>
      </div>`;
    }

    if (showManualLink) {
      html += `
      <button type="button" class="wc__nm-manual-link" data-role="nm-manual" data-pcd="${this._escAttr(manualPcd || '')}">
        <div class="wc__nm-manual-text">
          <div class="wc__nm-manual-title" style="display: flex; align-items: center;">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#999" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-right:0.4rem;"><line x1="4" y1="21" x2="4" y2="14"></line><line x1="4" y1="10" x2="4" y2="3"></line><line x1="12" y1="21" x2="12" y2="12"></line><line x1="12" y1="8" x2="12" y2="3"></line><line x1="20" y1="21" x2="20" y2="16"></line><line x1="20" y1="12" x2="20" y2="3"></line><line x1="1" y1="14" x2="7" y2="14"></line><line x1="9" y1="8" x2="15" y2="8"></line><line x1="17" y1="16" x2="23" y2="16"></line></svg> 
            Sett opp manuelt
          </div>
          <div class="wc__nm-manual-desc">Velg st\u00F8rrelse, bredde og innpress manuelt.</div>
        </div>
        <svg class="wc__nm-manual-chevron" style="color: #c8a051;" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
      </button>`;
    }

    if (!isAventador) {
      html += `
      <a href="/collections/aventador-wheels" class="wc__nm-hero">
        <div class="wc__nm-hero-header">
          <span class="wc__nm-hero-icon" style="margin-right:0.2rem">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="#a42" stroke="#d44" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:-4px"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10" stroke="#fff" stroke-width="2.5"/></svg>
          </span>
          <span class="wc__nm-hero-title">Aventador Wheels \u2013 OEM Garanti</span>
        </div>
        <div class="wc__nm-hero-desc" style="color:#aaa">Originalspesifisert <strong>passform</strong> \u2013 levert ferdig tilpasset <strong>din bil</strong>.</div>
        <ul class="wc__nm-hero-checks">
          <li style="color:#aaa"><span style="color:#27ae60;font-weight:700">100 %</span> korrekt bolt, <strong>nav</strong> og innpress til <strong>din bil</strong></li>
          <li style="color:#aaa"><span style="color:#27ae60;font-weight:700">Ingen</span> spacere eller modifikasjoner</li>
          <li style="color:#aaa">Produseres <strong>spesifikt</strong> for din bil</li>
          <li style="color:#aaa">P\u00E5virker <strong style="text-decoration:underline;text-decoration-color:#27ae60">ikke</strong> bilens garanti</li>
        </ul>
        <span class="wc__nm-hero-cta">Se utvalget &rarr;</span>
      </a>`;
    }

    this._noMatchCards.innerHTML = html;

    this._noMatchCards.querySelectorAll('[data-role="nm-manual"]')
      .forEach(btn => btn.addEventListener('click', () => {
        const forcedPcd = (btn.dataset.pcd || '').trim();
        this._manualMode = true;
        this._hidePcdInManualMode = true;
        this._quickBuyMode = false;
        this._selectedPcd = forcedPcd || null;
        this._selectedDiameter = null;
        this._selectedSize = null;
        this._selectedEt = null;
        this._selectedExactEt = null;
        this._applyVehicleFilter();
        this._render();
      }));
  }

  _renderAxleToggle() {
    if (!this._axleToggleEl) return;

    // Custom fit/manual mode should show all options in one flow,
    // so front/rear tabs are intentionally hidden.
    if (this._manualMode) {
      this._axleToggleEl.hidden = true;
      return;
    }

    if (!this._isVehicleStaggered()) {
      this._axleToggleEl.hidden = true;
      return;
    }

    const fActive = this._activeAxle === 'front';
    const rActive = this._activeAxle === 'rear';
    const fCarted = this._cartedAxles.front;
    const rCarted = this._cartedAxles.rear;
    const fCheck = fCarted ? '<span class="wc__tab-check">\u2713</span>' : '';
    const rCheck = rCarted ? '<span class="wc__tab-check">\u2713</span>' : '';
    const fSub = fCarted ? `<span class="wc__tab-sub">${fCarted.qty}x ${fCarted.size}</span>` : '';
    const rSub = rCarted ? `<span class="wc__tab-sub">${rCarted.qty}x ${rCarted.size}</span>` : '';

    const otherAxle = fActive ? '<strong style="color:#c8a051">Bak</strong>' : '<strong style="color:#fff">Foran</strong>';
    const currentLabel = fActive ? '<span style="color:#fff">foran</span>' : '<span style="color:#c8a051">bak</span>';
    const hintText = `Du ser n\u00e5 st\u00f8rrelser ${currentLabel}. Trykk p\u00e5 ${otherAxle} for \u00e5 velge ${fActive ? 'bakfelg' : 'forfelg'}.`;

    const html = `
      <div class="wc__axle-tabs">
        <button type="button" class="wc__axle-tab${fActive ? ' wc__axle-tab--active' : ''}" data-axle="front">
          <span class="wc__tab-label" style="color:#fff">Foran${fCheck}</span>${fSub}
        </button>
        <button type="button" class="wc__axle-tab${rActive ? ' wc__axle-tab--active' : ''}" data-axle="rear">
          <span class="wc__tab-label" style="color:#c8a051">Bak${rCheck}</span>${rSub}
        </button>
      </div>
      <div style="padding:0.3rem 0 0.7rem;font-size:0.78rem;color:#999;line-height:1.35;">
        ${hintText}
      </div>`;

    this._axleToggleEl.innerHTML = html;
    this._axleToggleEl.hidden = false;

    this._axleToggleEl.querySelectorAll('[data-axle]').forEach(btn => {
      btn.addEventListener('click', () => this._onAxleSwitch(btn.dataset.axle));
    });
  }

  _renderPcdStep() {
    // Gather visible PCDs
    const pcds = [];
    for (const [pcd, data] of this._pcdSet) {
      if (this._hiddenPcds?.has(pcd)) continue;
      pcds.push({ pcd, count: data.sizes.size });
    }

    if (pcds.length === 0) {
      this._pcdGrid.innerHTML = '<div class="wc__empty-msg">Ingen boltsirkler tilgjengelig</div>';
      return;
    }

    // If only one PCD, auto-select it
    if (pcds.length === 1 && !this._selectedPcd) {
      this._selectedPcd = pcds[0].pcd;
    }

    let html = '<div class="wc__pcd-grid">';
    for (const { pcd, count } of pcds) {
      const sel = pcd === this._selectedPcd ? ' wc__btn3d--selected' : '';
      const pcdLower = pcd.toLowerCase();
      const isUniversal = pcdLower === 'custom';
      const badge = isUniversal ? '' : `<span class="wc__pcd-badge">(${count})</span>`;
      const uStyle = isUniversal ? ' style="white-space:nowrap;grid-column:1/-1"' : '';
      html += `<button class="wc__btn3d${sel}"${uStyle} data-pcd="${this._escAttr(pcd)}">
        <span class="wc__btn3d-top">
          ${this._esc(pcd)}
          ${badge}
        </span>
      </button>`;
    }
    html += '</div>';
    this._pcdGrid.innerHTML = html;

    // Bind clicks
    this._pcdGrid.querySelectorAll('[data-pcd]').forEach(btn => {
      btn.addEventListener('click', () => this._onPcdSelect(btn.dataset.pcd));
    });
  }

  _renderSizeStep() {

    if (!this._selectedPcd) {
      this._sizeGrid.innerHTML = '<div class="wc__empty-msg">Velg boltsirkel først</div>';
      return;
    }

    // Gather sizes for selected PCD
    const sizes = [];
    const pcdData = this._pcdSet.get(this._selectedPcd);
    if (!pcdData) return;

    // Width filtering: per-diameter discrete widths from _diamSpecs, fallback to global range
    const axleSpecs = this._getAxleSpecs();
    const axleKey = this._activeAxle === 'rear' ? 'rear' : 'front';

    for (const size of pcdData.sizes) {
      const sizeKey = `${this._selectedPcd}|${size}`;
      const sizeData = this._sizeSet.get(sizeKey);
      if (!sizeData) continue;

      // Check if any variant of this size is in filteredVariants
      const isVisible = sizeData.variants.some(v =>
        this._filteredVariants.includes(v)
      );
      if (!isVisible && this._vehicle?.specs && !this._manualMode) continue;

      // Width filter: only show sizes whose width matches a discrete OEM/alt spec
      if (this._vehicle?.specs && !this._manualMode) {
        const wMatch = size.match(/^(\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)/);
        if (wMatch) {
          const sizeDiam = Math.round(parseFloat(wMatch[1]));
          const variantWidth = parseFloat(wMatch[2]);
          const diamEntry = this._diamSpecs?.get(sizeDiam);
          const diamAxle = diamEntry?.[axleKey];
          if (diamAxle?.widths) {
            // Per-diameter discrete filter: width must be in the allowed set
            if (!diamAxle.widths.has(variantWidth)) continue;
          } else {
            // Fallback to global range
            const widthMin = axleSpecs.width;
            const widthMax = axleSpecs.widthMax;
            if (widthMin > 0 && widthMax > 0 && (variantWidth < widthMin || variantWidth > widthMax)) continue;
          }
        }
      }

      // Aggregate stock for this size
      let totalQty = 0;
      let anyAvailable = false;
      let anyCanBackorder = false;
      for (const v of sizeData.variants) {
        const inv = this._inventory[this._variantKey(v)];
        if (inv) {
          totalQty += Math.max(0, inv.qty);
          if (inv.inventoryPolicy === 'continue') anyCanBackorder = true;
        }
        if (v.available) anyAvailable = true;
      }

      const diamMatch = size.match(/^(\d+)/);
      const diam = diamMatch ? parseInt(diamMatch[1], 10) : 18;
      sizes.push({ size, diam, totalQty, anyAvailable, anyCanBackorder });
    }

    // Sort sizes by diameter then width (numeric)
    sizes.sort((a, b) => {
      if (a.diam !== b.diam) return a.diam - b.diam;
      const wA = parseFloat(a.size.replace(/^[\d]+x/, '')) || 0;
      const wB = parseFloat(b.size.replace(/^[\d]+x/, '')) || 0;
      return wA - wB;
    });

    if (sizes.length === 0) {
      // If vehicle is selected and no sizes fit, trigger no-match state
      if (this._vehicle?.specs && !this._manualMode && !this._hasAventadorManufacturableVariant()) {
        this._noMatchActive = true;
        if (this._manualModeCard) this._manualModeCard.hidden = true;
        this._selectedSize = null;
        this._selectedEt = null;
        this._selectedExactEt = null;
        this.querySelectorAll('[data-step]').forEach(s => s.hidden = true);
        if (this._stepsBar) this._stepsBar.hidden = true;
        if (this._axleToggleEl) this._axleToggleEl.hidden = true;
        if (this._cartSection) this._cartSection.hidden = true;
        if (this._compatCard) this._compatCard.hidden = false;
        const compatEl = this.querySelector('[data-role="wc-compat"]');
        if (compatEl) {
        compatEl.innerHTML = this._isAventadorForged
          ? `<div class="wc__card-row wc__card-row--ok">${CHECK_SVG}<span><strong style="color:#d4a843">OEM-tilpasses bilen din</strong></span></div>`
          : `<div class="wc__card-row wc__card-row--fail">${X_SVG}<span>Ingen konfigurasjon passer</span></div>`;
      }
        this._renderVehicleBanner();
        this._renderNoMatchCards();
        return;
      }
      this._sizeGrid.innerHTML = '<div class="wc__empty-msg">Ingen størrelser tilgjengelig</div>';
      return;
    }

    // Auto-select if only one
    if (sizes.length === 1 && !this._selectedSize) {
      this._selectedSize = sizes[0].size;
    }

    // Extract unique diameters
    const diameters = [...new Set(sizes.map(s => s.diam))].sort((a, b) => a - b);

    // Auto-select diameter
    if (diameters.length === 1) {
      this._selectedDiameter = diameters[0];
    } else if (this._selectedDiameter === null || !diameters.includes(this._selectedDiameter)) {
      // Diameter changed — reset size/ET so they don't show stale values.
      // Prefer the diameter explicitly selected on the search page when available.
      this._selectedSize = null;
      this._selectedEt = null;
      this._selectedExactEt = null;
      this._selectedDiameter = (this._urlDiameter && diameters.includes(this._urlDiameter))
        ? this._urlDiameter
        : diameters[0];
    }

    // If selected size no longer exists under current diameter, reset it
    const filtered0 = sizes.filter(s => s.diam === this._selectedDiameter);
    if (this._selectedSize && !filtered0.some(s => s.size === this._selectedSize)) {
      this._selectedSize = null;
      this._selectedEt = null;
      this._selectedExactEt = null;
    }
    // Auto-select if only one size for this diameter
    if (!this._selectedSize && filtered0.length === 1) {
      this._selectedSize = filtered0[0].size;
    }

    let html = '';

    // Diameter pills (skip if only one diameter)
    if (diameters.length > 1) {
      html += '<div class="wc__diam-row">';
      for (const d of diameters) {
        const sel = d === this._selectedDiameter ? ' wc__diam-pill--selected' : '';
        const check = d === this._selectedDiameter ? '<span class="wc__qb-diam-check">\u2713</span>' : '';
        html += `<button type="button" class="wc__diam-pill${sel}" data-diam="${d}">${d}"${check}</button>`;
      }
      html += '</div>';
    }

    // Filter sizes by selected diameter
    const filtered = sizes.filter(s => s.diam === this._selectedDiameter);

    html += '<div class="wc__size-grid">';
    for (const { size, totalQty, anyAvailable, anyCanBackorder } of filtered) {
      const isSel = size === this._selectedSize;
      const sel = isSel ? ' wc__btn3d--selected' : '';
      const circleInner = isSel ? '<span class="wc__size-check">&#x2713;</span>' : '';

      let stockDot = 'wc__stock-dot--ok';
      let stockLabel = 'På lager';
      if (!anyAvailable || totalQty <= 0) {
        stockDot = anyCanBackorder ? 'wc__stock-dot--out' : 'wc__stock-dot--sold-out';
        stockLabel = anyCanBackorder ? 'Bestilling' : 'Utsolgt';
      } else if (totalQty <= 4) {
        stockDot = 'wc__stock-dot--low';
        stockLabel = `Kun ${totalQty}`;
      }

      html += `<button class="wc__btn3d wc__size-card${sel}" data-size="${this._escAttr(size)}">
        <span class="wc__btn3d-top">
          <span class="wc__size-label">
            <span class="wc__size-circle">${circleInner}</span>
            ${this._esc(size)}
          </span>
          <span class="wc__size-stock">
            <span class="wc__stock-dot ${stockDot}"></span> ${stockLabel}
          </span>
        </span>
      </button>`;
    }
    html += '</div>';
    this._sizeGrid.innerHTML = html;

    // Bind diameter pill clicks
    if (!this._savedByDiam) this._savedByDiam = {};
    this._sizeGrid.querySelectorAll('[data-diam]').forEach(btn => {
      btn.addEventListener('click', () => {
        const d = parseInt(btn.dataset.diam, 10);
        if (d === this._selectedDiameter) return;
        // Save current selection for this diameter before switching
        if (this._selectedSize && this._selectedDiameter) {
          this._savedByDiam[this._selectedDiameter] = {
            size: this._selectedSize,
            et: this._selectedEt,
            exactEt: this._selectedExactEt,
          };
        }
        this._selectedDiameter = d;
        // Restore previous size for this diameter, but always reset ET
        const saved = this._savedByDiam[d];
        this._selectedSize = saved?.size ?? null;
        this._selectedEt = null;
        this._selectedExactEt = null;
        this._render();
      });
    });

    // Bind size card clicks
    this._sizeGrid.querySelectorAll('[data-size]').forEach(btn => {
      btn.addEventListener('click', () => this._onSizeSelect(btn.dataset.size));
    });
  }

  _renderEtStep() {
    if (!this._selectedPcd || !this._selectedSize) {
      this._etArea.innerHTML = '<div class="wc__empty-msg">Velg størrelse først</div>';
      return;
    }

    const sizeKey = `${this._selectedPcd}|${this._selectedSize}`;
    const sizeData = this._sizeSet.get(sizeKey);
    if (!sizeData || sizeData.variants.length === 0) {
      this._etArea.innerHTML = '<div class="wc__empty-msg">Ingen ET-verdier tilgjengelig</div>';
      return;
    }

    // Gather all ET options for this PCD+Size, classify each
    const etOptions = [];
    for (const v of sizeData.variants) {
      if (!this._filteredVariants.includes(v) && this._vehicle?.specs && !this._manualMode) continue;
      const et = (v.option3 || '').trim();
      if (!et) continue;
      const inv = this._inventory[this._variantKey(v)];
      const qty = inv ? Math.max(0, inv.qty) : 0;
      const isRange = this._isEtRange(et);
      const inventoryPolicy = inv ? inv.inventoryPolicy : 'deny';
      etOptions.push({ et, variant: v, qty, available: v.available, isRange, inventoryPolicy });
    }

    if (etOptions.length === 0) {
      this._etArea.innerHTML = '<div class="wc__empty-msg">Ingen ET-verdier tilgjengelig</div>';
      return;
    }

    // Remove ranges that are subsets of a wider range
    // e.g. ET20-40 is a subset of ET20-51, so keep only ET20-51
    this._removeSubsetRanges(etOptions);

    // Check if ALL options are ranges (range-only mode: show slider directly)
    const allRanges = etOptions.every(o => o.isRange);

    // Vehicle ET for color-coding — use per-diameter OEM ET
    const diamEntry = this._diamSpecs?.get(this._selectedDiameter);
    const axleKey = this._activeAxle === 'rear' ? 'rear' : 'front';
    const vehicleEt = diamEntry?.[axleKey]?.et ?? this._getAxleSpecs().et;

    // Auto-select the ET closest to OEM for this diameter when nothing is selected
    if (!this._selectedEt && vehicleEt !== null) {
      const oemRound = Math.round(vehicleEt);
      let bestEt = null;
      let bestDiff = Infinity;
      for (const o of etOptions) {
        if (o.isRange) {
          const bounds = this._parseEtBounds(o.et);
          if (!bounds) continue;
          const lo = bounds.min;
          const hi = bounds.max;
          if (oemRound >= lo && oemRound <= hi) { bestEt = o.et; bestDiff = 0; break; }
          const d = Math.min(Math.abs(oemRound - lo), Math.abs(oemRound - hi));
          if (d < bestDiff) { bestDiff = d; bestEt = o.et; }
        } else {
          const m = o.et.match(/ET(-?\d+)/);
          if (m) {
            const d = Math.abs(parseInt(m[1], 10) - oemRound);
            if (d < bestDiff) { bestDiff = d; bestEt = o.et; }
          }
        }
      }
      if (bestEt) {
        this._selectedEt = bestEt;
        // Set exactEt for ranges
        if (this._isEtRange(bestEt)) {
          const bounds = this._parseEtBounds(bestEt);
          if (bounds) {
            this._selectedExactEt = Math.max(bounds.min, Math.min(bounds.max, oemRound));
          }
        } else {
          const m = bestEt.match(/ET(-?\d+)/);
          if (m) this._selectedExactEt = parseInt(m[1], 10);
        }
      }
    }

    // Fallback: auto-select single range ET when no vehicle data triggered selection
    if (!this._selectedEt && etOptions.length === 1 && etOptions[0].isRange) {
      const et = etOptions[0].et;
      this._selectedEt = et;
      const bounds = this._parseEtBounds(et);
      if (bounds) {
        this._selectedExactEt = Math.round((bounds.min + bounds.max) / 2);
      }
    }

    const showEtHint = !this._isAventadorForged && !(allRanges && etOptions.length === 1);
    let html = showEtHint ? `<div style="font-size:0.78rem;color:#999;line-height:1.5;margin-bottom:0.5rem;">
        Velg innpress (ET):
        <div style="margin-top:0.25rem;">
          <span style="display:inline-flex;align-items:center;gap:4px;margin-right:12px;"><span class="wc__stock-dot wc__stock-dot--ok" style="position:static;"></span> <strong style="color:#ccc;">Lager</strong> \u2013 klar for <strong style="color:#ccc;">rask levering</strong></span><br>
          <span style="display:inline-flex;align-items:center;gap:4px;"><strong style="color:#ccc;">Tilpasset ET</strong> \u2013 freses spesifikt til din bil</span>
        </div>
      </div>` : '';

    if (allRanges && etOptions.length === 1) {
      // ── Range-only with selection: show interactive slider directly ──
      const rangeEt = etOptions[0].et;
      html += this._buildRangeControls(rangeEt);
    } else {
      // ── Mixed or single-only: show buttons ──
      html += '<div class="wc__et-grid">';
      for (const { et, qty, available, isRange, inventoryPolicy } of etOptions) {
        const sel = et === this._selectedEt ? ' wc__btn3d--selected' : '';

        let stockDot = 'wc__stock-dot--ok';
        let stockLabel = 'Lager';
        if (!available || qty <= 0) { stockDot = inventoryPolicy === 'continue' ? 'wc__stock-dot--out' : 'wc__stock-dot--sold-out'; stockLabel = inventoryPolicy === 'continue' ? 'Best.' : 'Utsolgt'; }
        else if (qty <= 4) { stockDot = 'wc__stock-dot--low'; stockLabel = qty + ' stk'; }

        // OEM badge for single ETs matching active axle's OEM ET
        let etOemTag = '';
        if (vehicleEt !== null && !isRange) {
          const etM = et.match(/ET(-?\d+)/);
          if (etM && parseInt(etM[1], 10) === Math.round(vehicleEt)) {
            etOemTag = ' <span class="wc__oem-badge">OEM</span>';
          }
        }

        html += `<button class="wc__btn3d${sel}" data-et="${this._escAttr(et)}">
          <span class="wc__btn3d-top">
            <span class="wc__et-label">${this._esc(et.replace(/\s*\(.*\)/, ''))}${etOemTag}</span>
            <span class="wc__et-stock"><span class="wc__stock-dot ${stockDot}"></span> ${stockLabel}</span>
          </span>
        </button>`;
      }
      html += '</div>';

      // If selected ET is a range, show dropdown + slider below the buttons
      if (this._selectedEt && this._isEtRange(this._selectedEt)) {
        html += this._buildRangeControls(this._selectedEt);
      }
    }

    this._etArea.innerHTML = html;

    // Bind button clicks (only if buttons exist)
    this._etArea.querySelectorAll('[data-et]').forEach(btn => {
      btn.addEventListener('click', () => this._onEtSelect(btn.dataset.et));
    });

    // Bind range controls (dropdown + interactive slider)
    this._bindRangeControls();

    // ET calculator placement (manual/custom flow):
    // show directly under ET controls, before price/add-to-cart step.
    if (this._manualMode) {
      const calcIcon = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="2" width="16" height="20" rx="2"/><line x1="8" y1="6" x2="16" y2="6"/><line x1="8" y1="10" x2="10" y2="10"/><line x1="14" y1="10" x2="16" y2="10"/><line x1="8" y1="14" x2="10" y2="14"/><line x1="14" y1="14" x2="16" y2="14"/><line x1="8" y1="18" x2="16" y2="18"/></svg>';
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'wc__et-calc-btn wc__et-calc-btn--flow';
      btn.setAttribute('data-role', 'wc-et-calc-open-step');
      btn.innerHTML = `<span class="wc__et-calc-btn-top"><span class="wc__et-calc-btn-icon">${calcIcon}</span><span class="wc__et-calc-btn-title">Sjekk innpress med ET-kalkulator</span><span class="wc__et-calc-btn-chevron" aria-hidden="true">&#x203A;</span></span>`;
      this._etArea.appendChild(btn);
      btn.addEventListener('click', () => this._openEtCalcModal());
    }
  }

  /** Build the range dropdown + visual slider HTML for a range ET like "ET20-38" */
  _buildRangeControls(etStr) {
    const bounds = this._parseEtBounds(etStr);
    if (!bounds) return '';
    const etMin = bounds.min;
    const etMax = bounds.max;

    this._etRangeMin = etMin;
    this._etRangeMax = etMax;

    const diamEntry = this._diamSpecs?.get(this._selectedDiameter);
    const axleKey = this._activeAxle === 'rear' ? 'rear' : 'front';
    const vehicleEt = diamEntry?.[axleKey]?.et ?? this._getAxleSpecs().et;

    const oemEt = vehicleEt !== null ? Math.round(vehicleEt) : null;
    let selectHtml = '<option value="" disabled selected>Velg innpress</option>';
    for (let i = etMin; i <= etMax; i++) {
      const selected = this._selectedExactEt === i ? ' selected' : '';
      const oemLabel = (oemEt !== null && i === oemEt) ? ' (OEM)' : '';
      selectHtml += `<option value="ET${i}"${selected}>ET${i}${oemLabel}</option>`;
    }

    const range = etMax - etMin || 1;
    const thumbPos = this._selectedExactEt !== null
      ? ((this._selectedExactEt - etMin) / range * 100) : 0;

    // OEM marker on slider
    let oemMarkerHtml = '';
    if (oemEt !== null && oemEt >= etMin && oemEt <= etMax) {
      const oemPos = (oemEt - etMin) / range * 100;
      oemMarkerHtml = `<div class="wc__et-slider-oem" style="left:${oemPos}%"></div>`;
    }

    let html = '<div class="wc__et-range" style="margin-top:0.75rem">';
    html += '<div class="wc__et-select-row">';
    html += '<span class="wc__et-select-label">Tilpasset ET:</span>';
    html += `<select class="wc__et-select" data-role="wc-et-dropdown">${selectHtml}</select>`;
    html += '</div>';
    html += `<div class="wc__et-slider-wrap" data-role="wc-et-slider" data-min="${etMin}" data-max="${etMax}">`;
    html += '<div class="wc__et-slider-track"></div>';
    html += oemMarkerHtml;
    html += `<div class="wc__et-slider-thumb" style="left:${thumbPos}%"${this._selectedExactEt === null ? ' hidden' : ''}></div>`;
    html += '</div>';
    html += `<div class="wc__et-slider-labels"><span>${etMin}</span><span>${etMax}</span></div>`;
    html += '</div>';
    return html;
  }

  /** Bind dropdown change + slider drag/click interactions */
  _bindRangeControls() {
    const rangeSelect = this._etArea.querySelector('[data-role="wc-et-dropdown"]');
    if (rangeSelect) {
      rangeSelect.addEventListener('change', () => {
        const val = parseInt((rangeSelect.value || '').replace(/\D/g, ''), 10);
        if (!isNaN(val)) this._onExactEtSelect(val);
      });
    }

    const sliderWrap = this._etArea.querySelector('[data-role="wc-et-slider"]');
    if (!sliderWrap) return;

    const etMin = parseInt(sliderWrap.dataset.min, 10);
    const etMax = parseInt(sliderWrap.dataset.max, 10);
    const range = etMax - etMin || 1;

    const setFromX = (clientX) => {
      const rect = sliderWrap.getBoundingClientRect();
      const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const val = Math.round(etMin + pct * range);
      this._onExactEtSelect(val);
    };

    // Click on track to jump
    sliderWrap.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      sliderWrap.setPointerCapture(e.pointerId);
      setFromX(e.clientX);
    });

    sliderWrap.addEventListener('pointermove', (e) => {
      if (!sliderWrap.hasPointerCapture(e.pointerId)) return;
      setFromX(e.clientX);
    });

    sliderWrap.addEventListener('pointerup', (e) => {
      sliderWrap.releasePointerCapture(e.pointerId);
    });
  }

  _renderFinishStep() {
    if (!this._finishMode) {
      if (this._finishStep) this._finishStep.hidden = true;
      return;
    }
    if (this._finishStep) this._finishStep.hidden = false;

    // Drawer mode — show button with preview
    const previewStyle = this._selectedFinish ? 'display:inline-block' : 'display:none';
    const label = this._selectedFinish || 'Velg Custom Finish';
    const previewSrc = this._selectedFinishImage || '';

    this._finishArea.innerHTML = `
      <button type="button" class="wc__finish-btn" data-role="wc-open-drawer">
        <img class="wc__finish-btn-preview" src="${previewSrc}" alt="" style="${previewStyle}">
        <span data-role="wc-finish-label">${this._esc(label)}</span>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M6 4l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>`;

    this._finishArea.querySelector('[data-role="wc-open-drawer"]')
      ?.addEventListener('click', () => this._openFinishDrawer());
  }

  /* ================================================================
   *  Selection handlers
   * ================================================================ */

  _onPcdSelect(pcd) {
    this._selectedPcd = pcd;
    this._selectedDiameter = null;
    this._selectedSize = null;
    this._selectedEt = null;
    this._selectedExactEt = null;
    this._render();

    // Auto-select if only one size
    const pcdData = this._pcdSet.get(pcd);
    if (pcdData) {
      const visibleSizes = [...pcdData.sizes].filter(s => {
        const sizeKey = `${pcd}|${s}`;
        const sizeData = this._sizeSet.get(sizeKey);
        if (!sizeData) return false;
        if (!this._vehicle?.specs || this._manualMode) return true;
        return sizeData.variants.some(v => this._filteredVariants.includes(v));
      });
      if (visibleSizes.length === 1) {
        this._onSizeSelect(visibleSizes[0]);
        return;
      }
    }
  }

  _onSizeSelect(size) {
    this._selectedSize = size;
    this._selectedEt = null;
    this._selectedExactEt = null;
    this._render();
  }

  _onEtSelect(et) {
    this._selectedEt = et;
    this._selectedExactEt = null;

    if (this._isEtRange(et)) {
      // Range selected — auto-select midpoint (or vehicle ET if available)
      const bounds = this._parseEtBounds(et);
      if (!bounds) return;
      const etMin = bounds.min;
      const etMax = bounds.max;
      const axleEt = this._getAxleSpecs().et;
      if (axleEt !== null && axleEt >= etMin && axleEt <= etMax) {
        this._selectedExactEt = axleEt;
      } else {
        this._selectedExactEt = Math.round((etMin + etMax) / 2);
      }
      this._render();
      this._driveHorizonPicker();
    } else {
      // Single ET — drive Horizon immediately
      const etMatch = et.match(/ET(-?\d+)/);
      if (etMatch) {
        this._selectedExactEt = parseInt(etMatch[1], 10);
      }
      this._render();
      this._driveHorizonPicker();
    }
  }

  _onExactEtSelect(etVal) {
    this._selectedExactEt = etVal;

    // Lightweight slider + dropdown update (avoid full re-render for smooth drag)
    const thumb = this._etArea.querySelector('.wc__et-slider-thumb');
    const slider = this._etArea.querySelector('[data-role="wc-et-slider"]');
    if (thumb && slider) {
      const min = parseInt(slider.dataset.min, 10);
      const max = parseInt(slider.dataset.max, 10);
      const range = max - min || 1;
      thumb.hidden = false;
      thumb.style.left = `${((etVal - min) / range) * 100}%`;
    }
    const dropdown = this._etArea.querySelector('[data-role="wc-et-dropdown"]');
    if (dropdown) dropdown.value = `ET${etVal}`;

    // Update steps bar done-state for ET
    this._renderStepsBar();


    // Drive Horizon with the range variant
    this._driveHorizonPicker();
    this._updatePrice();
    this._updateAddButton();
    this._renderCompatInfo();

    // Dispatch
    window.dispatchEvent(new CustomEvent('et-picker:changed', { detail: { et: etVal } }));
  }

  _onFinishSelect(finish, image) {
    this._selectedFinish = finish;
    this._selectedFinishImage = image || '';

    // Invalidate cached AI result when finish changes
    this._aiResultImage = null;
    this._aiResultUrl = null;
    try { localStorage.removeItem(`wc_ai_result_${this.dataset.productHandle}`); } catch {}
    if (this._aiBtn) this._aiBtn.classList.remove('wc__ai-btn--ready');

    this._renderStepsBar();
    this._updateAddButton();
    this._placeAventadorFlowButtons();

    window.dispatchEvent(new CustomEvent('finish-picker:changed', {
      detail: { finish, image },
    }));
  }

  /* ================================================================
   *  Horizon puppet integration
   * ================================================================ */

  _getHorizonPicker() {
    const section = this.closest('.shopify-section') || document;
    return section.querySelector('variant-picker') || document.querySelector('variant-picker');
  }

  _hideHorizonPicker() {
    const picker = this._getHorizonPicker();
    if (picker) picker.style.display = 'none';
  }

  _driveHorizonPicker() {
    const picker = this._getHorizonPicker();
    if (!picker) return;

    // Find the exact variant matching current selections
    let variant = null;

    if (this._selectedEt && this._isEtRange(this._selectedEt)) {
      // Range ET — find the variant with this range option3
      const etKey = `${this._selectedPcd}|${this._selectedSize}|${this._selectedEt}`;
      variant = this._etSet.get(etKey);
    } else if (this._selectedEt) {
      // Single ET — exact match
      const etKey = `${this._selectedPcd}|${this._selectedSize}|${this._selectedEt}`;
      variant = this._etSet.get(etKey);
    }

    if (!variant) return;

    const options = [variant.option1, variant.option2, variant.option3];

    options.forEach((value, index) => {
      if (!value) return;

      // Radio buttons
      const fieldset = picker.querySelector(`fieldset[data-fieldset-index="${index}"]`);
      if (fieldset) {
        const radio = fieldset.querySelector(`input[type="radio"][value="${CSS.escape(value)}"]`);
        if (radio && !radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      // Select dropdowns
      const selects = picker.querySelectorAll('select.variant-option__select');
      const select = selects[index];
      if (select && select.value !== value) {
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    // Update URL with variant ID (for cart fallback + deep linking)
    const variantId = this._resolveVariantId(variant);
    if (variantId) {
      const url = new URL(window.location);
      url.searchParams.set('variant', String(variantId));
      window.history.replaceState({}, '', url);
    }

  }

  _syncFromHorizon() {
    // Called after Horizon's variant:update — sync our state from Horizon's picker
    const picker = this._getHorizonPicker();
    if (!picker) return;

    const opt1 = this._getHorizonValue(picker, 0);
    const opt2 = this._getHorizonValue(picker, 1);
    const opt3 = this._getHorizonValue(picker, 2);

    let changed = false;
    if (opt1 && opt1 !== this._selectedPcd) {
      this._selectedPcd = opt1;
      changed = true;
    }
    if (opt2 && opt2 !== this._selectedSize) {
      this._selectedSize = opt2;
      changed = true;
    }
    if (opt3 && opt3 !== this._selectedEt) {
      this._selectedEt = opt3;
      if (this._isEtRange(opt3)) {
        // For range ETs synced from Horizon, keep existing exactEt or reset
        if (this._selectedExactEt === null) {
          const bounds = this._parseEtBounds(opt3);
          if (bounds) {
            this._selectedExactEt = Math.round((bounds.min + bounds.max) / 2);
          }
        }
      }
      changed = true;
    }

    if (changed) this._render();
  }

  _getHorizonValue(picker, fieldsetIndex) {
    const fieldset = picker.querySelector(`fieldset[data-fieldset-index="${fieldsetIndex}"]`);
    if (fieldset) {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (checked) return checked.value;
    }
    const selects = picker.querySelectorAll('select.variant-option__select');
    if (selects[fieldsetIndex]) return selects[fieldsetIndex].value;
    return null;
  }

  /* ================================================================
   *  Auto-selection / preselect
   * ================================================================ */

  _autoSelectFirst() {
    // Select first available PCD
    for (const [pcd] of this._pcdSet) {
      if (!this._hiddenPcds?.has(pcd)) {
        this._selectedPcd = pcd;
        break;
      }
    }
    if (!this._selectedPcd) return;

    // Select first available size for this PCD
    const pcdData = this._pcdSet.get(this._selectedPcd);
    if (pcdData) {
      for (const size of pcdData.sizes) {
        const sizeKey = `${this._selectedPcd}|${size}`;
        const sizeData = this._sizeSet.get(sizeKey);
        if (sizeData) {
          this._selectedSize = size;
          break;
        }
      }
    }

    // Auto-select first ET for PCD+Size
    if (this._selectedSize) {
      const sizeKey = `${this._selectedPcd}|${this._selectedSize}`;
      const sizeData = this._sizeSet.get(sizeKey);
      if (sizeData && sizeData.variants.length > 0) {
        const v = sizeData.variants.find(v => v.available) || sizeData.variants[0];
        const et = (v.option3 || '').trim();
        this._selectedEt = et;
        if (this._isEtRange(et)) {
          const bounds = this._parseEtBounds(et);
          if (bounds) {
            this._selectedExactEt = Math.round((bounds.min + bounds.max) / 2);
          }
        } else {
          const m = et.match(/ET(-?\d+)/);
          if (m) this._selectedExactEt = parseInt(m[1], 10);
        }
      }
    }

    this._render();

    // Drive Horizon for initial selection
    if (this._selectedEt || this._selectedSize) {
      this._driveHorizonPicker();
    }
  }

  _preselectBestVariant() {
    const specs = this._vehicle?.specs;
    if (!specs) return;

    const vehiclePcd = (specs.pcd || '').toLowerCase();
    const axle = this._getAxleSpecs();
    const vehicleEt = axle.et ?? 0;
    const vehicleDiam = axle.diameter;

    let bestVariant = this._filteredVariants[0];
    let bestScore = -Infinity;

    for (const v of this._filteredVariants) {
      let score = 0;

      // Exact PCD match
      if ((v.option1 || '').toLowerCase() === vehiclePcd) score += 10;

      // Closest diameter (strong bonus when URL diameter is explicit)
      const diamMatch = (v.option2 || '').match(/^(\d+(?:\.\d+)?)/);
      if (diamMatch) {
        const vDiam = parseFloat(diamMatch[1]);
        if (this._urlDiameter > 0 && vDiam === this._urlDiameter) {
          score += 50;
        } else {
          score -= Math.abs(vDiam - vehicleDiam);
        }
      }

      // Closest ET midpoint
      const etStr = (v.option3 || '').replace(/\s*\(.*\)/, '');
      const etMatch = etStr.match(/ET(-?\d+)(?:-(-?\d+))?/);
      if (etMatch) {
        const etMin = parseInt(etMatch[1], 10);
        const etMax = etMatch[2] ? parseInt(etMatch[2], 10) : etMin;
        const etMid = (etMin + etMax) / 2;
        score -= Math.abs(etMid - vehicleEt) * 0.5;
      }

      // In-stock bonus
      if (v.available) score += 5;

      if (score > bestScore) {
        bestScore = score;
        bestVariant = v;
      }
    }

    if (!bestVariant) return;

    // Set our state from best variant
    this._selectedPcd = (bestVariant.option1 || '').trim();
    this._selectedSize = (bestVariant.option2 || '').trim();
    const bestDiam = this._selectedSize.match(/^(\d+)/);
    if (bestDiam) this._selectedDiameter = parseInt(bestDiam[1], 10);

    const bestEt = (bestVariant.option3 || '').trim();
    this._selectedEt = bestEt;
    if (this._isEtRange(bestEt)) {
      // For range ET, clamp vehicle's ET within the range
      const bounds = this._parseEtBounds(bestEt);
      if (bounds) {
        const clamped = Math.max(bounds.min, Math.min(bounds.max, vehicleEt));
        this._selectedExactEt = clamped;
      }
    } else {
      const m = bestEt.match(/ET(-?\d+)/);
      if (m) this._selectedExactEt = parseInt(m[1], 10);
    }

    this._render();
    this._driveHorizonPicker();
  }

  /* ================================================================
   *  Compatibility + delivery info
   * ================================================================ */


  _resolveEtValue(etLabel, exactEt = null) {
    if (exactEt !== null && Number.isFinite(exactEt)) return Math.round(exactEt);
    if (!etLabel) return null;
    const m = String(etLabel).match(/ET(-?\d+)/i);
    return m ? parseInt(m[1], 10) : null;
  }


  _renderCompatInfo() {
    const el = this.querySelector('[data-role="wc-compat"]');
    if (!el) return;

    const row = (cls, svg, text) =>
      `<div class="wc__card-row wc__card-row--${cls}">${svg}<span>${text}</span></div>`;

    if (!this._vehicle?.specs) {
      el.innerHTML = row('warn', WARN_SVG, 'Velg en bil for \u00e5 bekrefte konfigurasjon');
      this._placeAventadorFlowButtons();
      return;
    }

    const specs = this._vehicle.specs;
    const variant = this._getSelectedVariant();
    if (!variant) {
      el.innerHTML = row('warn', WARN_SVG, 'Velg konfigurasjon for \u00e5 sjekke kompatibilitet');
      this._placeAventadorFlowButtons();
      return;
    }

    const result = this._checkVariantCompat(specs, variant);
    let html = '';

    if (result.compatible) {
      if (!(this._isAventadorForged && this._manualMode)) {
        html += row('ok', CHECK_SVG, 'Konfigurasjon passer med bilen din');
      }
      if (this._isAventadorForged && this._manualMode) {
        html += row('custom-detail', CHECK_SVG, '<strong style="color:#d4a843">Custom konfigurert</strong> for din bil');
        html += row('custom-detail', CHECK_SVG, 'Fully forged 6061-T6 aluminium');
        html += row('custom-detail', CHECK_SVG, 'Velg bredde og innpress selv');
        html += row('custom-detail', CHECK_SVG, 'Produseres spesifikt etter <strong style="color:#d4a843">dine valg</strong>');
        html += row('custom-detail', CHECK_SVG, 'Optimal styrke og <strong style="color:#d4a843">lav vekt</strong>');
      } else if (this._isAventadorForged) {
        html += row('detail', CHECK_SVG, 'Fully Forged 6061-T6 aluminium');
        html += row('detail', CHECK_SVG, 'OEM-passform \u2013 monteres som originale felger');
        html += row('detail', CHECK_SVG, 'Ingen senterringer eller spesialbolter n\u00f8dvendig');
        html += row('detail', CHECK_SVG, 'P\u00e5virker ikke bilens nybilgaranti');
        html += row('detail', CHECK_SVG, 'Produseres spesifikt for din bil');
      }
      const customRow = this._isAventadorForged && this._manualMode;

      // When the customer has selected a card built directly from an original
      // OEM pair, the displayed dimensions and ET values come from that exact
      // OEM setup. Never compare them against another/default OEM entry.
      const isOriginalOemSelection =
        this._quickBuyMode &&
        !this._manualMode &&
        !!this._qbActivePair;

      const isQbStaggered =
        this._quickBuyMode &&
        this._isVehicleStaggered() &&
        this._qbRearSelection?.size;

      if (isOriginalOemSelection) {
        const pair = this._qbActivePair;
        const frontEt = Number(pair?.frontEt);
        const rearEt = Number(pair?.rearEt);
        const hasFrontEt = Number.isFinite(frontEt);
        const hasRearEt = Number.isFinite(rearEt);

        if (hasFrontEt && hasRearEt) {
          const sameEt = Math.round(frontEt) === Math.round(rearEt);
          html += row(
            'detail',
            CHECK_SVG,
            sameEt
              ? `ET er hentet fra original OEM-spesifikasjon (ET${Math.round(frontEt)})`
              : `ET er hentet fra original OEM-spesifikasjon (Foran ET${Math.round(frontEt)} / Bak ET${Math.round(rearEt)})`
          );
        } else {
          html += row(
            'detail',
            CHECK_SVG,
            'Konfigurasjonen er hentet fra bilens originale OEM-oppsett'
          );
        }
      } else if (isQbStaggered) {
        const diamEntry = this._diamSpecs?.get(this._selectedDiameter);
        const rawFrontEt = this._getOemEtForSelection(
          diamEntry,
          'front',
          specs.et_front ?? specs.et ?? null,
          this._selectedSize
        );
        const rawRearEt = this._getOemEtForSelection(
          diamEntry,
          'rear',
          specs.et_rear ?? specs.et_front ?? specs.et ?? null,
          this._qbRearSelection.size
        );
        if (rawFrontEt !== null && rawRearEt !== null) {
          const oemFrontEt = Math.round(rawFrontEt);
          const oemRearEt = Math.round(rawRearEt);
          const selFrontEt = this._resolveEtValue(this._selectedEt, this._selectedExactEt);
          const selRearEt = this._resolveEtValue(this._qbRearSelection.et, this._qbRearSelection.exactEt);

          const frontExact = selFrontEt !== null && selFrontEt === oemFrontEt;
          const rearExact = selRearEt !== null && selRearEt === oemRearEt;
          const frontNear = selFrontEt !== null && Math.abs(selFrontEt - oemFrontEt) <= 5;
          const rearNear = selRearEt !== null && Math.abs(selRearEt - oemRearEt) <= 5;

          if (frontExact && rearExact) {
            html += row(
              customRow ? 'custom-detail' : 'detail',
              CHECK_SVG,
              `ET stemmer med OEM-spesifikasjon (Foran ET${oemFrontEt} / Bak ET${oemRearEt})`
            );
          } else if (frontNear && rearNear) {
            html += row(
              'near',
              CHECK_SVG,
              `ET er nær OEM-spesifikasjon (Foran OEM ET${oemFrontEt} / Bak OEM ET${oemRearEt})`
            );
          } else {
            const parts = [];
            if (!frontNear && selFrontEt !== null) parts.push(`Foran valgt ET${selFrontEt} (OEM ET${oemFrontEt})`);
            if (!rearNear && selRearEt !== null) parts.push(`Bak valgt ET${selRearEt} (OEM ET${oemRearEt})`);
            if (!parts.length) parts.push(`Foran OEM ET${oemFrontEt} / Bak OEM ET${oemRearEt}`);
            html += row('warn', WARN_SVG, `ET avviker fra OEM-spesifikasjon: ${parts.join(' | ')}`);
          }
        }
      } else if (result.oemEt !== null) {
        const selEt = this._resolveEtValue(this._selectedEt, this._selectedExactEt);
        const exact = selEt !== null && selEt === result.oemEt;
        const near = selEt !== null && Math.abs(selEt - result.oemEt) <= 5;
        if (exact) {
          html += row(customRow ? 'custom-detail' : 'detail', CHECK_SVG, `ET stemmer med OEM-spesifikasjon (ET${result.oemEt})`);
        } else if (near) {
          html += row('near', CHECK_SVG, `ET er nær OEM-spesifikasjon (OEM ET${result.oemEt})`);
        } else {
          html += row('warn', WARN_SVG, `ET avviker fra OEM-spesifikasjon (ET${result.oemEt})`);
        }
      }
      if (result.diamWarning && !isOriginalOemSelection) {
        html += row('warn', WARN_SVG, 'Valgt st\u00f8rrelse er utenfor bilens OEM-spesifikasjon!');
      }
      if (result.needsRing) {
        html += row('warn', WARN_SVG, `Krever navring (${result.wheelBore}mm \u2192 ${result.vehicleBore}mm)`);
      } else if (result.borePreciseMatch) {
        html += row(customRow ? 'custom-detail' : 'detail', CHECK_SVG, 'Presist samsvar \u2013 navring ikke n\u00f8dvendig');
      } else if (result.boreChecked) {
        html += row(customRow ? 'custom-detail' : 'detail', CHECK_SVG, 'Navring ikke n\u00f8dvendig');
      }
    } else if (this._isAventadorForged && !result.loadFailure) {
      // Fully Forged is made to order. A missing automatic OEM pair must not
      // be presented as a universal-wheel fitment problem.
      html += row('ok', CHECK_SVG, '<strong style="color:#d4a843">OEM-tilpasses bilen din</strong>');
      html += row('detail', CHECK_SVG, 'Produseres med korrekt boltsirkel, navhull og innpress');
      html += row('detail', CHECK_SVG, 'Originale bolter benyttes');
    } else if (this._isAventadorForged) {
      html += row('ok', CHECK_SVG, '<strong style="color:#d4a843">OEM-tilpasses bilen din</strong>');
      html += row('detail', CHECK_SVG, 'Produseres med korrekt boltsirkel, navhull, bredde og innpress');
      html += row('detail', CHECK_SVG, 'Originale bolter benyttes');
    } else {
      html += row('warn', WARN_SVG, 'Felgene passer denne bilen, men kan ikke leveres med OEM-oppsett.');
    }

    el.innerHTML = html;
    this._placeAventadorFlowButtons();
  }

  _openEtCalcModal() {
    // Create modal on document.body (not inside custom element) so it sits above everything
    if (!this._etCalcModal) {
      const modal = document.createElement('div');
      modal.style.cssText = 'position:fixed;inset:0;z-index:999999;background:rgba(0,0,0,0.75);display:flex;align-items:center;justify-content:center;padding:1rem';
      modal.innerHTML = '<div data-role="wc-et-calc-body" style="width:100%;max-width:960px;max-height:90vh;overflow:auto"></div>';
      document.body.appendChild(modal);
      this._etCalcModal = modal;

      const body = modal.querySelector('[data-role="wc-et-calc-body"]');
      body.innerHTML = this._getEtCalcHtml();
      this._initEtCalc(body);

      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          modal.style.display = 'none';
          document.body.style.overflow = '';
        }
      });
    }

    this._etCalcModal.style.display = 'flex';
    document.body.style.overflow = 'hidden';
  }

  _getEtCalcHtml() {
    return '<style>'
      + '.et-overlay{position:relative;width:100%;padding:20px;box-sizing:border-box}'
      + '.et-popup{position:relative;max-width:920px;margin:0 auto;padding:30px 24px 24px;border-radius:26px;border:2px solid rgba(212,175,55,0.88);color:#fff;overflow:hidden;background:radial-gradient(circle at top center,rgba(255,215,120,0.16),transparent 24%),radial-gradient(circle at bottom center,rgba(255,215,120,0.12),transparent 22%),linear-gradient(180deg,#111 0%,#090909 100%);box-shadow:0 0 0 1px rgba(255,255,255,0.04) inset,0 18px 40px rgba(0,0,0,0.35),0 0 24px rgba(212,175,55,0.12)}'
      + '.et-popup::before{content:"";position:absolute;inset:0;pointer-events:none;background:linear-gradient(90deg,transparent 0%,rgba(212,175,55,0.10) 50%,transparent 100%);opacity:.55}'
      + '.et-close{position:absolute;top:14px;right:14px;width:42px;height:42px;border-radius:50%;border:1px solid rgba(255,255,255,0.14);background:rgba(255,255,255,0.06);color:#fff;font-size:26px;line-height:1;cursor:pointer;z-index:5;transition:.2s ease}'
      + '.et-close:hover{background:rgba(212,175,55,0.14);border-color:rgba(212,175,55,0.75);color:#f3d57b}'
      + '.et-popup h2{margin:0;text-align:center;font-size:42px;line-height:1.1;font-weight:800;color:#f2d47d;position:relative;z-index:2}'
      + '.et-subtitle{text-align:center;margin:10px 0 28px;color:rgba(255,255,255,0.84);font-size:18px;position:relative;z-index:2}'
      + '.et-grid{display:grid;grid-template-columns:1fr 1fr;gap:22px 24px;position:relative;z-index:2}'
      + '.et-field label{display:block;font-size:16px;font-weight:700;margin-bottom:10px;color:#fff}'
      + '.step-input{display:grid;grid-template-columns:56px 1fr 56px auto;align-items:stretch;border:2px solid rgba(255,255,255,0.16);border-radius:16px;overflow:hidden;min-height:64px;background:linear-gradient(180deg,rgba(30,30,30,0.96) 0%,rgba(14,14,14,0.98) 100%);box-shadow:inset 0 0 0 1px rgba(255,255,255,0.02)}'
      + '.step-input.highlight{border-color:rgba(212,175,55,0.9);box-shadow:0 0 12px rgba(212,175,55,0.16),inset 0 0 0 1px rgba(255,255,255,0.02)}'
      + '.step-input button{border:0;background:linear-gradient(180deg,rgba(34,34,34,1) 0%,rgba(18,18,18,1) 100%);color:#fff;font-size:28px;font-weight:700;cursor:pointer;transition:.2s ease}'
      + '.step-input button:hover{background:linear-gradient(180deg,rgba(70,55,20,1) 0%,rgba(38,30,12,1) 100%);color:#f2d47d}'
      + '.step-input input{border:0;outline:none;background:transparent;color:#fff;text-align:center;font-size:28px;font-weight:700;min-width:0;padding:0 8px}'
      + '.step-input input::-webkit-outer-spin-button,.step-input input::-webkit-inner-spin-button{-webkit-appearance:none;margin:0}'
      + '.step-input input[type=number]{appearance:textfield;-moz-appearance:textfield}'
      + '.step-input .unit{display:flex;align-items:center;padding:0 18px;font-size:17px;color:rgba(255,255,255,0.88);border-left:1px solid rgba(255,255,255,0.08);white-space:nowrap;background:linear-gradient(180deg,rgba(24,24,24,0.96) 0%,rgba(10,10,10,0.98) 100%)}'
      + '.et-result-box{margin-top:26px;padding-top:20px;border-top:1px solid rgba(255,255,255,0.12);position:relative;z-index:2}'
      + '.et-result-line{display:flex;align-items:flex-start;gap:12px;font-size:20px;line-height:1.5;color:#f5f5f5}'
      + '.et-result-line.secondary{margin-top:12px;color:rgba(255,255,255,0.88);font-size:18px}'
      + '.et-icon{color:#f2d47d;font-size:22px;flex:0 0 auto;margin-top:2px}'
      + '.et-result-box strong{color:#f2d47d}'
      + '@media (max-width:768px){.et-overlay{padding:12px}.et-popup{padding:22px 14px 18px;border-radius:22px}.et-popup h2{font-size:30px;padding-right:34px;padding-left:34px}.et-subtitle{font-size:16px;margin-bottom:18px}.et-grid{grid-template-columns:1fr 1fr;gap:14px 10px}.et-field label{font-size:14px;margin-bottom:8px}.step-input{grid-template-columns:36px 1fr 36px auto;min-height:52px;border-radius:12px}.step-input button{font-size:20px}.step-input input{font-size:20px;padding:0 4px}.step-input .unit{font-size:12px;padding:0 10px}.et-result-line{font-size:16px}.et-result-line.secondary{font-size:15px}.et-close{width:36px;height:36px;top:10px;right:10px;font-size:22px}}'
      + '</style>'
      + '<div class="et-overlay" id="etOverlay">'
      + '  <div class="et-popup">'
      + '    <button type="button" class="et-close" data-role="et-close" aria-label="Lukk">&times;</button>'
      + '    <h2>ET-kalkulator</h2>'
      + '    <p class="et-subtitle">Beregn riktig innpress for felgene dine</p>'
      + '    <div class="et-grid">'
      + '      <div class="et-field"><label>Original bredde</label><div class="step-input"><button type="button" data-step-target="oldWidth" data-step="-0.5">−</button><input type="number" id="oldWidth" value="9" step="0.5"><button type="button" data-step-target="oldWidth" data-step="0.5">+</button><span class="unit">in</span></div></div>'
      + '      <div class="et-field"><label>Ny bredde</label><div class="step-input highlight"><button type="button" data-step-target="newWidth" data-step="-0.5">−</button><input type="number" id="newWidth" value="10" step="0.5"><button type="button" data-step-target="newWidth" data-step="0.5">+</button><span class="unit">in</span></div></div>'
      + '      <div class="et-field"><label>Original ET</label><div class="step-input"><button type="button" data-step-target="oldEt" data-step="-1">−</button><input type="number" id="oldEt" value="50" step="1"><button type="button" data-step-target="oldEt" data-step="1">+</button><span class="unit">mm</span></div></div>'
      + '      <div class="et-field"><label>Ny ET</label><div class="step-input highlight"><button type="button" data-step-target="newEt" data-step="-1">−</button><input type="number" id="newEt" value="44" step="1"><button type="button" data-step-target="newEt" data-step="1">+</button><span class="unit">mm</span></div></div>'
      + '    </div>'
      + '    <div class="et-result-box">'
      + '      <div class="et-result-line"><span class="et-icon">✔</span><span id="etOuterText">Laster resultat…</span></div>'
      + '      <div class="et-result-line secondary"><span class="et-icon">✔</span><span id="etInnerText">Laster resultat…</span></div>'
      + '    </div>'
      + '  </div>'
      + '</div>';
  }

  _initEtCalc(root) {
    const oldWidth = root.querySelector('#oldWidth');
    const newWidth = root.querySelector('#newWidth');
    const oldEt = root.querySelector('#oldEt');
    const newEt = root.querySelector('#newEt');
    const outerTextEl = root.querySelector('#etOuterText');
    const innerTextEl = root.querySelector('#etInnerText');

    const roundSmart = (num) => Math.round(num * 10) / 10;

    const close = () => {
      if (this._etCalcModal) {
        this._etCalcModal.style.display = 'none';
        document.body.style.overflow = '';
      }
    };

    // Pre-fill from vehicle OEM specs if available
    if (this._vehicle?.specs) {
      const specs = this._vehicle.specs;
      const oemWidth = specs.width_front ?? specs.width ?? specs.widthFront;
      const oemEt = specs.et_front ?? specs.et ?? specs.etFront;
      if (oemWidth != null && oldWidth) oldWidth.value = String(roundSmart(parseFloat(oemWidth)));
      if (oemEt != null && oldEt) oldEt.value = String(Math.round(parseFloat(oemEt)));
    }

    const update = () => {
      const ow = parseFloat(oldWidth?.value || '');
      const nw = parseFloat(newWidth?.value || '');
      const oe = parseFloat(oldEt?.value || '');
      const ne = parseFloat(newEt?.value || '');

      if ([ow, nw, oe, ne].some(v => Number.isNaN(v))) {
        if (outerTextEl) outerTextEl.innerHTML = 'Fyll inn gyldige verdier.';
        if (innerTextEl) innerTextEl.innerHTML = '';
        return;
      }

      const inchToMm = 25.4;
      const halfWidthDiff = ((nw - ow) * inchToMm) / 2;
      const outerChange = halfWidthDiff - (ne - oe);
      const innerChange = halfWidthDiff + (ne - oe);

      let outerText = '';
      let innerText = '';

      if (outerChange > 0) {
        outerText = `Den nye felgen kommer <strong>${Math.round(outerChange)} mm</strong> nærmere skjermkanten enn originalfelgen.`;
      } else if (outerChange < 0) {
        outerText = `Den nye felgen kommer <strong>${Math.abs(Math.round(outerChange))} mm</strong> lenger inn fra skjermkanten enn originalfelgen.`;
      } else {
        outerText = 'Den nye felgen får <strong>samme plassering</strong> mot skjermkanten som originalfelgen.';
      }

      if (innerChange > 0) {
        innerText = `På innsiden kommer felgen <strong>${Math.round(innerChange)} mm</strong> nærmere fjærbein / demper.`;
      } else if (innerChange < 0) {
        innerText = `På innsiden får du <strong>${Math.abs(Math.round(innerChange))} mm</strong> mer klaring mot fjærbein / demper.`;
      } else {
        innerText = 'På innsiden blir plasseringen <strong>uendret</strong>.';
      }

      if (outerTextEl) outerTextEl.innerHTML = outerText;
      if (innerTextEl) innerTextEl.innerHTML = innerText;
    };

    root.querySelector('[data-role="et-close"]')?.addEventListener('click', close);

    root.querySelectorAll('[data-step-target]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const targetId = btn.getAttribute('data-step-target');
        const change = parseFloat(btn.getAttribute('data-step') || '0');
        const input = root.querySelector('#' + targetId);
        if (!input || Number.isNaN(change)) return;
        let current = parseFloat(input.value);
        if (Number.isNaN(current)) current = 0;
        current += change;

        if (targetId === 'oldWidth' || targetId === 'newWidth') {
          current = Math.max(4, roundSmart(current));
          input.value = current.toFixed(1).replace('.0', '');
        } else {
          input.value = String(Math.round(current));
        }

        update();
      });
    });

    ['oldWidth', 'newWidth', 'oldEt', 'newEt'].forEach((id) => {
      const input = root.querySelector('#' + id);
      if (input) {
        input.addEventListener('input', update);
        input.addEventListener('change', update);
      }
    });

    update();
  }

  _checkVariantCompat(specs, variant) {
    // 0. Load capacity
    const loadFailure = this._getVariantLoadFailure(variant, this._activeAxle);
    if (loadFailure) {
      return { compatible: false, loadFailure };
    }

    // 1. PCD match
    const variantPcd = (variant.option1 || '').trim().toLowerCase();
    if (specs.pcd && variantPcd) {
      const vehiclePcd = specs.pcd.trim().toLowerCase();
      if (variantPcd !== vehiclePcd && variantPcd !== 'custom') {
        return { compatible: false };
      }
    }

    // 2. ET check — use per-diameter OEM ET
    const axle = this._getAxleSpecs();
    const diamEntry = this._diamSpecs?.get(this._selectedDiameter);
    const axleKey = this._activeAxle === 'rear' ? 'rear' : 'front';
    const selectedSize = this._selectedSize || variant.option2 || null;
    const vehicleEt = this._getOemEtForSelection(diamEntry, axleKey, axle.et, selectedSize);
    const etStr = (variant.option3 || '').replace(/\s*\(.*\)/, '');
    const etMatch = etStr.match(/ET(-?\d+)(?:-(-?\d+))?/);
    let etWarning = false;
    if (vehicleEt != null && etMatch) {
      let etMin = parseInt(etMatch[1], 10);
      let etMax = etMatch[2] ? parseInt(etMatch[2], 10) : etMin;
      // Use user's exact ET selection if available
      if (this._selectedExactEt !== null) {
        etMin = this._selectedExactEt;
        etMax = this._selectedExactEt;
      }
      const hardMin = vehicleEt - ET_TOLERANCE;
      const hardMax = vehicleEt + ET_TOLERANCE;
      if (etMin > hardMax || etMax < hardMin) {
        return { compatible: false };
      }
      if (Math.abs(etMin - vehicleEt) > 5 || Math.abs(etMax - vehicleEt) > 5) {
        etWarning = true;
      }
    }

    // 3. Diameter — use vehicle's full spec range (fallback: OEM + tolerance)
    const minDiam = axle.diameterMin || axle.diameter;
    const maxDiam = axle.diameterMax || (minDiam + DIAMETER_TOLERANCE);
    const sizeStr = variant.option2 || '';
    const diamMatch = sizeStr.match(/^(\d+(?:\.\d+)?)/);
    let diamWarning = false;
    if (minDiam > 0 && diamMatch) {
      const diameter = parseFloat(diamMatch[1]);
      if (diameter > 0 && (diameter < minDiam || diameter > maxDiam)) {
        diamWarning = true;
      }
    }

    // 4. Bore — use PCD-specific bore from API
    // Fully Forged Aventador Wheels are machined to the vehicle's exact hub bore.
    const isCustomBore = this.hasAttribute('data-custom-bore') || this._isAventadorForged;
    let boreFinal = parseFloat(this.dataset.centerBore) || 0;
    let boreMaxFinal = parseFloat(this.dataset.centerBoreMax) || 0;
    if (!isCustomBore && Object.keys(this._boreByPcd).length > 0 && variantPcd) {
      const pcdBore = this._boreByPcd[variantPcd];
      if (pcdBore) {
        boreFinal = pcdBore.center_bore;
        boreMaxFinal = pcdBore.center_bore_max;
      }
    }

    const vehicleBore = specs.center_bore;
    let needsRing = false;
    let boreChecked = false;
    let borePreciseMatch = false;
    if (!isCustomBore && !specs.is_centerlock && vehicleBore > 0) {
      // Use MAX bore for compatibility (conservative: if largest bore < hub, wheel won't fit)
      const compatBore = boreMaxFinal > 0 ? boreMaxFinal : boreFinal;
      // Use MIN bore for hub ring check (closest to actual variant bore)
      const displayBore = boreFinal > 0 ? boreFinal : boreMaxFinal;
      if (compatBore > 0) {
        boreChecked = true;
        if (compatBore < vehicleBore) {
          return { compatible: false, boreChecked: true };
        }
        if (displayBore - vehicleBore > 0.5) {
          needsRing = true;
        } else if (displayBore > 0 && displayBore >= vehicleBore) {
          borePreciseMatch = true;
        }
      }
    }

    return {
      compatible: true,
      needsRing,
      borePreciseMatch,
      boreChecked,
      etWarning,
      diamWarning,
      oemEt: vehicleEt !== null ? Math.round(vehicleEt) : null,
      wheelBore: needsRing ? (boreFinal > 0 ? boreFinal : boreMaxFinal) : 0,
      vehicleBore: needsRing ? vehicleBore : 0,
    };
  }

  /* ================================================================
   *  Vehicle selector modal
   * ================================================================ */

  _bindModalEvents() {
    const vehicleModal = this.querySelector('[data-role="wc-vehicle-modal"]');
    const vehicleClose = this.querySelector('[data-role="wc-vehicle-close"]');
    const vehicleSave = this.querySelector('[data-role="wc-vehicle-save"]');
    const makeSelect = this.querySelector('[data-role="wc-make"]');
    const modelSelect = this.querySelector('[data-role="wc-model"]');
    const yearSelect = this.querySelector('[data-role="wc-year"]');
    const trimSelect = this.querySelector('[data-role="wc-trim"]');

    vehicleClose?.addEventListener('click', () => this._closeVehicleModal());
    vehicleModal?.addEventListener('click', (e) => {
      if (e.target === vehicleModal) this._closeVehicleModal();
    });
    makeSelect?.addEventListener('change', () => this._onModalMakeChange());
    modelSelect?.addEventListener('change', () => this._onModalModelChange());
    yearSelect?.addEventListener('change', () => this._onModalYearChange());
    trimSelect?.addEventListener('change', () => {
      if (vehicleSave) vehicleSave.disabled = !trimSelect.value;
    });
    vehicleSave?.addEventListener('click', () => this._onModalSave());

    // Regnr input in modal
    const regnrInput = this.querySelector('[data-role="wc-regnr"]');
    const regnrBtn = this.querySelector('[data-role="wc-regnr-search"]');
    const regnrStatus = this.querySelector('[data-role="wc-regnr-status"]');
    if (regnrInput) {
      regnrInput.addEventListener('input', () => {
        const val = regnrInput.value.replace(/[\s-]/g, '');
        if (regnrBtn) regnrBtn.disabled = val.length < 4;
        if (regnrStatus) regnrStatus.textContent = '';
      });
      regnrInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !regnrBtn?.disabled) this._onModalRegnrSearch();
      });
    }
    regnrBtn?.addEventListener('click', () => this._onModalRegnrSearch());

    // Specs modal
    const specsModal = this.querySelector('[data-role="wc-specs-modal"]');
    const specsClose = this.querySelector('[data-role="wc-specs-close"]');
    specsClose?.addEventListener('click', () => this._closeSpecsModal());
    specsModal?.addEventListener('click', (e) => {
      if (e.target === specsModal) this._closeSpecsModal();
    });
  }

  _openVehicleModal() {
    const modal = this.querySelector('[data-role="wc-vehicle-modal"]');
    if (!modal) return;
    modal.hidden = false;

    const makeSelect = this.querySelector('[data-role="wc-make"]');
    if (makeSelect && makeSelect.options.length <= 1) {
      this._fetchApi('/api/vehicles/makes').then(makes => {
        this._modalMakes = makes;
        this._populateSelect(makeSelect, makes, 'Merke', m => ({
          value: m.slug, label: m.name,
        }));
        makeSelect.disabled = false;
      }).catch(e => console.error('WC: failed to load makes', e));
    }
  }

  _closeVehicleModal() {
    const modal = this.querySelector('[data-role="wc-vehicle-modal"]');
    if (modal) modal.hidden = true;
  }

  async _onModalMakeChange() {
    const modelSelect = this.querySelector('[data-role="wc-model"]');
    const yearSelect = this.querySelector('[data-role="wc-year"]');
    const trimSelect = this.querySelector('[data-role="wc-trim"]');
    const saveBtn = this.querySelector('[data-role="wc-vehicle-save"]');

    this._resetSelect(modelSelect, 'Modell');
    this._resetSelect(yearSelect, '\u00c5r');
    this._resetSelect(trimSelect, 'Type');
    if (saveBtn) saveBtn.disabled = true;

    const slug = this.querySelector('[data-role="wc-make"]')?.value;
    if (!slug) return;

    try {
      this._modalModels = await this._fetchApi(`/api/vehicles/makes/${slug}`);
      this._populateSelect(modelSelect, this._modalModels, 'Modell', m => {
        return { value: m.slug, label: m.ModelName || m.modelName || m.name || '' };
      });
      modelSelect.disabled = false;
    } catch (e) { console.error('WC: failed to load models', e); }
  }

  async _onModalModelChange() {
    const yearSelect = this.querySelector('[data-role="wc-year"]');
    const trimSelect = this.querySelector('[data-role="wc-trim"]');
    const saveBtn = this.querySelector('[data-role="wc-vehicle-save"]');

    this._resetSelect(yearSelect, '\u00c5r');
    this._resetSelect(trimSelect, 'Type');
    if (saveBtn) saveBtn.disabled = true;

    const makeSlug = this.querySelector('[data-role="wc-make"]')?.value;
    const modelSlug = this.querySelector('[data-role="wc-model"]')?.value;
    if (!makeSlug || !modelSlug) return;

    try {
      this._modalYears = await this._fetchApi(`/api/vehicles/makes/${makeSlug}/models/${modelSlug}`);
      this._populateSelect(yearSelect, this._modalYears, '\u00c5r', y => ({ value: y, label: y }));
      yearSelect.disabled = false;
    } catch (e) { console.error('WC: failed to load years', e); }
  }

  async _onModalYearChange() {
    const trimSelect = this.querySelector('[data-role="wc-trim"]');
    const saveBtn = this.querySelector('[data-role="wc-vehicle-save"]');

    this._resetSelect(trimSelect, 'Type');
    if (saveBtn) saveBtn.disabled = true;

    const makeSlug = this.querySelector('[data-role="wc-make"]')?.value;
    const modelSlug = this.querySelector('[data-role="wc-model"]')?.value;
    const year = this.querySelector('[data-role="wc-year"]')?.value;
    if (!makeSlug || !modelSlug || !year) return;

    try {
      this._modalTrims = await this._fetchApi(`/api/vehicles/makes/${makeSlug}/models/${modelSlug}/years/${year}`);
      this._populateSelect(trimSelect, this._modalTrims, 'Type', t => {
        const name = t.CarName || t.carName || t.name || '';
        return { value: String(t.CarID || t.carId || t.id || ''), label: name };
      });
      trimSelect.disabled = false;
    } catch (e) { console.error('WC: failed to load trims', e); }
  }

  async _onModalSave() {
    const makeSlug = this.querySelector('[data-role="wc-make"]')?.value;
    const modelSlug = this.querySelector('[data-role="wc-model"]')?.value;
    const year = this.querySelector('[data-role="wc-year"]')?.value;
    const trimId = this.querySelector('[data-role="wc-trim"]')?.value;
    if (!makeSlug || !modelSlug || !trimId) return;

    const make = (this._modalMakes || []).find(m => m.slug === makeSlug);
    const model = (this._modalModels || []).find(m => m.slug === modelSlug);
    const trim = (this._modalTrims || []).find(
      t => String(t.CarID || t.carId || t.id || '') === trimId
    );

    const vehicle = {
      makeSlug,
      makeName: make?.name || makeSlug,
      modelSlug,
      modelName: model?.ModelName || model?.modelName || model?.name || modelSlug,
      year: year || '',
      trimId,
      trimName: trim?.CarName || trim?.carName || trim?.name || trimId,
    };

    this._closeVehicleModal();

    try {
      const data = await this._fetchApi(`/api/vehicles/makes/${makeSlug}/models/${modelSlug}/${trimId}`);
      // Keep the complete Wheel-Size trim response as well as parsed fitment specs.
      // generated_car can then pass the richest available identity/body data to Gemini.
      vehicle.specsRaw = data;
      vehicle.specs = this._parseVehicleSpecs(data);
    } catch { /* save without specs */ }

    saveVehicle(vehicle);
  }

  async _onModalRegnrSearch() {
    const regnrInput = this.querySelector('[data-role="wc-regnr"]');
    const regnrBtn = this.querySelector('[data-role="wc-regnr-search"]');
    const regnrStatus = this.querySelector('[data-role="wc-regnr-status"]');
    const plate = (regnrInput?.value || '').replace(/[\s-]/g, '').toUpperCase();
    if (plate.length < 4) return;

    if (regnrBtn) regnrBtn.disabled = true;
    if (regnrStatus) { regnrStatus.textContent = 'Søker...'; regnrStatus.style.color = '#aaa'; }

    try {
      const res = await fetch(`${API_BASE}/api/vehicles/regnr/${encodeURIComponent(plate)}`);
      const json = await res.json();
      if (!res.ok || !json.success) {
        if (regnrStatus) { regnrStatus.textContent = (json.error?.message || json.error) || 'Ikke funnet'; regnrStatus.style.color = '#e57373'; }
        if (regnrBtn) regnrBtn.disabled = false;
        return;
      }

      const meta = json.meta;
      const vehicle = {
        makeSlug: meta.make_slug,
        makeName: meta.make,
        modelSlug: meta.model_slug,
        modelName: meta.model,
        year: meta.year ? String(meta.year) : '',
        trimId: meta.trim_id ? String(meta.trim_id) : `regnr:${plate}`,
        trimName: meta.trim_name || meta.model,
        regnr: plate,
        regnrSource: 'vegvesen',
      };

      // Parse specs from response data
      if (json.data) {
        vehicle.specsRaw = json.data;
        vehicle.specs = this._parseVehicleSpecs(json.data);
      }

      // If CSV match with trim_id, fetch full trim specs
      if (meta.csv_match && meta.trim_id) {
        try {
          const trimData = await this._fetchApi(
            `/api/vehicles/makes/${meta.make_slug}/models/${meta.model_slug}/${meta.trim_id}`
          );
          vehicle.specsRaw = trimData;
          vehicle.specs = this._parseVehicleSpecs(trimData);
        } catch { /* keep vegvesen specs */ }
      }

      this._closeVehicleModal();
      saveVehicle(vehicle);
    } catch (e) {
      console.error('WC: regnr lookup failed', e);
      if (regnrStatus) { regnrStatus.textContent = 'Noe gikk galt'; regnrStatus.style.color = '#e57373'; }
      if (regnrBtn) regnrBtn.disabled = false;
    }
  }

  _normalizeVehiclePcd(value) {
    const raw = String(value ?? '').trim();
    const compact = raw.toLowerCase().replace(/\s+/g, '');
    if (!raw || ['n/a', 'na', 'none', 'null', '-', '0', '0x0'].includes(compact)) return '';
    return raw;
  }

  _isCenterlockSpec(config = {}, rawPcd = '') {
    const explicitValues = [
      config.CenterLock, config.CentreLock, config.centerlock, config.center_lock,
      config.FixingType, config.MountingType, config.BoltType, config.FasteningType,
    ];
    if (explicitValues.some(value => value === true || value === 1 || String(value).toLowerCase() === 'true')) {
      return true;
    }
    const text = explicitValues.map(value => String(value ?? '')).join(' ').toLowerCase();
    if (/(?:center|centre)[\s_-]*lock|central[\s_-]*lock|single[\s_-]*nut|monolock/.test(text)) {
      return true;
    }

    if (this._normalizeVehiclePcd(rawPcd)) return false;
    const make = String(this._vehicle?.makeName || this._vehicle?.makeSlug || '').toLowerCase();
    const model = String(this._vehicle?.modelName || this._vehicle?.modelSlug || '').toLowerCase();
    return make.includes('porsche') && /(?:^|\b)(?:911|991|992|997)(?:\b|$)/.test(model);
  }

  _formatVehiclePcd(value, config = {}) {
    const normalized = this._normalizeVehiclePcd(value);
    if (normalized) return normalized;
    return this._isCenterlockSpec(config, value) ? 'Centerlock' : '–';
  }

  _vehicleRimInfo(value) {
    const parts = String(value || '').trim().toLowerCase().replace('j', '').split('x').map(part => part.trim());
    if (parts.length !== 2) return null;
    const width = parseFloat(parts[0]);
    const diameter = Math.round(parseFloat(parts[1]));
    if (!Number.isFinite(width) || !Number.isFinite(diameter)) return null;
    if (width < 4 || width > 16 || diameter < 12 || diameter > 26) return null;
    return { width, diameter };
  }

  _vehicleTyreDiameter(value) {
    const text = String(value || '').toUpperCase();
    const match = text.match(/R\s*(\d{2})(?:\D|$)/);
    return match ? parseInt(match[1], 10) : null;
  }

  _vehicleFrontOffset(config = {}) {
    const values = [
      config.RimOffset,
      config.Offset,
      config.ET,
      config.et,
      config.offset,
    ];
    for (const value of values) {
      if (value === null || value === undefined || String(value).trim() === '') continue;
      const parsed = parseFloat(value);
      if (Number.isFinite(parsed)) return parsed;
    }
    return null;
  }

  _vehicleRearOffset(config = {}) {
    // Different backend/CSV revisions have used both OffsetR and OffSetR.
    // Normalize every known spelling here so rear ET never silently falls back
    // to an unrelated front/default value.
    const values = [
      config.OffsetR,
      config.OffSetR,
      config.RimOffsetR,
      config.RearOffset,
      config.ETRear,
      config.et_rear,
      config.offset_rear,
    ];
    for (const value of values) {
      if (value === null || value === undefined || String(value).trim() === '') continue;
      const parsed = parseFloat(value);
      if (Number.isFinite(parsed)) return parsed;
    }
    return null;
  }

  _copyVehicleConfig(config = {}) {
    return { ...config };
  }

  _repairVehicleConfigPairing(configs = []) {
    // Read from an immutable snapshot while writing to a separate clone.
    // Otherwise the first repaired row could become the source for the next
    // row and rotate the data a second time.
    const sourceSnapshot = configs.map(config => this._copyVehicleConfig(config));
    const cloned = configs.map(config => this._copyVehicleConfig(config));
    const valid = sourceSnapshot.map((config, index) => ({
      index,
      config,
      front: this._vehicleRimInfo(config.RimSize),
      rear: this._vehicleRimInfo(config.RimSizeR),
    })).filter(item => item.front && item.rear);

    if (valid.length < 2) return cloned;

    const countDiameters = (items, axle) => {
      const counts = new Map();
      for (const item of items) {
        const diameter = item[axle].diameter;
        counts.set(diameter, (counts.get(diameter) || 0) + 1);
      }
      return counts;
    };

    const frontCounts = countDiameters(valid, 'front');
    const rearCounts = countDiameters(valid, 'rear');
    const sameDiameterMultiset =
      frontCounts.size === rearCounts.size &&
      [...frontCounts.entries()].every(([diameter, count]) => rearCounts.get(diameter) === count);

    const mismatched = valid.filter(item => item.front.diameter !== item.rear.diameter).length;

    /*
     * Guard against a known flattened-CSV/API failure mode where the rear rows
     * are rotated one position relative to the front rows. Example:
     *   19/21, 20/19, 21/20
     * Although every individual row looks staggered, the front and rear
     * diameter multisets are identical. A real staggered family such as
     * 19/20 + 20/21 does NOT have identical multisets and is therefore kept.
     *
     * Only repair when at least two rows disagree and the majority of complete
     * rows are mismatched. This deliberately avoids forcing equal diameters on
     * genuine 20/21, 21/22, etc. OEM setups.
     */
    const looksLikeRearPermutation =
      sameDiameterMultiset &&
      mismatched >= 2 &&
      mismatched >= Math.ceil(valid.length * 0.6);

    if (!looksLikeRearPermutation) return cloned;

    const rearByDiameter = new Map();
    for (const item of valid) {
      const diameter = item.rear.diameter;
      if (!rearByDiameter.has(diameter)) rearByDiameter.set(diameter, []);
      rearByDiameter.get(diameter).push(item);
    }

    const usedRearIndices = new Set();
    for (const item of valid) {
      const targetDiameter = item.front.diameter;
      const pool = (rearByDiameter.get(targetDiameter) || [])
        .filter(candidate => !usedRearIndices.has(candidate.index));
      if (!pool.length) continue;

      // Prefer the rear row whose width is the nearest sensible match for the
      // current front row. This matters when a diameter has several OEM widths.
      pool.sort((a, b) => {
        const score = candidate => {
          const rearWidth = candidate.rear.width;
          const frontWidth = item.front.width;
          const narrowerPenalty = rearWidth + 0.001 < frontWidth ? 100 : 0;
          return narrowerPenalty + Math.abs(rearWidth - frontWidth);
        };
        return score(a) - score(b);
      });

      const source = pool[0];
      usedRearIndices.add(source.index);

      const target = cloned[item.index];
      const sourceConfig = source.config;
      target.RimSizeR = sourceConfig.RimSizeR;

      const rearOffset = this._vehicleRearOffset(sourceConfig);
      if (rearOffset !== null) target.OffsetR = rearOffset;
      else delete target.OffsetR;

      const rearTyre = sourceConfig.TyreSizeR || sourceConfig.TyreSizeRear || sourceConfig.RearTires;
      if (rearTyre) target.TyreSizeR = rearTyre;

      if (sourceConfig.CentreBoreR !== undefined) target.CentreBoreR = sourceConfig.CentreBoreR;
      if (sourceConfig.CenterBoreR !== undefined) target.CenterBoreR = sourceConfig.CenterBoreR;
      if (sourceConfig.MinETRear !== undefined) target.MinETRear = sourceConfig.MinETRear;
      if (sourceConfig.MaxETRear !== undefined) target.MaxETRear = sourceConfig.MaxETRear;
    }

    console.warn('[fitment] repaired rotated rear OEM configuration rows', {
      before: valid.map(item => `${item.front.diameter}/${item.rear.diameter}`),
      after: cloned.map(config => {
        const front = this._vehicleRimInfo(config.RimSize);
        const rear = this._vehicleRimInfo(config.RimSizeR);
        return front && rear ? `${front.diameter}/${rear.diameter}` : null;
      }).filter(Boolean),
    });

    return cloned;
  }

  _getVehicleConfigSource(data, { modelOnly = false } = {}) {
    const modelReturn = data?.DRDModelReturn;
    const modelConfigs = [
      modelReturn?.PrimaryOption,
      ...(Array.isArray(modelReturn?.Options) ? modelReturn.Options : []),
    ].filter(Boolean);

    // Trim-specific model rows are authoritative when present. Chassis is a
    // fallback and can describe another/base configuration of the same model.
    const source = modelConfigs.length
      ? modelConfigs
      : (modelOnly ? [] : [data?.DRDChassisReturn].filter(Boolean));

    return this._repairVehicleConfigPairing(source);
  }

  _vehicleConfigKey(config = {}) {
    const clean = value => String(value ?? '').trim().toLowerCase().replace(/\s+/g, '');
    return [
      config.RimSize,
      this._vehicleFrontOffset(config),
      config.TyreSize || config.TyreSizeFront || config.FrontTires,
      config.RimSizeR,
      this._vehicleRearOffset(config),
      config.TyreSizeR || config.TyreSizeRear || config.RearTires,
      config.PCD,
      config.CentreBore,
    ].map(clean).join('|');
  }

  _getUniqueVehicleConfigs(data) {
    const source = this._getVehicleConfigSource(data);
    const seen = new Set();
    return source.filter(config => {
      const key = this._vehicleConfigKey(config);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  _specRowKey(row = {}) {
    const clean = value => String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '')
      .replace(/\.0(?=\D|$)/g, '');
    return [row.size, row.pcd, row.bore, row.et, row.tyre, row.pos].map(clean).join('|');
  }

  _specRowBaseKey(row = {}) {
    const clean = value => String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '')
      .replace(/\.0(?=\D|$)/g, '');
    return [row.size, row.pcd, row.bore, row.et, row.pos].map(clean).join('|');
  }

  _dedupeSpecRows(rows = []) {
    const exactSeen = new Set();
    const exact = rows.filter(row => {
      const key = this._specRowKey(row);
      if (!key || exactSeen.has(key)) return false;
      exactSeen.add(key);
      return true;
    });
    const completeBases = new Set(
      exact.filter(row => String(row.tyre || '').trim())
        .map(row => this._specRowBaseKey(row))
    );
    return exact.filter(row =>
      String(row.tyre || '').trim() || !completeBases.has(this._specRowBaseKey(row))
    );
  }

  _parseVehicleSpecs(data) {
    const chassis = data?.DRDChassisReturn;
    if (chassis) {
      const configs = this._getUniqueVehicleConfigs(data);
      const primary = configs[0] || chassis;
      const front = this._vehicleRimInfo(primary.RimSize) || this._vehicleRimInfo(chassis.RimSize);
      if (!front) {
        return { pcd: '', center_bore: 0, diameter_front: 0, diameter_rear: 0, is_staggered: false };
      }
      const rear = this._vehicleRimInfo(primary.RimSizeR) || front;

      let minDiam = Math.min(front.diameter, rear.diameter);
      let maxDiam = Math.max(front.diameter, rear.diameter);
      let maxWidthFront = parseFloat(chassis.MaxRimWidthFront || 0);
      let maxWidthRear = parseFloat(chassis.MaxRimWidthRear || 0);

      const hasSplit = config => {
        const f = this._vehicleRimInfo(config?.RimSize);
        const r = this._vehicleRimInfo(config?.RimSizeR) || f;
        if (!f || !r) return false;
        const fEt = this._vehicleFrontOffset(config);
        const rEt = this._vehicleRearOffset(config) ?? fEt;
        const fTyre = String(config.TyreSize || config.TyreSizeFront || config.FrontTires || '').trim();
        const rTyre = String(config.TyreSizeR || config.TyreSizeRear || config.RearTires || '').trim();
        return (
          f.diameter !== r.diameter ||
          Math.abs(f.width - r.width) > 0.001 ||
          (Number.isFinite(fEt) && Number.isFinite(rEt) && Math.abs(fEt - rEt) > 0.001) ||
          (!!rTyre && rTyre !== fTyre)
        );
      };

      for (const config of configs) {
        const f = this._vehicleRimInfo(config.RimSize);
        if (!f) continue;
        const r = this._vehicleRimInfo(config.RimSizeR) || f;
        minDiam = Math.min(minDiam, f.diameter, r.diameter);
        maxDiam = Math.max(maxDiam, f.diameter, r.diameter);
        maxWidthFront = Math.max(maxWidthFront || 0, f.width);
        maxWidthRear = Math.max(maxWidthRear || 0, r.width);
      }

      const frontEt = this._vehicleFrontOffset(primary)
        ?? this._vehicleFrontOffset(chassis)
        ?? 0;
      const rearEt = this._vehicleRearOffset(primary)
        ?? frontEt;

      return {
        pcd: this._normalizeVehiclePcd(primary.PCD || chassis.PCD),
        is_centerlock: this._isCenterlockSpec(primary, primary.PCD || chassis.PCD)
          || this._isCenterlockSpec(chassis, chassis.PCD),
        center_bore: parseFloat(primary.CentreBore || chassis.CentreBore || 0),
        et_front: Number.isFinite(frontEt) ? frontEt : 0,
        et_rear: Number.isFinite(rearEt) ? rearEt : (Number.isFinite(frontEt) ? frontEt : 0),
        et_min_front: parseFloat(primary.MinETFront ?? chassis.MinETFront ?? 0),
        et_max_front: parseFloat(primary.MaxETFront ?? chassis.MaxETFront ?? 0),
        et_min_rear: parseFloat(primary.MinETRear ?? chassis.MinETRear ?? primary.MinETFront ?? chassis.MinETFront ?? 0),
        et_max_rear: parseFloat(primary.MaxETRear ?? chassis.MaxETRear ?? primary.MaxETFront ?? chassis.MaxETFront ?? 0),
        diameter_front: front.diameter,
        diameter_rear: rear.diameter,
        diameter_min: minDiam,
        diameter_max: maxDiam,
        width_front: front.width,
        width_rear: rear.width,
        width_max_front: Math.max(maxWidthFront || 0, front.width),
        width_max_rear: Math.max(maxWidthRear || 0, rear.width),
        is_staggered: configs.some(hasSplit),
      };
    }

    let front, rear;
    if (Array.isArray(data)) {
      front = data[0] || {};
      rear = data.length > 1 ? data[1] : front;
    } else {
      front = data.wheels?.front || data.front || data;
      rear = data.wheels?.rear || data.rear || front;
      if (Array.isArray(front)) {
        rear = front.length > 1 ? front[1] : front[0];
        front = front[0] || {};
      }
    }
    return {
      pcd: this._normalizeVehiclePcd(front.bolt_pattern || front.pcd),
      is_centerlock: this._isCenterlockSpec(front, front.bolt_pattern || front.pcd),
      center_bore: parseFloat(front.center_bore || front.hub_bore || front.bore || 0),
      et_front: parseFloat(front.offset || front.et || 0),
      et_rear: parseFloat(rear.offset || rear.et || front.offset || front.et || 0),
      diameter_front: parseFloat(front.rim_diameter || front.Diameter || front.diameter || 0),
      diameter_rear: parseFloat(rear.rim_diameter || rear.Diameter || rear.diameter || front.rim_diameter || front.Diameter || front.diameter || 0),
      width_front: parseFloat(front.rim_width || front.Width || front.width || 0),
      width_rear: parseFloat(rear.rim_width || rear.Width || rear.width || front.rim_width || front.Width || front.width || 0),
      is_staggered: false,
    };
  }


  /* ================================================================
   *  Specs modal  /* ================================================================
   *  Specs modal
   * ================================================================ */

  _openSpecsModal() {
    const modal = this.querySelector('[data-role="wc-specs-modal"]');
    if (!modal) return;
    modal.hidden = false;
    if (!this._specsFetched) {
      // If _fetchDiamSpecs already got the data, reuse it for the table
      if (this._diamSpecsData) {
        this._buildSpecsTable(this._diamSpecsData);
        this._specsFetched = true;
      } else {
        this._fetchAndRenderSpecs();
      }
    }
  }

  _closeSpecsModal() {
    const modal = this.querySelector('[data-role="wc-specs-modal"]');
    if (modal) modal.hidden = true;
  }

  async _fetchAndRenderSpecs() {
    const vehicle = getVehicle();
    if (!vehicle?.makeSlug || !vehicle?.modelSlug || !vehicle?.trimId) return;

    const loading = this.querySelector('[data-role="wc-specs-loading"]');
    const table = this.querySelector('[data-role="wc-specs-table"]');
    if (loading) loading.hidden = false;
    if (table) table.hidden = true;

    try {
      let data;
      if (String(vehicle.trimId).startsWith('regnr:')) {
        // Regnr vehicle — fetch specs from regnr API
        const plate = vehicle.trimId.replace('regnr:', '');
        const res = await fetch(`${API_BASE}/api/vehicles/regnr/${encodeURIComponent(plate)}`);
        const json = await res.json();
        if (!res.ok || !json.success) throw new Error((json.error?.message || json.error) || 'Regnr lookup failed');
        data = json.data;
      } else {
        data = await this._fetchApi(
          `/api/vehicles/makes/${vehicle.makeSlug}/models/${vehicle.modelSlug}/${vehicle.trimId}`
        );
      }
      this._buildSpecsTable(data);
      this._buildDiamSpecs(data);
      this._specsFetched = true;
      // Re-render now that per-diameter data is available
      this._render();
    } catch (e) {
      console.error('WC: failed to fetch specs', e);
      if (loading) loading.textContent = 'Kunne ikke laste spesifikasjoner.';
    }
  }

  _buildSpecsTable(data) {
    const table = this.querySelector('[data-role="wc-specs-table"]');
    const loading = this.querySelector('[data-role="wc-specs-loading"]');
    if (!table) return;
    const tbody = table.querySelector('tbody');
    if (!tbody) return;

    const chassis = data.DRDChassisReturn;
    if (!chassis) {
      if (loading) loading.textContent = 'Ingen spesifikasjoner tilgjengelig.';
      return;
    }

    const fallbackPcd = chassis.PCD || '';
    const fallbackBore = chassis.CentreBore || '';
    const rows = [];

    for (const config of this._getUniqueVehicleConfigs(data)) {
      this._addSpecRows(rows, config, fallbackPcd, fallbackBore, true);
    }

    const unique = this._dedupeSpecRows(rows);

    unique.sort((a, b) => {
      if (a.diam !== b.diam) return a.diam - b.diam;
      if (a.width !== b.width) return a.width - b.width;
      const posOrder = { Foran: 0, Bak: 1, Begge: 2 };
      return (posOrder[a.pos] ?? 3) - (posOrder[b.pos] ?? 3);
    });

    tbody.innerHTML = unique.map(r => `<tr>
      <td>${this._esc(r.size)}</td>
      <td>${this._esc(r.pcd)}</td>
      <td>${this._esc(r.bore)}</td>
      <td>${this._esc(r.et)}</td>
      <td>${this._esc(r.tyre)}</td>
      <td>${this._esc(r.pos)}</td>
    </tr>`).join('');

    if (loading) loading.hidden = true;
    table.hidden = false;
  }

  /**
   * Eagerly fetch vehicle API data and build _diamSpecs for width/ET filtering.
   * Called on init and on vehicle change — no need to wait for specs modal.
   */
  async _fetchDiamSpecs() {
    const vehicle = this._vehicle || getVehicle();
    if (!vehicle?.makeSlug || !vehicle?.modelSlug || !vehicle?.trimId) return;
    if (this._diamSpecs.size > 0) return; // already built
    try {
      let data;
      if (String(vehicle.trimId).startsWith('regnr:')) {
        const plate = vehicle.trimId.replace('regnr:', '');
        const res = await fetch(`${API_BASE}/api/vehicles/regnr/${encodeURIComponent(plate)}`);
        const json = await res.json();
        if (!res.ok || !json.success) throw new Error((json.error?.message || json.error) || 'Regnr lookup failed');
        data = json.data;
      } else {
        data = await this._fetchApi(
          `/api/vehicles/makes/${vehicle.makeSlug}/models/${vehicle.modelSlug}/${vehicle.trimId}`
        );
      }
      this._buildDiamSpecs(data);
      this._diamSpecsData = data; // cache for specs table

      // If the customer arrived from the search page with a diameter that is
      // larger than every OEM diameter, open Custom Fit automatically and
      // keep that requested diameter selected (green) in the manual menu.
      const requestedDiameter = Math.round(Number(this._urlDiameter) || 0);
      const oemDiameters = [...this._diamSpecs.keys()]
        .map(value => Math.round(Number(value) || 0))
        .filter(value => value > 0);
      const largestOemDiameter = oemDiameters.length ? Math.max(...oemDiameters) : 0;

      if (
        requestedDiameter > 0 &&
        largestOemDiameter > 0 &&
        requestedDiameter > largestOemDiameter
      ) {
        this._selectedDiameter = requestedDiameter;
        this._selectedSize = null;
        this._selectedEt = null;
        this._selectedExactEt = null;
        this._enterManualMode({ hidePcd: true });
        return;
      }

      this._render(); // re-render with per-diameter filtering
    } catch { /* silent — fallback to basic specs */ }
  }

  /**
   * Build per-diameter specs lookup from API response.
   * Map: diameter → { front: {width, widths: Set, et}, rear: {width, widths: Set, et} }
   * Tracks discrete allowed widths per diameter+axle from OEM + alt configs.
   */
  _buildDiamSpecs(data) {
    const map = new Map();
    const chassis = data?.DRDChassisReturn;
    if (!chassis) { this._diamSpecs = map; return; }

    const updateAxle = (entry, axle, w, et) => {
      if (!entry[axle]) {
        entry[axle] = {
          width: w,
          widths: new Set([w]),
          widthMin: w,
          widthMax: w,
          et: Number.isFinite(et) ? et : null,
          ets: new Set(Number.isFinite(et) ? [et] : []),
          etByWidth: new Map(),
        };
        if (Number.isFinite(et)) {
          entry[axle].etByWidth.set(w.toFixed(3), new Set([et]));
        }
      } else {
        entry[axle].widths.add(w);
        if (Number.isFinite(et)) {
          entry[axle].ets.add(et);
          if (!Number.isFinite(entry[axle].et)) entry[axle].et = et;
          const key = w.toFixed(3);
          if (!entry[axle].etByWidth.has(key)) entry[axle].etByWidth.set(key, new Set());
          entry[axle].etByWidth.get(key).add(et);
        }
        if (w < entry[axle].widthMin) entry[axle].widthMin = w;
        if (w > entry[axle].widthMax) entry[axle].widthMax = w;
      }
    };

    const addConfig = config => {
      const front = this._vehicleRimInfo(config.RimSize);
      if (!front) return;
      const rear = this._vehicleRimInfo(config.RimSizeR) || front;
      const frontEt = this._vehicleFrontOffset(config);
      const rearEt = this._vehicleRearOffset(config) ?? frontEt;

      const fDiam = Math.round(front.diameter);
      const rDiam = Math.round(rear.diameter);
      if (!(fDiam > 0)) return;

      if (!map.has(fDiam)) map.set(fDiam, {});
      const entry = map.get(fDiam);

      updateAxle(entry, 'front', front.width, frontEt);
      updateAxle(entry, 'rear', rear.width, rearEt);

      if (!Array.isArray(entry.pairs)) entry.pairs = [];
      const pair = {
        frontDiameter: fDiam,
        rearDiameter: rDiam || fDiam,
        frontWidth: front.width,
        rearWidth: rear.width,
        frontEt,
        rearEt,
        source: 'vehicle-oem',
      };

      const alreadyStored = entry.pairs.some(existing =>
        existing.frontDiameter === pair.frontDiameter &&
        existing.rearDiameter === pair.rearDiameter &&
        Math.abs(existing.frontWidth - pair.frontWidth) < 0.001 &&
        Math.abs(existing.rearWidth - pair.rearWidth) < 0.001 &&
        existing.frontEt === pair.frontEt &&
        existing.rearEt === pair.rearEt
      );
      if (!alreadyStored) entry.pairs.push(pair);
    };

    // One authoritative, repaired config stream for both the search page and
    // product page. This keeps real mixed-diameter 20/21 setups intact while
    // correcting rotated rows such as 19/21, 20/19, 21/20.
    for (const config of this._getUniqueVehicleConfigs(data)) addConfig(config);

    this._diamSpecs = map;

    if (this._vehicle?.specs && !this._vehicle.specs.is_staggered && this._isVehicleStaggered()) {
      this._vehicle.specs.is_staggered = true;
      saveVehicle(this._vehicle);
    }
  }


  _addSpecRows(rows, config, fallbackPcd, fallbackBore, isOEM) {
    const frontRim = (config.RimSize || '').trim();
    const rearRim = (config.RimSizeR || '').trim();
    if (!frontRim) return;

    const rawPcd = config.PCD || fallbackPcd;
    const pcd = this._formatVehiclePcd(rawPcd, config);
    const bore = config.CentreBore ?? fallbackBore ?? '–';
    const frontETValue = this._vehicleFrontOffset(config);
    const rearETValue = this._vehicleRearOffset(config);
    const frontET = frontETValue ?? '';
    const rearET = rearETValue ?? '';
    const frontTyre = config.TyreSize || config.TyreSizeFront || config.FrontTires || '';
    const rearTyre = config.TyreSizeR || config.TyreSizeRear || config.RearTires || '';

    const formatSize = (rimStr, oem) => {
      const parts = rimStr.split('x').map(s => s.trim());
      if (parts.length === 2) {
        const label = `${parts[1]}x${parts[0]}`;
        return oem ? `${label} (OEM)` : label;
      }
      return oem ? `${rimStr} (OEM)` : rimStr;
    };
    const parseDiam = (rimStr) => { const p = rimStr.split('x').map(s => s.trim()); return p.length === 2 ? parseFloat(p[1]) || 0 : 0; };
    const parseWidth = (rimStr) => { const p = rimStr.split('x').map(s => s.trim()); return p.length >= 1 ? parseFloat(p[0]) || 0 : 0; };

    const fSize = formatSize(frontRim, isOEM);
    const rSize = formatSize(rearRim || frontRim, isOEM);
    const fDiam = parseDiam(frontRim);
    const rDiam = parseDiam(rearRim || frontRim);
    const fWidth = parseWidth(frontRim);
    const rWidth = parseWidth(rearRim || frontRim);

    const hasRear = !!(rearRim && rearRim !== frontRim) || !!(rearET && rearET !== frontET) || !!(rearTyre && rearTyre !== frontTyre);

    if (!hasRear) {
      rows.push({ size: fSize, pcd, bore: String(bore), et: String(frontET), tyre: frontTyre, pos: 'Begge', oem: isOEM, diam: fDiam, width: fWidth });
    } else {
      rows.push({ size: fSize, pcd, bore: String(bore), et: String(frontET), tyre: frontTyre, pos: 'Foran', oem: isOEM, diam: fDiam, width: fWidth });
      rows.push({ size: rSize, pcd, bore: String(bore), et: String(rearET), tyre: rearTyre, pos: 'Bak', oem: isOEM, diam: rDiam, width: rWidth });
    }
  }

  /* ================================================================
   *  Finish drawer
   * ================================================================ */

  _bindDrawerEvents() {
    const backdrop = document.querySelector('[data-role="wc-finish-backdrop"]');
    const closeBtn = document.querySelector('[data-role="wc-finish-drawer-close"]');

    backdrop?.addEventListener('click', () => this._closeFinishDrawer());
    closeBtn?.addEventListener('click', () => this._closeFinishDrawer());

    // Bind finish tiles (drawer lives on body)
    document.querySelectorAll('.wc__finish-tile[data-finish]').forEach(tile => {
      tile.addEventListener('click', () => {
        const finish = tile.dataset.finish;
        const image = tile.dataset.finishImage;
        this._selectFinishTile(finish, image);
      });
    });
  }

  _openFinishDrawer() {
    const backdrop = document.querySelector('[data-role="wc-finish-backdrop"]');
    const drawer = document.querySelector('[data-role="wc-finish-drawer"]');
    backdrop?.classList.add('is-open');
    drawer?.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  _closeFinishDrawer() {
    const backdrop = document.querySelector('[data-role="wc-finish-backdrop"]');
    const drawer = document.querySelector('[data-role="wc-finish-drawer"]');
    backdrop?.classList.remove('is-open');
    drawer?.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  _selectFinishTile(finish, image) {
    if (!finish) return;

    // Highlight tile (drawer lives on body)
    document.querySelectorAll('.wc__finish-tile').forEach(t => t.classList.remove('selected'));
    const selected = document.querySelector(`.wc__finish-tile[data-finish="${CSS.escape(finish)}"]`);
    if (selected) selected.classList.add('selected');

    this._closeFinishDrawer();
    this._onFinishSelect(finish, image);

    // Update the button preview
    if (this._quickBuyMode) {
      this._renderQbFinish();
    } else {
      this._renderFinishStep();
    }
  }

  /* ================================================================
   *  Add to cart + price + quantity
   * ================================================================ */

  _initCenterCapAddon() {
    if (!this._cartSection || !this._priceEl || this._centerCapAddonEl) return;

    if (!document.getElementById('wc-center-cap-addon-styles')) {
      const style = document.createElement('style');
      style.id = 'wc-center-cap-addon-styles';
      style.textContent = `
        .wc__center-cap-addon[hidden] { display: none !important; }
        .wc__center-cap-addon { margin: 10px 0 12px; }
        .wc__center-cap-addon-label {
          margin: 0 0 5px;
          color: #c9a14b;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: .04em;
          text-transform: uppercase;
        }
        .wc__center-cap-addon-card {
          width: 100%;
          display: grid;
          grid-template-columns: 22px minmax(0, 1fr) auto;
          align-items: center;
          gap: 9px;
          box-sizing: border-box;
          padding: 9px 10px;
          border: 1px solid rgba(201, 161, 75, .82);
          border-radius: 7px;
          background: rgba(255, 255, 255, .035);
          color: #fff;
          transition: border-color .18s ease, background .18s ease, box-shadow .18s ease;
        }
        .wc__center-cap-addon-card.is-in-cart {
          border-color: #18c96e;
          background: rgba(32, 201, 120, .08);
          box-shadow: inset 0 0 0 1px rgba(32, 201, 120, .08);
        }
        .wc__center-cap-addon-icon {
          width: 20px;
          height: 20px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: 1.5px solid #c9a14b;
          border-radius: 50%;
          color: #c9a14b;
          font-size: 11px;
          font-weight: 900;
          line-height: 1;
          flex: 0 0 20px;
        }
        .wc__center-cap-addon-card.is-in-cart .wc__center-cap-addon-icon {
          border-color: #18c96e;
          background: #18c96e;
          color: #fff;
        }
        .wc__center-cap-addon-copy {
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .wc__center-cap-addon-title {
          color: #fff;
          font-size: 12px;
          font-weight: 800;
          line-height: 1.2;
        }
        .wc__center-cap-addon-sub {
          color: rgba(255, 255, 255, .62);
          font-size: 9.5px;
          font-weight: 500;
          line-height: 1.25;
        }
        .wc__center-cap-addon-buy {
          min-width: 76px;
          min-height: 38px;
          display: inline-flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 1px;
          padding: 6px 9px;
          border: 1px solid #c9a14b;
          border-radius: 6px;
          background: #c9a14b;
          color: #111;
          font: inherit;
          cursor: pointer;
          white-space: nowrap;
          transition: background .18s ease, border-color .18s ease, opacity .18s ease;
        }
        .wc__center-cap-addon-buy:hover:not(:disabled) {
          background: #e0b85e;
          border-color: #e0b85e;
        }
        .wc__center-cap-addon-buy:disabled { cursor: default; }
        .wc__center-cap-addon-card.is-in-cart .wc__center-cap-addon-buy {
          min-width: 104px;
          border-color: #18c96e;
          background: #18c96e;
          color: #fff;
        }
        .wc__center-cap-addon-buy-label {
          font-size: 10px;
          font-weight: 900;
          line-height: 1.05;
        }
        .wc__center-cap-addon-price {
          font-size: 9px;
          font-weight: 800;
          line-height: 1.05;
        }
        .wc__center-cap-addon-status {
          min-height: 0;
          margin-top: 4px;
          color: #50dc9a;
          font-size: 9.5px;
          font-weight: 700;
          line-height: 1.2;
        }
        .wc__center-cap-addon-status:empty { display: none; }
        @media (max-width: 480px) {
          .wc__center-cap-addon-card {
            grid-template-columns: 20px minmax(0, 1fr) auto;
            gap: 7px;
            padding: 8px 9px;
          }
          .wc__center-cap-addon-title { font-size: 11px; }
          .wc__center-cap-addon-sub { font-size: 8.8px; }
          .wc__center-cap-addon-buy { min-width: 70px; padding: 6px 7px; }
          .wc__center-cap-addon-card.is-in-cart .wc__center-cap-addon-buy { min-width: 96px; }
        }
      `;
      document.head.appendChild(style);
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'wc__center-cap-addon';
    wrapper.hidden = true;
    wrapper.innerHTML = `
      <div class="wc__center-cap-addon-label">Tilvalg</div>
      <div class="wc__center-cap-addon-card">
        <span class="wc__center-cap-addon-icon" aria-hidden="true">○</span>
        <span class="wc__center-cap-addon-copy">
          <span class="wc__center-cap-addon-title">Tilpass for originale senterkopper</span>
          <span class="wc__center-cap-addon-sub">Gjelder alle 4 felger. Senterkopper følger ikke med.</span>
        </span>
        <button type="button" class="wc__center-cap-addon-buy">
          <span class="wc__center-cap-addon-buy-label">Kjøp</span>
          <span class="wc__center-cap-addon-price">1 290 kr</span>
        </button>
      </div>
      <div class="wc__center-cap-addon-status" aria-live="polite"></div>`;

    this._priceEl.parentNode.insertBefore(wrapper, this._priceEl);
    this._centerCapAddonEl = wrapper;
    this._centerCapAddonCard = wrapper.querySelector('.wc__center-cap-addon-card');
    this._centerCapAddonBuyBtn = wrapper.querySelector('.wc__center-cap-addon-buy');
    this._centerCapAddonBuyLabelEl = wrapper.querySelector('.wc__center-cap-addon-buy-label');
    this._centerCapAddonPriceEl = wrapper.querySelector('.wc__center-cap-addon-price');
    this._centerCapAddonStatusEl = wrapper.querySelector('.wc__center-cap-addon-status');

    this._centerCapAddonBuyBtn?.addEventListener('click', () => this._buyCenterCapAddon());

    // Load ID/price and check whether the service already exists in the cart.
    this._loadCenterCapAddonProduct().then(() => this._syncCenterCapAddonCartStatus());
    this._renderCenterCapAddon();
  }

  _shouldShowCenterCapAddon() {
    // Show the same one-time add-on in both Aventador Wheels flows:
    // 1) OEM / Quick Buy before the customer opens Custom Fit
    // 2) Manual Custom Fit
    // Never show it on other wheel brands.
    return !!(
      this._isAventadorForged &&
      (this._manualMode || (this._quickBuyMode && this._vehicle?.specs))
    );
  }

  _renderCenterCapAddon() {
    if (!this._centerCapAddonEl) return;

    const visible = this._shouldShowCenterCapAddon();
    this._centerCapAddonEl.hidden = !visible;
    if (!visible) return;

    this._centerCapAddonCard?.classList.toggle('is-in-cart', this._centerCapAddonInCart);
    if (this._centerCapAddonBuyBtn) {
      this._centerCapAddonBuyBtn.disabled = this._centerCapAddonBusy || this._centerCapAddonInCart;
    }
    if (this._centerCapAddonBuyLabelEl) {
      this._centerCapAddonBuyLabelEl.textContent = this._centerCapAddonBusy
        ? 'Legger til…'
        : (this._centerCapAddonInCart ? 'I handlekurv ✓' : 'Kjøp');
    }
    if (this._centerCapAddonPriceEl) {
      this._centerCapAddonPriceEl.textContent = this._centerCapAddonInCart
        ? ''
        : this._formatCenterCapAddonMoney(this._centerCapAddonPrice);
    }
    const icon = this._centerCapAddonEl.querySelector('.wc__center-cap-addon-icon');
    if (icon) icon.textContent = this._centerCapAddonInCart ? '✓' : '○';
  }

  _formatCenterCapAddonMoney(cents) {
    const value = Math.max(0, Number(cents) || 0) / 100;
    return `${value.toLocaleString('nb-NO', { maximumFractionDigits: 0 })} kr`;
  }

  async _loadCenterCapAddonProduct() {
    if (this._centerCapAddonVariantId) return this._centerCapAddonVariantId;
    if (this._centerCapAddonLoadPromise) return this._centerCapAddonLoadPromise;

    const handle = this.dataset.centerCapAddonHandle || CENTER_CAP_ADDON_HANDLE;
    this._centerCapAddonLoadPromise = fetch(`/products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' },
    })
      .then(async response => {
        if (!response.ok) throw new Error(`Tilvalgsproduktet kunne ikke lastes (${response.status})`);
        const product = await response.json();
        const variants = Array.isArray(product?.variants) ? product.variants : [];
        const variant = variants.find(item => item.available !== false) || variants[0];
        if (!variant?.id) throw new Error('Tilvalgsproduktet mangler en gyldig variant');

        this._centerCapAddonVariantId = Number(variant.id);
        if (Number.isFinite(Number(variant.price)) && Number(variant.price) > 0) {
          this._centerCapAddonPrice = Number(variant.price);
        }
        this._renderCenterCapAddon();
        return this._centerCapAddonVariantId;
      })
      .catch(error => {
        console.error('Failed to load center-cap add-on product', error);
        this._centerCapAddonLoadPromise = null;
        if (this._centerCapAddonStatusEl) {
          this._centerCapAddonStatusEl.textContent = 'Tilvalget kunne ikke lastes. Prøv igjen.';
        }
        return null;
      });

    return this._centerCapAddonLoadPromise;
  }

  async _getCenterCapAddonCartMatches(variantId) {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Kunne ikke kontrollere handlekurven');
    const cart = await response.json();
    const items = Array.isArray(cart?.items) ? cart.items : [];
    return items.filter(item => Number(item.variant_id ?? item.id) === Number(variantId));
  }

  async _syncCenterCapAddonCartStatus() {
    if (this._centerCapAddonCartChecked || this._centerCapAddonBusy) return;
    const variantId = await this._loadCenterCapAddonProduct();
    if (!variantId) return;

    try {
      const matches = await this._getCenterCapAddonCartMatches(variantId);
      if (matches.length) {
        await this._normalizeCenterCapAddonInCart(matches);
      }
      this._centerCapAddonInCart = matches.length > 0;
      this._centerCapAddonCartChecked = true;
      this._renderCenterCapAddon();
    } catch (error) {
      console.warn('Could not check center-cap add-on cart status', error);
    }
  }

  async _normalizeCenterCapAddonInCart(matches) {
    if (!Array.isArray(matches) || !matches.length) return;

    // Keep exactly one unit on the first matching line and remove every duplicate.
    for (let index = 0; index < matches.length; index += 1) {
      const item = matches[index];
      const desiredQuantity = index === 0 ? 1 : 0;
      if (Number(item.quantity) === desiredQuantity) continue;

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: item.key, quantity: desiredQuantity }),
      });
      if (!response.ok) throw new Error('Kunne ikke korrigere tilvalget i handlekurven');
    }
  }

  async _buyCenterCapAddon() {
    if (!this._shouldShowCenterCapAddon() || this._centerCapAddonBusy) return;

    this._centerCapAddonBusy = true;
    if (this._centerCapAddonStatusEl) this._centerCapAddonStatusEl.textContent = '';
    this._renderCenterCapAddon();

    try {
      const variantId = await this._loadCenterCapAddonProduct();
      if (!variantId) throw new Error('Kunne ikke finne tilvalgsproduktet');

      const existingMatches = await this._getCenterCapAddonCartMatches(variantId);
      if (existingMatches.length) {
        await this._normalizeCenterCapAddonInCart(existingMatches);
      } else {
        const properties = {
          'Tilvalg': 'Tilpasning for originale senterkopper',
          'Gjelder': '4 felger',
          'Valgt fra': this._manualMode ? 'Custom Fit' : 'OEM Fit',
          'Originale senterkopper': 'Følger ikke med',
        };
        const vehicleName = this._vehicle ? this._getVehicleDisplayName(this._vehicle) : '';
        if (vehicleName) properties['Bil'] = vehicleName;
        const regNumber = localStorage.getItem('sbf_regnummer') || '';
        if (regNumber) properties['Regnummer'] = regNumber;

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1, properties }],
          }),
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.description || errorData.message || 'Kunne ikke legge til tilvalget');
        }
      }

      this._centerCapAddonInCart = true;
      this._centerCapAddonCartChecked = true;
      if (this._centerCapAddonStatusEl) {
        this._centerCapAddonStatusEl.textContent = 'Lagt i handlekurven ✓';
      }
      await this._refreshCart();
    } catch (error) {
      console.error('Failed to add center-cap service', error);
      if (this._centerCapAddonStatusEl) {
        this._centerCapAddonStatusEl.textContent = error.message || 'Noe gikk galt. Prøv igjen.';
      }
    } finally {
      this._centerCapAddonBusy = false;
      this._renderCenterCapAddon();
    }
  }

  _hideBuyButtons() {
    const section = this.closest('.shopify-section') || document;
    const buyBtns = section.querySelector('buy-buttons, .buy-buttons, [data-buy-buttons]');
    if (buyBtns) buyBtns.style.display = 'none';
  }

  _bindCartEvents() {
    this.querySelector('[data-role="wc-qty-minus"]')?.addEventListener('click', () => {
      const v = Math.max(1, (parseInt(this._qtyInput.value) || 1) - 1);
      this._qtyInput.value = v;
      this._updatePrice();
    });
    this.querySelector('[data-role="wc-qty-plus"]')?.addEventListener('click', () => {
      const v = Math.min(99, (parseInt(this._qtyInput.value) || 1) + 1);
      this._qtyInput.value = v;
      this._updatePrice();
    });
    this._qtyInput?.addEventListener('change', () => {
      let v = parseInt(this._qtyInput.value) || 1;
      v = Math.max(1, Math.min(99, v));
      this._qtyInput.value = v;
      this._updatePrice();
    });
    this._addBtn?.addEventListener('click', () => this._addToCart());
  }

  _getSelectedVariant() {
    return this._findVariant(this._selectedPcd, this._selectedSize, this._selectedEt);
  }

  /** Find a variant by pcd, size, and ET label. */
  _findVariant(pcd, size, et) {
    if (!pcd || !size || !et) return null;

    if (this._isEtRange(et)) {
      const sizeKey = `${pcd}|${size}`;
      const sizeData = this._sizeSet.get(sizeKey);
      if (!sizeData) return null;
      return sizeData.variants.find(v => (v.option3 || '').trim() === et) || null;
    }

    const etKey = `${pcd}|${size}|${et}`;
    return this._etSet.get(etKey) || null;
  }

  _updatePrice() {
    if (!this._priceEl) return;

    const variant = this._getSelectedVariant()
      || (this._quickBuyMode && !this._vehicle?.specs
        ? this._allVariants.find(v => v.available) || this._allVariants[0]
        : null);

    // Hybrid-Forged fallback pricing is only for a genuinely missing Shopify
    // price. Never overwrite a contextual Shopify/B2B catalog price.
    if (this._isHybridForged && variant && !(Number(variant.price) > 0)) {
      const agreedPrice = this._getHybridForgedPriceBySize(
        variant.option2 || variant.title
      );
      if (agreedPrice) {
        variant.price = agreedPrice.price;
        if (!(Number(variant.compare_at_price) > 0)) {
          variant.compare_at_price = agreedPrice.compareAtPrice;
        }
      }
    }

    if (!variant || !(Number(variant.price) > 0)) {
      this._priceEl.innerHTML = `
        <div class="wc__price-current">
          <span class="wc__price-current-label">Pris for komplett sett:</span>
          <strong class="wc__price-current-value">Laster pris…</strong>
        </div>`;
      return;
    }

    const formatMoney = (cents) =>
      `${Math.round(cents / 100).toLocaleString('nb-NO')} kr`;

    const savingIcon = `
      <span class="wc__premium-save-icon" aria-hidden="true">
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M20.59 13.41 11 23l-9-9V2h12l6.59 6.59a2 2 0 0 1 0 2.82Z"/>
          <circle cx="7" cy="7" r="1.5"/>
        </svg>
      </span>`;

    let total = 0;
    let compareTotal = 0;
    let itemCount = 1;

    if (this._quickBuyMode && this._vehicle?.specs) {
      itemCount = 4;
      const isStaggered = this._isVehicleStaggered() && this._qbRearSelection.size;

      if (isStaggered) {
        const rearVariant = this._findVariant(
          this._qbRearSelection.pcd,
          this._qbRearSelection.size,
          this._qbRearSelection.et
        );

        if (!rearVariant) {
          this._priceEl.textContent = 'Pris utilgjengelig';
          return;
        }

        total = variant.price * 2 + rearVariant.price * 2;

        const frontCompare = variant.compare_at_price > variant.price
          ? variant.compare_at_price
          : 0;
        const rearCompare = rearVariant.compare_at_price > rearVariant.price
          ? rearVariant.compare_at_price
          : 0;

        if (frontCompare || rearCompare) {
          compareTotal = (frontCompare || variant.price) * 2
            + (rearCompare || rearVariant.price) * 2;
        }
      } else {
        total = variant.price * 4;
        if (variant.compare_at_price > variant.price) {
          compareTotal = variant.compare_at_price * 4;
        }
      }
    } else {
      itemCount = Math.max(1, parseInt(this._qtyInput?.value, 10) || 1);
      total = variant.price * itemCount;
      if (variant.compare_at_price > variant.price) {
        compareTotal = variant.compare_at_price * itemCount;
      }
    }

    const label = this._quickBuyMode && !this._manualMode ? 'Pris for 4 felger:' : `Pris for ${itemCount} ${itemCount === 1 ? 'felg' : 'felger'}:`;
    const currentPriceHtml = `
      <div class="wc__price-current">
        <span class="wc__price-current-label">${label}</span>
        <strong class="wc__price-current-value">${formatMoney(total)}</strong>
      </div>`;

    if (compareTotal > total) {
      const savings = compareTotal - total;
      this._priceEl.innerHTML = `
        <div class="wc__premium-saving-card">
          <div class="wc__premium-before">
            <span>Førpris:</span>
            <s>${formatMoney(compareTotal)}</s>
          </div>
          <span class="wc__premium-divider" aria-hidden="true"></span>
          ${savingIcon}
          <div class="wc__premium-save-copy">
            <span>Du sparer:</span>
            <strong>${formatMoney(savings)}</strong>
          </div>
        </div>
        ${currentPriceHtml}`;
    } else {
      this._priceEl.innerHTML = currentPriceHtml;
    }

    // Keep the legacy savings row hidden so existing integrations/selectors remain intact.
    const legacySavingsRow = this.querySelector('.wc__price-savings-row');
    if (legacySavingsRow) legacySavingsRow.style.display = 'none';
  }

  _updateAddButton() {
    if (!this._addBtn) return;
    const variant = this._getSelectedVariant();

    // Hybrid-Forged fallback pricing is only for a genuinely missing Shopify
    // price. Never overwrite a contextual Shopify/B2B catalog price.
    if (this._isHybridForged && variant && !(Number(variant.price) > 0)) {
      const agreedPrice = this._getHybridForgedPriceBySize(
        variant.option2 || variant.title
      );
      if (agreedPrice) {
        variant.price = agreedPrice.price;
        if (!(Number(variant.compare_at_price) > 0)) {
          variant.compare_at_price = agreedPrice.compareAtPrice;
        }
      }
    }

    const hasLiveVariant =
      !!variant &&
      Number(variant.id) > 0 &&
      Number(variant.price) > 0;
    // For range ETs, require exact ET selection; for single ETs, just need selection
    const hasEt = this._isSelectedEtRange
      ? this._selectedExactEt !== null
      : !!this._selectedEt;
    const hasFinish = !this._finishMode || !!this._selectedFinish;

    // Check if selected variant is out of stock with deny policy (not backorderable)
    const inv = variant ? this._inventory[this._variantKey(variant)] : null;
    const isOutOfStock = inv && !inv.available && inv.inventoryPolicy !== 'continue';

    const isQbStaggered =
      this._quickBuyMode &&
      this._isVehicleStaggered() &&
      this._qbRearSelection.size;

    const rearVariantForLoad = isQbStaggered
      ? this._findVariant(
          this._qbRearSelection.pcd,
          this._qbRearSelection.size,
          this._qbRearSelection.et
        )
      : null;

    const frontAxleForLoad = isQbStaggered
      ? 'front'
      : (this._isVehicleStaggered() && !this._manualMode ? this._activeAxle : 'both');

    const frontLoadFailure = this._getVariantLoadFailure(variant, frontAxleForLoad);
    const rearLoadFailure = rearVariantForLoad
      ? this._getVariantLoadFailure(rearVariantForLoad, 'rear')
      : null;
    const loadFailure = frontLoadFailure || rearLoadFailure;

    const btnTop = this._addBtn.querySelector('.wc__add-btn-top');

    if (loadFailure) {
      this._addBtn.disabled = true;
      if (btnTop) btnTop.textContent = 'Ikke godkjent for bilen';
      this._showLoadError(loadFailure);
    } else if (isOutOfStock) {
      this._addBtn.disabled = true;
      if (btnTop) btnTop.textContent = 'Utsolgt';
    } else if (!hasLiveVariant) {
      this._addBtn.disabled = true;
      if (btnTop) btnTop.textContent = 'Laster pris og lager…';
    } else {
      // In Quick Buy staggered mode, also require rear variant
      if (isQbStaggered) {
        const rearVariant = this._findVariant(this._qbRearSelection.pcd, this._qbRearSelection.size, this._qbRearSelection.et);
        this._addBtn.disabled = !variant || !hasEt || !hasFinish || !rearVariant;
      } else {
        this._addBtn.disabled = !variant || !hasEt || !hasFinish;
      }

      // Update button label for Quick Buy (only when vehicle is set)
      if (btnTop && this._quickBuyMode && this._vehicle?.specs) {
        const cartIcon = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:-3px;margin-right:4px"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>';
        const label = window.innerWidth > 480 ? 'Legg komplett sett i handlekurv' : 'Legg i handlekurv';
        btnTop.innerHTML = `${cartIcon} ${label}`;
      } else if (btnTop) {
        btnTop.textContent = 'Legg i handlekurv';
      }
    }

    // Show tire CTA on all products
    const tireCta = this.querySelector('[data-role="wc-tire-cta"]');
    if (tireCta) tireCta.hidden = false;
  }

  async _addToCart() {
    const variant = this._getSelectedVariant();
    if (!variant) return;

    // In custom fit/manual mode we do not run staggered per-axle flow.
    const isStaggered = this._isVehicleStaggered() && !this._manualMode;
    const isQbStaggered = this._quickBuyMode && isStaggered && this._qbRearSelection.size;

    const frontAxleForLoad = isQbStaggered
      ? 'front'
      : (isStaggered ? this._activeAxle : 'both');

    const frontLoadFailure = this._getVariantLoadFailure(
      variant,
      frontAxleForLoad
    );

    let rearLoadFailure = null;
    if (isQbStaggered) {
      const rearVariantForLoad = this._findVariant(
        this._qbRearSelection.pcd,
        this._qbRearSelection.size,
        this._qbRearSelection.et
      );
      rearLoadFailure = this._getVariantLoadFailure(
        rearVariantForLoad,
        'rear'
      );
    }

    const loadFailure = frontLoadFailure || rearLoadFailure;
    if (loadFailure) {
      this._showLoadError(loadFailure);
      this._updateAddButton();
      return;
    }

    this._addBtn.disabled = true;
    const btnTop = this._addBtn.querySelector('.wc__add-btn-top');
    if (btnTop) btnTop.textContent = 'Legger til...';
    if (this._cartError) this._cartError.hidden = true;

    try {
      let items;
      const vehicleName = this._vehicle ? this._getVehicleDisplayName(this._vehicle) : '';

      if (isQbStaggered) {
        // --- Quick Buy staggered: add front + rear in one request ---
        const rearSel = this._qbRearSelection;
        const rearVariant = this._findVariant(rearSel.pcd, rearSel.size, rearSel.et);
        if (!rearVariant) throw new Error('Kunne ikke finne bakhjul-variant');

        const frontId = this._resolveVariantId(variant);
        const rearId = this._resolveVariantId(rearVariant);
        if (!frontId || !rearId) throw new Error('Kunne ikke finne variant-ID');

        const frontProps = {};
        if (vehicleName) frontProps['Bil'] = vehicleName;
        if (this._selectedExactEt !== null) frontProps['ET Offset'] = `ET${this._selectedExactEt}`;
        if (this._selectedFinish) frontProps['Custom Finish'] = this._selectedFinish;
        frontProps['Aksling'] = 'Foran';

        const rearProps = {};
        if (vehicleName) rearProps['Bil'] = vehicleName;
        if (rearSel.exactEt !== null) rearProps['ET Offset'] = `ET${rearSel.exactEt}`;
        if (this._selectedFinish) rearProps['Custom Finish'] = this._selectedFinish;
        rearProps['Aksling'] = 'Bak';

        items = [
          { id: frontId, quantity: 2, properties: frontProps },
          { id: rearId, quantity: 2, properties: rearProps },
        ];
      } else {
        // --- Standard (non-staggered or manual staggered) ---
        const variantId = this._resolveVariantId(variant);
        if (!variantId) throw new Error('Kunne ikke finne variant-ID');

        const qty = this._quickBuyMode ? 4 : (parseInt(this._qtyInput.value) || 1);
const properties = {};

const savedRegNumber = localStorage.getItem('sbf_regnummer') || '';
if (savedRegNumber) {
  properties['Regnummer'] = savedRegNumber;
}

if (vehicleName) properties['Bil'] = vehicleName;

        if (this._selectedExactEt !== null) {
          properties['ET Offset'] = `ET${this._selectedExactEt}`;
        }
        if (this._selectedFinish) {
          properties['Custom Finish'] = this._selectedFinish;
        }
        if (isStaggered) {
          properties['Aksling'] = this._activeAxle === 'front' ? 'Foran' : 'Bak';
        }

        items = [{ id: variantId, quantity: qty, properties }];
      }

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || 'Kunne ikke legge til i handlekurv');
      }

      // Update cart count in header + trigger any theme cart events (background)
      this._refreshCart();

      if (isQbStaggered) {
        // Quick Buy staggered — both axles added at once
        this._cartedAxles.front = { qty: 2, size: this._selectedSize };
        this._cartedAxles.rear = { qty: 2, size: this._qbRearSelection.size };
        if (btnTop) btnTop.textContent = 'Lagt til 2x foran + 2x bak \u2713';
        setTimeout(() => {
          if (btnTop) btnTop.textContent = 'Legg i handlekurv';
          this._updateAddButton();
        }, 2500);
        this._renderVehicleBanner();
      } else if (isStaggered) {
        // Manual staggered — existing per-axle flow
        const qty = parseInt(this._qtyInput.value) || 1;
        const sizeLabel = this._selectedSize || variant.title;
        this._cartedAxles[this._activeAxle] = { qty, size: sizeLabel };
        this._renderAxleToggle();

        if (this._activeAxle === 'front' && !this._cartedAxles.rear) {
          if (btnTop) btnTop.textContent = `Lagt til ${qty}x foran \u2713`;
          setTimeout(() => {
            if (btnTop) btnTop.textContent = 'Konfigurer bakhjul \u2192';
            this._addBtn.disabled = false;
            this._addBtn.onclick = (e) => {
              e.preventDefault();
              this._addBtn.onclick = null;
              this._onAxleSwitch('rear');
              this._updateAddButton();
            };
          }, 1500);
        } else {
          const label = this._activeAxle === 'rear'
            ? `Lagt til ${qty}x bak — Komplett sett! \u2713`
            : `Lagt til ${qty}x \u2713`;
          if (btnTop) btnTop.textContent = label;
          setTimeout(() => {
            if (btnTop) btnTop.textContent = 'Legg i handlekurv';
            this._updateAddButton();
          }, 2500);
        }
        this._renderVehicleBanner();
      } else {
        // Non-staggered — standard flow
        if (btnTop) btnTop.textContent = 'Lagt til \u2713';
        setTimeout(() => {
          if (btnTop) btnTop.textContent = 'Legg i handlekurv';
          this._updateAddButton();
        }, 2000);
      }
    } catch (e) {
      if (this._cartError) {
        this._cartError.textContent = e.message;
        this._cartError.hidden = false;
      }
      if (btnTop) btnTop.textContent = 'Legg i handlekurv';
      this._updateAddButton();
    }
  }

  /**
   * Refresh cart state after adding an item.
   * Fetches /cart.js to get the updated item count, then:
   * 1. Updates any cart-count/bubble elements in the header
   * 2. Dispatches common cart events that themes may listen for
   * 3. Uses Section Rendering API to re-render cart sections if possible
   */
  async _refreshCart() {
    try {
      const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await cartRes.json();

      // Update all common cart count selectors used by Shopify themes
      const countSelectors = [
        '[data-cart-count]',
        '.cart-count',
        '.cart-count-bubble span',
        '.cart-icon-bubble span',
        '#cart-icon-bubble span',
        '#CartCount',
        '.js-cart-count',
        '.header__cart-count',
        'cart-count',
        '[data-cart-item-count]',
      ];
      const count = cart.item_count || 0;
      for (const sel of countSelectors) {
        document.querySelectorAll(sel).forEach(el => {
          el.textContent = count;
          // Show/hide bubble based on count
          const bubble = el.closest('[data-cart-count], .cart-count-bubble, .cart-icon-bubble, #cart-icon-bubble');
          if (bubble) bubble.hidden = count === 0;
        });
      }

      // Dispatch common cart events
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart } }));
      window.dispatchEvent(new CustomEvent('cart:change', { detail: { cart } }));

      // Section Rendering API — fire-and-forget so it doesn't block
      const sectionIds = this._getCartSectionIds();
      if (sectionIds.length > 0) {
        fetch(`/?sections=${sectionIds.join(',')}`)
          .then(r => r.json())
          .then(sections => {
            for (const [id, html] of Object.entries(sections)) {
              const existing = document.getElementById(`shopify-section-${id}`);
              if (existing) {
                const temp = document.createElement('div');
                temp.innerHTML = html;
                const newSection = temp.querySelector(`#shopify-section-${id}`);
                if (newSection) existing.innerHTML = newSection.innerHTML;
              }
            }
          })
          .catch(() => { });
      }
    } catch (e) {
      console.warn('WC: cart refresh failed', e);
    }
  }

  /** Find cart-related section IDs from the DOM for Section Rendering API */
  _getCartSectionIds() {
    const ids = [];
    document.querySelectorAll('[id^="shopify-section-"]').forEach(el => {
      const id = el.id.replace('shopify-section-', '');
      if (id.includes('cart') || id.includes('header')) {
        ids.push(id);
      }
    });
    return ids;
  }

  /* ================================================================
   *  Utility helpers
   * ================================================================ */

  async _fetchApi(path) {
    const res = await fetch(`${API_BASE}${path}`);
    if (!res.ok) throw new Error(`API ${res.status}`);
    const json = await res.json();
    return json.data ?? json;
  }

  _populateSelect(select, items, placeholder, mapFn) {
    const opts = [`<option value="">${placeholder}</option>`];
    for (const item of items) {
      const { value, label } = mapFn(item);
      opts.push(`<option value="${this._escAttr(value)}">${this._esc(label)}</option>`);
    }
    select.innerHTML = opts.join('');
  }

  _resetSelect(select, placeholder) {
    if (!select) return;
    select.innerHTML = `<option value="">${placeholder}</option>`;
    select.disabled = true;
  }

  _esc(str) {
    const d = document.createElement('div');
    d.textContent = String(str ?? '');
    return d.innerHTML;
  }

  _escAttr(str) {
    return String(str ?? '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  _getAventadorGalleryModel() {
    const handle = String(this.dataset.productHandle || '').toLowerCase();
    const title = String(this.dataset.productTitle || '').toLowerCase();
    const source = `${handle} ${title}`;

    // Most specific names must be checked before shorter names.
    const models = [
      { match: ['hybrid-forged-stealth', 'hybrid forged stealth', 'hybrid-stealth'], model: 'Hybrid Stealth' },
      { match: ['revolution-y', 'revolution y'], model: 'Revolution Y' },
      { match: ['bullet-air', 'bullet air'], model: 'Bullet Air' },
      { match: ['aero-sport', 'aero sport', 'aerosport'], model: 'Aero Sport' },
      { match: ['bladecore'], model: 'Bladecore' },
      { match: ['starlight'], model: 'Starlight' },
      { match: ['limitless'], model: 'Limitless' },
      { match: ['rs-evo', 'rs evo'], model: 'RS Evo' },
      { match: ['stealth'], model: 'Stealth' },
      { match: ['spider'], model: 'Spider' },
      { match: ['titan'], model: 'Titan' },
      { match: ['prime'], model: 'Prime' },
      { match: ['ghost'], model: 'Ghost' },
      { match: ['bullet'], model: 'Bullet' },
      { match: ['nova'], model: 'Nova' },
      { match: ['gts'], model: 'GTS' },
    ];

    return models.find(entry => entry.match.some(value => source.includes(value)))?.model || '';
  }

  _configureAventadorGalleryButton(galleryBtn) {
    if (!galleryBtn) return;

    const model = this._getAventadorGalleryModel();
    const titleEl = galleryBtn.querySelector('.wc__ai-btn-title');
    const descEl = galleryBtn.querySelector('.wc__ai-btn-desc');

    if (titleEl) titleEl.textContent = 'Se felgene på premiumbiler';
    if (descEl) descEl.textContent = 'Utforsk AI-galleriet';

    const params = new URLSearchParams();
    params.set('tab', 'woc');
    if (model) params.set('model', model);
    galleryBtn.href = `/pages/galleri?${params.toString()}`;
  }

  _findMainProductColorAnchor() {
    // The product colour selector belongs to the Horizon product-info stack,
    // outside <wheel-configurator>. Find its visible "Velg farge"/"Choose color"
    // label and return the smallest surrounding block that also contains swatches.
    const section = this.closest('.shopify-section, [id^="shopify-section-"]') || document;
    const selector = 'legend,label,span,p,div,h2,h3';
    const colourLabelPattern = /^(velg farge|choose color|välj färg|vælg farve)\b/i;

    const candidates = Array.from(section.querySelectorAll(selector)).filter(el => {
      if (el.closest('wheel-configurator')) return false;
      const text = String(el.textContent || '').replace(/\s+/g, ' ').trim();
      if (!text || text.length > 90 || !colourLabelPattern.test(text)) return false;
      try {
        const style = window.getComputedStyle(el);
        if (style.display === 'none' || style.visibility === 'hidden') return false;
        if (typeof el.getClientRects === 'function' && el.getClientRects().length === 0) return false;
      } catch { /* visible by default */ }
      return true;
    });

    if (!candidates.length) return null;

    // Prefer a visible colour label that appears before the configurator in the
    // product-info stack, then choose the shortest matching text.
    const beforeConfigurator = candidates.filter(el => {
      try {
        return Boolean(el.compareDocumentPosition(this) & Node.DOCUMENT_POSITION_FOLLOWING);
      } catch {
        return true;
      }
    });
    const pool = beforeConfigurator.length ? beforeConfigurator : candidates;
    const label = pool.reduce((best, el) => {
      const bestText = String(best.textContent || '').replace(/\s+/g, ' ').trim();
      const text = String(el.textContent || '').replace(/\s+/g, ' ').trim();
      return text.length < bestText.length ? el : best;
    }, pool[0]);
    const interactiveSelector = [
      'input[type="radio"]',
      'button',
      'a[href]',
      '[role="radio"]',
      '[data-swatch]',
      '[class*="swatch"]'
    ].join(',');

    let node = label;
    let anchor = label.parentElement || label;
    for (let depth = 0; depth < 5; depth += 1) {
      const parent = node?.parentElement;
      if (!parent || parent === section || parent === document.body || parent.contains(this)) break;

      const text = String(parent.textContent || '').replace(/\s+/g, ' ').trim();
      if (text.length > 320) break;

      const controls = parent.querySelectorAll(interactiveSelector).length;
      if (controls > 0 && controls <= 30) {
        anchor = parent;
        break;
      }

      anchor = parent;
      node = parent;
    }

    return anchor;
  }

  _placeAventadorFlowButtons() {
    const aiBtn = this._aiBtn || this.querySelector('[data-role="wc-ai-visualizer"]');
    const galleryBtn = this.querySelector('[data-role="wc-gallery-btn"]');
    if (!aiBtn) return;

    if (!document.getElementById('sbf-ai-position-and-price-polish-v5')) {
      const style = document.createElement('style');
      style.id = 'sbf-ai-position-and-price-polish-v5';
      style.textContent = `
        /* Remove any outer frame around AI + configurator. */
        wheel-configurator[data-aventador] {
          border: 0 !important;
          border-color: transparent !important;
          border-image: none !important;
          outline: 0 !important;
          box-shadow: none !important;
          background: transparent !important;
          padding: 0 !important;
          overflow: visible !important;
        }
        wheel-configurator[data-aventador]::before,
        wheel-configurator[data-aventador]::after {
          content: none !important;
          display: none !important;
          border: 0 !important;
          box-shadow: none !important;
        }

        /* AI is its own box, immediately below "Velg farge". */
        .wc__ai-btn--hero {
          margin-top: 0.12rem !important;
          margin-bottom: 0.62rem !important;
          background: transparent !important;
        }
        .wc__ai-btn--hero .wc__ai-btn-top,
        .wc__ai-btn--hero:hover .wc__ai-btn-top {
          background: #000 !important;
          background-color: #000 !important;
        }

        /* The rest of the configurator remains a separate rectangle. */
        wheel-configurator[data-aventador] > .wc[data-role="wc-root"] {
          border: 1px solid rgba(200,160,81,.68) !important;
          border-radius: 10px !important;
          background: #000 !important;
          box-shadow: none !important;
          box-sizing: border-box !important;

          /* Move ONLY the configurator frame upward.
             Do not alter the AI button spacing/DOM, so price/menu rendering
             and the confirmed-working AI flow remain exactly as before. */
          transform: translateY(-7px) !important;
          margin-bottom: -7px !important;
        }

        /* Compact price block: no vertical separator lines. */
        wheel-configurator .wc__price-current {
          border-left: 0 !important;
          border-right: 0 !important;
        }
        wheel-configurator .wc__premium-divider,
        wheel-configurator .wc__premium-save-icon {
          display: none !important;
        }
        wheel-configurator .wc__premium-before,
        wheel-configurator .wc__price-current,
        wheel-configurator .wc__premium-save-copy {
          padding-left: .45rem !important;
          padding-right: .45rem !important;
        }

        @media (max-width: 749px) {
          .wc__ai-btn--hero {
            margin-top: 0.08rem !important;
            margin-bottom: 0.55rem !important;
          }
          wheel-configurator .wc__price-current-label {
            font-size: .70rem !important;
          }
          wheel-configurator .wc__premium-before span,
          wheel-configurator .wc__premium-save-copy span {
            font-size: .58rem !important;
          }
          wheel-configurator .wc__premium-before,
          wheel-configurator .wc__price-current,
          wheel-configurator .wc__premium-save-copy {
            padding-left: .30rem !important;
            padding-right: .30rem !important;
          }
        }
      `;
      document.head.appendChild(style);
    }

    this._configureAventadorGalleryButton(galleryBtn);

    const qbCustomCard = this.querySelector('[data-role="qb-custom-card"]');
    const cartSection = this.querySelector('[data-role="wc-cart"]');
    const hasVisibleQbCustomCard = !!(qbCustomCard && !qbCustomCard.hidden && !this._quickBuyEl?.hidden);

    // Keep the real AI button inside <wheel-configurator>. Some of the AI code and
    // theme integrations expect it to stay there. The high CTA below "Velg farge"
    // is a proxy button that calls the exact same AI flow.
    aiBtn.hidden = true;
    aiBtn.classList.remove('wc__ai-btn--hero', 'wc__ai-btn--flow', 'wc__ai-btn--post-cart');

    const placeGallery = () => {
      if (!galleryBtn) return;
      galleryBtn.hidden = true;
      galleryBtn.classList.remove('wc__ai-btn--flow');

      if (this._isAventadorForged && this._quickBuyMode && this._vehicle?.specs) {
        galleryBtn.hidden = false;
        galleryBtn.classList.add('wc__ai-btn--flow');
        if (hasVisibleQbCustomCard) qbCustomCard.insertAdjacentElement('afterend', galleryBtn);
        else if (cartSection) cartSection.insertAdjacentElement('afterend', galleryBtn);
      }
    };

    this.querySelector('[data-role="wc-design-modal-btn"]')?.remove();

    // Recreate the proxy if Shopify has replaced the surrounding product DOM.
    if (!this._aiHeroBtn || !this._aiHeroBtn.isConnected) {
      const hero = aiBtn.cloneNode(true);
      hero.hidden = false;
      hero.removeAttribute('hidden');
      hero.disabled = false;
      hero.removeAttribute('disabled');
      hero.dataset.role = 'wc-ai-visualizer-hero';
      hero.classList.remove('wc__ai-btn--flow', 'wc__ai-btn--post-cart');
      hero.classList.add('wc__ai-btn--hero');
      hero.style.pointerEvents = 'auto';

      hero.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this._aiHandlePrimaryClick();
      });

      this._aiHeroBtn = hero;
    }

    const hero = this._aiHeroBtn;
    this._aiSyncHeroButton();

    const mustHide =
      (this._noMatchCards && !this._noMatchCards.hidden) ||
      this._shouldHideAiForPartialFit() ||
      (this._finishMode && !this._selectedFinish);

    if (mustHide) {
      hero.hidden = true;
      placeGallery();
      return;
    }

    hero.hidden = false;
    hero.disabled = false;
    hero.removeAttribute('disabled');

    // Primary placement: directly below the product colour selector.
    const colourAnchor = this._findMainProductColorAnchor();
    if (colourAnchor?.parentNode) {
      // If the chosen anchor is inside a fieldset, move outside the fieldset so a
      // disabled variant-picker can never swallow the AI button click.
      const fieldset = colourAnchor.closest?.('fieldset');
      const safeAnchor = fieldset && !fieldset.contains(this) ? fieldset : colourAnchor;
      safeAnchor.insertAdjacentElement('afterend', hero);
      placeGallery();
      return;
    }

    // Fallback: directly before the configurator, still high on the product page.
    if (this.parentNode) {
      this.parentNode.insertBefore(hero, this);
    }

    placeGallery();
  }

  _aiSyncHeroButton() {
    if (!this._aiHeroBtn || !this._aiBtn) return;
    const sourceTop = this._aiBtn.querySelector('.wc__ai-btn-top');
    const heroTop = this._aiHeroBtn.querySelector('.wc__ai-btn-top');
    if (sourceTop && heroTop && heroTop.innerHTML !== sourceTop.innerHTML) {
      heroTop.innerHTML = sourceTop.innerHTML;
    }
    this._aiHeroBtn.classList.toggle('wc__ai-btn--ready', this._aiBtn.classList.contains('wc__ai-btn--ready'));
    this._aiHeroBtn.classList.toggle('wc__ai-btn--generating', this._aiBtn.classList.contains('wc__ai-btn--generating'));
  }

  _aiHandlePrimaryClick() {
    if (!this._aiModal) return;

    if (!this._aiCustomerId) {
      this._aiOpenGate();
      return;
    }

    if (this._aiResultUrl || this._aiResultImage) {
      this._aiOpenModal(4);
      const img = this._aiModal.querySelector('[data-role="ai-result-img"]');
      if (img) {
        const mimeType = this._aiResultMimeType || 'image/jpeg';
        img.src = this._aiResultObjectUrl || this._aiResultUrl || `data:${mimeType};base64,${this._aiResultImage}`;
      }
      this._aiApplyResultSummary(this._aiResultMeta || this._aiBuildResultMeta());
      return;
    }

    this._aiOpenModal(1);
  }

  _shouldHideAiForPartialFit() {
    if (!this._vehicle?.specs) return false;
    const variant = this._getSelectedVariant();
    if (!variant) return false;

    const result = this._checkVariantCompat(this._vehicle.specs, variant);
    // Hide only when the setup is not compatible.
    // Warning-level states (ring/ET/diameter notes) may still show visualizer.
    return !result?.compatible;
  }

  // ═══════════════════════════════════════════════════════════════════
  // AI Visualizer Modal
  // ═══════════════════════════════════════════════════════════════════

  _initAiVisualizer() {
    this._aiBtn = this.querySelector('[data-role="wc-ai-visualizer"]');
    this._aiModal = this.querySelector('[data-role="wc-ai-modal"]');
    if (this._aiModal && this.hasAttribute('data-aventador')) {
      this._aiModal.dataset.awBrand = 'aventador';
    }
    if (!this._aiBtn || !this._aiModal) return;

    // For custom-finish products: hide button until finish is selected
    if (this._finishMode) {
      this._aiBtn.hidden = true;
    }

    this._aiCarBase64 = null;
    // null = customer has not chosen an AI flow yet. Existing photo mode remains unchanged.
    this._aiMode = null;
    this._aiVehicleColor = '';
    this._aiCurrentDiam = null;
    this._aiDesiredDiam = null;
    this._aiResultImage = null;
    this._aiResultUrl = null;
    this._aiResultObjectUrl = null;
    this._aiResultMimeType = 'image/jpeg';
    this._aiResultMeta = null;
    this._aiVisualOptions = { darkStudio: true, lowerCar: false };
    this._aiDefaultBtnHTML = this._aiBtn.querySelector('.wc__ai-btn-top')?.innerHTML || '';
    this.dataset.sbfAiBuild = SBF_AI_BUILD_ID;

    // Check localStorage for saved result
    const saved = this._aiLoadResult();
    if (saved) {
      this._aiResultImage = saved.image || null;
      this._aiResultUrl = saved.url || null;
      this._aiResultMimeType = saved.mimeType || 'image/jpeg';
      this._aiResultMeta = saved.meta || null;
      this._aiSetBtnReady();
    }

    // Gate modal
    this._aiGateModal = this.querySelector('[data-role="wc-ai-gate-modal"]');
    if (this._aiGateModal && this.hasAttribute('data-aventador')) {
      this._aiGateModal.dataset.awBrand = 'aventador';
    }
    this._aiGateModal?.querySelector('[data-role="wc-ai-gate-close"]')
      ?.addEventListener('click', () => this._aiCloseGate());
    this._aiGateModal?.addEventListener('click', (e) => {
      if (e.target === this._aiGateModal) this._aiCloseGate();
    });

    // Real button stays inside the component; the hero proxy calls the same handler.
    this._aiBtn.addEventListener('click', () => this._aiHandlePrimaryClick());

    // Close button + backdrop
    this._aiModal.querySelector('[data-role="wc-ai-close"]')
      ?.addEventListener('click', () => this._aiCloseModal());
    this._aiModal.addEventListener('click', (e) => {
      if (e.target === this._aiModal) this._aiCloseModal();
    });

    // AI mode choice: keep the proven upload flow, add a separate generated-car flow.
    this._aiModal.querySelector('[data-role="ai-mode-upload"]')
      ?.addEventListener('click', () => {
        this._aiMode = 'uploaded_car';
        this._aiShowModePanel('uploaded_car');
      });

    this._aiModal.querySelector('[data-role="ai-mode-generated"]')
      ?.addEventListener('click', () => {
        this._aiMode = 'generated_car';
        this._aiShowModePanel('generated_car');
        this._aiRefreshGeneratedVehiclePanel();
      });

    this._aiModal.querySelectorAll('[data-role="ai-mode-back"]')
      .forEach(btn => btn.addEventListener('click', () => {
        this._aiMode = null;
        this._aiShowModePanel(null);
      }));

    this._aiModal.querySelector('[data-role="ai-generated-select-vehicle"]')
      ?.addEventListener('click', () => {
        this._aiCloseModal();
        this._openVehicleModal();
      });

    this._aiModal.querySelector('[data-role="ai-generated-color-grid"]')
      ?.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-ai-color]');
        if (!btn) return;
        this._aiVehicleColor = String(btn.dataset.aiColor || '').trim();
        this._aiModal.querySelectorAll('[data-role="ai-generated-color-grid"] [data-ai-color]')
          .forEach(colorBtn => colorBtn.classList.toggle('wc__ai-color--selected', colorBtn === btn));
        this._aiRefreshGeneratedVehiclePanel();
      });

    this._aiModal.querySelector('[data-role="ai-generated-continue"]')
      ?.addEventListener('click', () => {
        if (!this._vehicle || !this._aiVehicleColor) {
          this._aiRefreshGeneratedVehiclePanel();
          return;
        }
        const preferredDiameter =
          Number(this._selectedDiameter) ||
          Number(this._aiBuildResultMeta()?.desired_diameter) ||
          Number(this._aiGetProductDiameters()?.[0]) ||
          0;
        this._aiDesiredDiam = preferredDiameter > 0 ? preferredDiameter : null;
        this._aiCurrentDiam = null;
        this._aiPopulateDesiredDiameters();
        this._aiSetStep(2);
      });

    this._aiShowModePanel(null);

    // Upload events
    const uploadZone = this._aiModal.querySelector('[data-role="ai-upload-zone"]');
    const fileInput = this._aiModal.querySelector('[data-role="ai-file-input"]');

    uploadZone?.addEventListener('click', (e) => {
      if (e.target.closest('label')) return;
      fileInput?.click();
    });

    fileInput?.addEventListener('change', () => {
      if (fileInput.files?.length) this._aiHandleFile(fileInput.files[0]);
    });

    uploadZone?.addEventListener('dragover', (e) => {
      e.preventDefault();
      uploadZone.classList.add('wc__ai-upload-zone--dragover');
    });
    uploadZone?.addEventListener('dragleave', () => {
      uploadZone.classList.remove('wc__ai-upload-zone--dragover');
    });
    uploadZone?.addEventListener('drop', (e) => {
      e.preventDefault();
      uploadZone.classList.remove('wc__ai-upload-zone--dragover');
      if (e.dataTransfer.files?.length) this._aiHandleFile(e.dataTransfer.files[0]);
    });

    // Delete image → reset to upload zone
    this._aiModal.querySelector('[data-role="ai-delete-image"]')
      ?.addEventListener('click', () => {
        this._aiCarBase64 = null;
        this._aiResultImage = null;
        this._aiResultUrl = null;
        this._aiResultMeta = null;

        const resultImg = this._aiModal.querySelector('[data-role="ai-result-img"]');
        if (resultImg) {
          resultImg.onload = null;
          resultImg.onerror = null;
          resultImg.removeAttribute('srcset');
          resultImg.removeAttribute('src');
        }

        const staleObjectUrl = this._aiResultObjectUrl;
        this._aiResultObjectUrl = null;
        if (staleObjectUrl) {
          window.setTimeout(() => {
            try { URL.revokeObjectURL(staleObjectUrl); } catch { /* ignore */ }
          }, 0);
        }

        const preview = this._aiModal.querySelector('[data-role="ai-preview"]');
        const previewImg = this._aiModal.querySelector('[data-role="ai-preview-img"]');
        const zone = this._aiModal.querySelector('[data-role="ai-upload-zone"]');
        const fileInput = this._aiModal.querySelector('[data-role="ai-file-input"]');
        if (preview) preview.hidden = true;
        if (previewImg) previewImg.removeAttribute('src');
        if (zone) zone.hidden = false;
        if (fileInput) fileInput.value = '';
      });

    // Next → diameter step
    this._aiModal.querySelector('[data-role="ai-to-diameter"]')
      ?.addEventListener('click', () => {
        if (this._aiCarBase64) {
          this._aiPopulateDesiredDiameters();
          this._aiSetStep(2);
        }
      });

    // Diameter grids
    this._aiModal.querySelector('[data-role="ai-current-diam-grid"]')
      ?.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-diam]');
        if (!btn) return;
        this._aiCurrentDiam = parseInt(btn.dataset.diam, 10);
        this._aiModal.querySelectorAll('[data-role="ai-current-diam-grid"] .wc__btn3d')
          .forEach(b => b.classList.toggle('wc__btn3d--selected', b === btn));
        this._aiUpdateGenerateBtn();
      });

    this._aiModal.querySelector('[data-role="ai-desired-diam-grid"]')
      ?.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-diam]');
        if (!btn) return;
        this._aiDesiredDiam = parseInt(btn.dataset.diam, 10);
        this._aiModal.querySelectorAll('[data-role="ai-desired-diam-grid"] .wc__btn3d')
          .forEach(b => b.classList.toggle('wc__btn3d--selected', b === btn));
        this._aiUpdateGenerateBtn();
        this._aiResultMeta = this._aiBuildResultMeta();
        this._aiApplyResultSummary(this._aiResultMeta);
      });

    // Studio background is always enabled. The customer no longer gets a background toggle.
    const darkStudioToggle = this._aiModal.querySelector('[data-role="ai-dark-studio"]');
    const darkStudioValue = this.querySelector('[data-role="aw-ai-dark-studio-value"]');
    const studioOptions = this._aiModal.querySelector('.aw-ai-options');
    if (darkStudioToggle) {
      darkStudioToggle.checked = true;
      darkStudioToggle.disabled = true;
    }
    if (darkStudioValue) darkStudioValue.value = 'true';
    if (studioOptions) studioOptions.style.display = 'none';
    this._aiVisualOptions = {
      ...(this._aiVisualOptions || {}),
      darkStudio: true,
      lowerCar: this._aiGetLowerCarEnabled(),
    };
    this._aiResultMeta = this._aiBuildResultMeta();
    this._aiApplyResultSummary(this._aiResultMeta);

    // Generate button — credit-based
    this._aiModal.querySelector('[data-role="ai-generate"]')
      ?.addEventListener('click', () => {
        this._aiGenerateWithCredits();
      });

    // Buy credits button in diameter step
    this._aiModal.querySelector('[data-role="ai-buy-credits-btn"]')
      ?.addEventListener('click', () => this._aiPurchaseCredits());

    // Login button — show info popup before redirecting
    this._aiModal.querySelector('[data-role="ai-login-btn"]')
      ?.addEventListener('click', () => {
        if (confirm('Etter at du har logget inn, naviger tilbake til denne produktsiden for å fortsette med AI-visualisering.')) {
          window.location.href = '/account/login';
        }
      });

    // Read customer info from data attributes
    this._aiCustomerId = this.dataset.customerId || null;
    this._aiCustomerEmail = this.dataset.customerEmail || null;

    // Credit state (loaded from API when modal opens)
    this._aiCreditStatus = null;
    this._aiLoadCreditStatus();
  }


  _aiVehicleDisplayName(vehicle = this._vehicle) {
    if (!vehicle) return '';
    const values = [vehicle.year, vehicle.makeName, vehicle.modelName, vehicle.trimName]
      .map(value => this._aiCleanText(value))
      .filter(Boolean);
    const deduped = [];
    for (const value of values) {
      if (!deduped.some(existing => existing.toLowerCase() === value.toLowerCase())) {
        deduped.push(value);
      }
    }
    return deduped.join(' ');
  }

  _aiShowModePanel(mode = this._aiMode) {
    if (!this._aiModal) return;
    const chooser = this._aiModal.querySelector('[data-role="ai-mode-choice"]');
    const uploadPanel = this._aiModal.querySelector('[data-role="ai-upload-panel"]');
    const generatedPanel = this._aiModal.querySelector('[data-role="ai-generated-panel"]');

    if (chooser) chooser.hidden = !!mode;
    if (uploadPanel) uploadPanel.hidden = mode !== 'uploaded_car';
    if (generatedPanel) generatedPanel.hidden = mode !== 'generated_car';

    if (mode === 'generated_car') this._aiRefreshGeneratedVehiclePanel();
  }

  _aiRefreshGeneratedVehiclePanel() {
    if (!this._aiModal) return;
    const label = this._aiModal.querySelector('[data-role="ai-generated-vehicle-label"]');
    const missing = this._aiModal.querySelector('[data-role="ai-generated-vehicle-missing"]');
    const continueBtn = this._aiModal.querySelector('[data-role="ai-generated-continue"]');
    const selectedColor = this._aiModal.querySelector('[data-role="ai-generated-color-selected"]');

    const vehicleName = this._aiVehicleDisplayName();
    if (label) {
      label.textContent = vehicleName || 'Ingen bil valgt';
      label.classList.toggle('wc__ai-generated-vehicle--missing', !vehicleName);
    }
    if (missing) missing.hidden = !!vehicleName;
    if (selectedColor) {
      selectedColor.textContent = this._aiVehicleColor
        ? `Valgt farge: ${this._aiVehicleColor}`
        : 'Velg bilfarge';
    }
    if (continueBtn) continueBtn.disabled = !vehicleName || !this._aiVehicleColor;
  }

  _aiSyncDiameterStepForMode() {
    if (!this._aiModal) return;
    const currentSection = this._aiModal.querySelector('[data-role="ai-current-diam-section"]');
    const desiredSection = this._aiModal.querySelector('[data-role="ai-desired-diam-section"]');
    const generatedNote = this._aiModal.querySelector('[data-role="ai-generated-config-note"]');

    const generated = this._aiMode === 'generated_car';

    // Uploaded photo: customer chooses both the current wheel diameter and desired diameter.
    // Generated comparable car: there is no donor wheel size to preserve, so only the desired
    // wheel diameter is needed. The desired-size chooser MUST remain visible in both modes.
    if (currentSection) currentSection.hidden = generated;
    if (desiredSection) desiredSection.hidden = false;

    if (generatedNote) {
      generatedNote.hidden = !generated;
      if (generated) {
        const name = this._aiVehicleDisplayName();
        generatedNote.innerHTML =
          `<strong>${this._esc(name || 'Valgt bil')}</strong>` +
          `<span>${this._esc(this._aiVehicleColor || '')}</span>`;
      }
    }

    this._aiUpdateGenerateBtn();
  }

  async _aiBuildGeneratedVehiclePayload() {
    if (!this._vehicle) {
      throw new Error('Velg bilen din i felgvelgeren før du lager illustrasjonen.');
    }
    if (!this._aiVehicleColor) {
      throw new Error('Velg bilfarge før du lager illustrasjonen.');
    }

    // Clone so the visualizer can enrich the request without mutating the saved vehicle.
    let snapshot;
    try {
      snapshot = JSON.parse(JSON.stringify(this._vehicle));
    } catch {
      snapshot = { ...this._vehicle };
    }

    // Older saved selections may not contain the full Wheel-Size trim response.
    // Fetch it once here when possible so Gemini receives all available identity/body data.
    const trimId = String(snapshot?.trimId || '').trim();
    if (
      !snapshot?.specsRaw &&
      snapshot?.makeSlug &&
      snapshot?.modelSlug &&
      trimId &&
      !trimId.startsWith('regnr:')
    ) {
      try {
        snapshot.specsRaw = await this._fetchApi(
          `/api/vehicles/makes/${snapshot.makeSlug}/models/${snapshot.modelSlug}/${trimId}`
        );
      } catch (error) {
        console.warn('[SBF AI] Full Wheel-Size trim data unavailable; using saved identity.', error);
      }
    }

    return {
      mode: 'generated_car',
      vehicle_make: this._aiCleanText(snapshot.makeName || snapshot.makeSlug),
      vehicle_model: this._aiCleanText(snapshot.modelName || snapshot.modelSlug),
      vehicle_year: this._aiCleanText(snapshot.year),
      vehicle_trim: this._aiCleanText(snapshot.trimName),
      vehicle_color: this._aiVehicleColor,
      vehicle_data: JSON.stringify({ vehicle: snapshot }),
      fitment_mode: 'generated_car',
    };
  }


  _aiGetDarkStudioEnabled() {
    // Locked by design: every AI visualization uses the premium dark studio background.
    const checkbox = this._aiModal?.querySelector('[data-role="ai-dark-studio"]');
    if (checkbox) checkbox.checked = true;
    const hidden = this.querySelector('[data-role="aw-ai-dark-studio-value"]');
    if (hidden) hidden.value = 'true';
    return true;
  }

  _aiGetLowerCarEnabled() {
    const checkbox = this._aiModal?.querySelector('[data-role="ai-lower-car"]');
    if (checkbox) return Boolean(checkbox.checked);

    const hidden = this.querySelector('[data-role="aw-ai-lower-car-value"]');
    return String(hidden?.value || 'false').toLowerCase() === 'true';
  }

  _aiCleanText(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  _aiBuildResultMeta(data = {}) {
    const fullTitle = this._aiCleanText(data.product_title || this.dataset.productTitle || 'Felg');
    const vendor = this._aiCleanText(data.wheel_brand || this.dataset.productVendor || '');
    const knownFinishes = [
      'Black Gloss Polished', 'Carbon Gloss Dark', 'Carbon Gloss Polished',
      'Gloss Satin Silver', 'Gloss Light Gunmetal', 'Matt Light Gunmetal',
      'Brushed Tinted Black', 'Brushed Titanium', 'Custom Finish',
      'Gloss Black', 'Gloss Bronze', 'Gloss Silver', 'Matt Black',
      'Matt Graphite', 'Matt Bronze', 'Gloss Gold', 'Gloss Red',
      'Gloss White', 'Brushed Bronze', 'Brushed Gold', 'Hyper Black',
      'Gunmetal', 'Silver', 'Bronze', 'Gold', 'White', 'Black'
    ];

    const selectedFinish = this._aiCleanText(data.finish_name || this._selectedFinish || '');
    const titleFinish = knownFinishes.find(finish =>
      fullTitle.toLowerCase().endsWith(finish.toLowerCase())
    ) || '';
    const finishName = selectedFinish || titleFinish || 'Standard finish';

    let wheelModel = this._aiCleanText(data.wheel_model || fullTitle);
    const prefixes = [
      vendor,
      'Aventador Wheels',
      'Japan Racing Wheels',
      'Japan Racing',
      'JR Wheels',
      'Vesser Wheels',
      'Vesser',
      'Concaver Wheels',
      'Concaver'
    ].filter(Boolean);

    for (const prefix of prefixes) {
      const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      wheelModel = wheelModel.replace(new RegExp(`^${escaped}[\\s–—-]*`, 'i'), '').trim();
    }

    if (titleFinish) {
      const escapedFinish = titleFinish.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      wheelModel = wheelModel.replace(new RegExp(`[\\s–—-]*${escapedFinish}$`, 'i'), '').trim();
    }

    const desiredRaw = data.desired_diameter ?? this._aiDesiredDiam ?? this._selectedDiameter ?? null;
    const currentRaw = data.current_diameter ?? this._aiCurrentDiam ?? null;
    const desiredDiameter = Number(desiredRaw);
    const currentDiameter = Number(currentRaw);

    const selectedSize = this._aiCleanText(
      data.selected_size ||
      this._selectedSize ||
      this._qbActivePair?.frontSize ||
      this._qbRearSelection?.size ||
      ''
    );

    const selectedPcd = this._aiCleanText(
      data.selected_pcd ||
      this._selectedPcd ||
      this._qbActivePair?.pcd ||
      this._qbRearSelection?.pcd ||
      ''
    );

    const selectedEt = data.selected_et ?? this._selectedExactEt ?? this._selectedEt ?? '';
    const darkStudio = data.dark_studio == null
      ? this._aiGetDarkStudioEnabled()
      : Boolean(data.dark_studio);
    const lowerCar = data.lower_car == null
      ? this._aiGetLowerCarEnabled()
      : Boolean(data.lower_car);

    return {
      wheel_brand: vendor,
      wheel_model: wheelModel || fullTitle || 'Felg',
      product_title: fullTitle,
      finish_name: finishName,
      current_diameter: Number.isFinite(currentDiameter) && currentDiameter > 0 ? currentDiameter : null,
      desired_diameter: Number.isFinite(desiredDiameter) && desiredDiameter > 0 ? desiredDiameter : null,
      selected_pcd: selectedPcd,
      selected_size: selectedSize,
      selected_et: selectedEt,
      dark_studio: darkStudio,
      lower_car: lowerCar,
    };
  }

  _aiApplyResultSummary(meta = null) {
    if (!this._aiModal) return;
    const current = meta || this._aiBuildResultMeta();
    const sizeText = current.desired_diameter
      ? `${current.desired_diameter}″`
      : (current.selected_size || 'Valgt størrelse');

    const setText = (role, value) => {
      this._aiModal
        .querySelectorAll(`[data-role="${role}"]`)
        .forEach(element => { element.textContent = String(value || ''); });
    };

    setText('aw-result-model', current.wheel_model || current.product_title || 'Felg');
    setText('aw-result-finish', current.finish_name || 'Standard finish');
    setText('aw-result-size', sizeText);
    setText('aw-result-ride-height', current.lower_car ? 'Senket' : 'Original');
    setText('aw-result-background', current.dark_studio ? 'Mørkt studio' : 'Original');
    setText(
      'aw-result-background-note',
      current.dark_studio ? 'Premium studiobelysning' : 'Originalt miljø'
    );

    this._aiVisualOptions = {
      ...(this._aiVisualOptions || {}),
      darkStudio: Boolean(current.dark_studio),
      lowerCar: Boolean(current.lower_car),
    };

    this.dataset.aiWheelModel = current.wheel_model || '';
    this.dataset.aiFinish = current.finish_name || '';
    this.dataset.aiDesiredDiameter = current.desired_diameter || '';
    this.dataset.aiSelectedSize = current.selected_size || '';
    this.dataset.aiDarkStudio = current.dark_studio ? 'true' : 'false';

    this._aiModal.dataset.aiWheelModel = current.wheel_model || '';
    this._aiModal.dataset.aiFinish = current.finish_name || '';
    this._aiModal.dataset.aiDesiredDiameter = current.desired_diameter || '';
    this._aiModal.dataset.aiDarkStudio = current.dark_studio ? 'true' : 'false';

    const detail = {
      root: this,
      product_handle: this.dataset.productHandle || '',
      ...current,
    };
    window.__SBF_WHEEL_VISUALIZER_META__ = detail;
    this.dispatchEvent(new CustomEvent('wheel-configurator:ai-meta', {
      detail,
      bubbles: true,
    }));
    window.dispatchEvent(new CustomEvent('wheel-configurator:ai-meta', { detail }));
  }

  _aiOpenModal(step) {
    if (this._aiModal) {
      // Move to body to escape parent stacking contexts
      document.body.appendChild(this._aiModal);
      this._aiModal.hidden = false;
      this._aiSetStep(step);
      this._aiApplyResultSummary(this._aiResultMeta || this._aiBuildResultMeta());
    }
  }

_aiOpenGate() {
    if (!this._aiGateModal) return;
    document.body.appendChild(this._aiGateModal);
    this._aiGateModal.hidden = false;
    document.body.style.overflow = 'hidden';
  }

  _aiCloseGate() {
    if (!this._aiGateModal) return;
    this._aiGateModal.hidden = true;
    this.appendChild(this._aiGateModal);
    document.body.style.overflow = '';
  }
  _aiCloseModal() {
    if (this._aiModal) {
      this._aiModal.hidden = true;
      // Move back into component
      this.appendChild(this._aiModal);
    }
    if (this._aiResultUrl || this._aiResultImage) {
      this._aiSaveResult(this._aiResultUrl ? null : this._aiResultImage, this._aiResultUrl, this._aiResultMeta);
      this._aiSetBtnReady();
    }
  }

  _aiSetStep(n) {
    const steps = this._aiModal.querySelectorAll('[data-role^="ai-step-"]');
    const names = { 1: 'ai-step-upload', 2: 'ai-step-diameter', 3: 'ai-step-generating', 4: 'ai-step-result' };
    steps.forEach(el => { el.hidden = el.dataset.role !== names[n]; });
    if (n === 1) this._aiShowModePanel(this._aiMode);
    if (n === 2) this._aiSyncDiameterStepForMode();
  }

  async _aiHandleFile(file) {
    if (!file.type.startsWith('image/')) return;
    if (file.size > 10 * 1024 * 1024) { alert('Bildet er for stort. Maks 10MB.'); return; }

    this._aiCarBase64 = await this._aiResizeAndEncode(file);
    const previewImg = this._aiModal.querySelector('[data-role="ai-preview-img"]');
    const preview = this._aiModal.querySelector('[data-role="ai-preview"]');
    const zone = this._aiModal.querySelector('[data-role="ai-upload-zone"]');

    if (previewImg) previewImg.src = `data:image/jpeg;base64,${this._aiCarBase64}`;
    if (preview) preview.hidden = false;
    if (zone) zone.hidden = true;
  }

  _aiResizeAndEncode(file) {
    const MAX_W = 1920;
    return new Promise(resolve => {
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(img.src);
        const canvas = document.createElement('canvas');
        if (img.width <= MAX_W && img.height <= MAX_W) {
          canvas.width = img.width;
          canvas.height = img.height;
        } else {
          const scale = Math.min(MAX_W / img.width, MAX_W / img.height);
          canvas.width = Math.round(img.width * scale);
          canvas.height = Math.round(img.height * scale);
        }
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.85).split(',')[1]);
      };
      img.src = URL.createObjectURL(file);
    });
  }

  _aiGetProductDiameters() {
    const diams = new Set();
    for (const v of this._allVariants) {
      const m = (v.option2 || '').match(/^(\d+)/);
      if (m) diams.add(parseInt(m[1], 10));
    }
    return [...diams].sort((a, b) => a - b);
  }

  _aiPopulateDesiredDiameters() {
    const grid = this._aiModal.querySelector('[data-role="ai-desired-diam-grid"]');
    if (!grid) return;
    const diams = this._aiGetProductDiameters();
    grid.innerHTML = diams.map(d => {
      const selected = Number(this._aiDesiredDiam) === Number(d);
      return `<button type="button" class="wc__btn3d wc__ai-diam-btn${selected ? ' wc__btn3d--selected' : ''}" data-diam="${d}"><span class="wc__btn3d-top">${d}"</span></button>`;
    }).join('');
  }

  _aiUpdateGenerateBtn() {
    const btn = this._aiModal.querySelector('[data-role="ai-generate"]');
    if (!btn) return;

    if (this._aiMode === 'generated_car') {
      btn.disabled = !this._vehicle || !this._aiVehicleColor || this._aiDesiredDiam == null;
      return;
    }

    btn.disabled = this._aiCurrentDiam == null || this._aiDesiredDiam == null;
  }

  async _aiLoadCreditStatus() {
    try {
      const params = new URLSearchParams();
      if (this._aiCustomerId) params.set('customer_id', this._aiCustomerId);
      const res = await fetch(`${API_BASE}/api/visualizer/credits/status?${params}`);
      if (!res.ok) throw new Error(`API ${res.status}`);
      this._aiCreditStatus = await res.json();
    } catch (err) {
      console.error('Failed to load credit status', err);
      this._aiCreditStatus = { free_available: true, free_remaining: 3, credits: 0, logged_in: !!this._aiCustomerId };
    }
    this._aiUpdateCreditUI();
  }

  _aiUpdateCreditUI() {
    const s = this._aiCreditStatus;
    if (!s) return;

    // The modal may be appended to body, so we query it separately from the main component
    const statusEls = new Set([
      ...this.querySelectorAll('[data-role="ai-credit-status"]'),
      ...(this._aiModal ? this._aiModal.querySelectorAll('[data-role="ai-credit-status"]') : [])
    ]);

    // Buttons are inside the modal
    const genBtn = this._aiModal?.querySelector('[data-role="ai-generate"]');
    const loginBtn = this._aiModal?.querySelector('[data-role="ai-login-btn"]');
    const buyBtn = this._aiModal?.querySelector('[data-role="ai-buy-credits-btn"]');

    // Reset visibility
    if (genBtn) { genBtn.hidden = false; genBtn.style.display = ''; }
    if (loginBtn) { loginBtn.hidden = true; loginBtn.style.display = 'none'; }
    if (buyBtn) { buyBtn.hidden = true; buyBtn.style.display = 'none'; }

    statusEls.forEach(el => {
      el.classList.remove('wc__ai-credit-status--free', 'wc__ai-credit-status--credits', 'wc__ai-credit-status--empty', 'wc__ai-credit-status--login');
      el.hidden = false;
    });

    let badgeClass = '';
    let badgeText = '';

    if (s.free_available) {
      badgeClass = 'wc__ai-credit-status--card';
      const num = s.free_remaining ?? 3;
      badgeText = `${num} gratis AI-visualisering${num === 1 ? '' : 'er'} inkludert`;
    } else if (!this._aiCustomerId) {
      // Not logged in
      badgeClass = 'wc__ai-credit-status--empty';
      badgeText = '10 visualiseringer - kr 99';
      if (genBtn) { genBtn.hidden = true; genBtn.style.display = 'none'; }
      if (loginBtn) { loginBtn.hidden = false; loginBtn.style.display = ''; }
    } else if (s.credits > 0) {
      // Logged in with credits
      badgeClass = 'wc__ai-credit-status--card';
      badgeText = `${s.credits} AI-visualisering${s.credits === 1 ? '' : 'er'} igjen`;
    } else {
      // Logged in, no credits
      badgeClass = 'wc__ai-credit-status--empty';
      badgeText = '10 visualiseringer - kr 99';
      if (genBtn) { genBtn.hidden = true; genBtn.style.display = 'none'; }
      if (buyBtn) { buyBtn.hidden = false; buyBtn.style.display = ''; }
    }

    statusEls.forEach(el => {
      if (badgeClass) el.classList.add(badgeClass);
      const textEl = el.querySelector('[data-role="ai-credit-text"]');
      if (textEl) textEl.textContent = badgeText;
    });

    // Update the product-page button description
    const btnDesc = this.querySelector('[data-role="wc-ai-btn-desc"]');
    if (btnDesc) btnDesc.textContent = badgeText;
  }


  async _aiGenerateWithCredits() {
    const generationId = (this._aiGenerationId || 0) + 1;
    this._aiGenerationId = generationId;

    const generateBtn = this._aiModal.querySelector('[data-role="ai-generate"]');
    const generateLabel = generateBtn?.querySelector('.wc__btn3d-top');
    const originalGenerateText = generateLabel?.textContent || 'Lag forhåndsvisning';
    const diameterError = this._aiModal.querySelector('[data-role="ai-diam-error"]');
    const resultError = this._aiModal.querySelector('[data-role="ai-result-error"]');
    const resultImg = this._aiModal.querySelector('[data-role="ai-result-img"]');

    if (generateBtn) generateBtn.disabled = true;
    if (generateLabel) generateLabel.textContent = 'Genererer...';

    [diameterError, resultError].forEach(errorBox => {
      if (!errorBox) return;
      errorBox.hidden = true;
      errorBox.innerHTML = '';
    });

    this._aiSetStep(3);
    this._aiSetBtnGenerating(true);

    const darkStudio = this._aiGetDarkStudioEnabled();
    const lowerCar = this._aiGetLowerCarEnabled();
    const resultMeta = this._aiBuildResultMeta({
      dark_studio: darkStudio,
      lower_car: lowerCar,
    });

    this._aiResultMeta = resultMeta;
    this._aiApplyResultSummary(resultMeta);

    const previousObjectUrl = this._aiResultObjectUrl || '';
    let createdObjectUrl = '';
    let acceptedObjectUrl = '';

    try {
      /*
       * Use the original, proven backend request shape.
       *
       * The experimental prompt/vehicle/fitment fields changed model behavior
       * for Japan Racing and Concaver, while using an Aventador finish image as
       * wheel_image_url could make the backend return the literal error
       * "Load failed".
       *
       * wheel_image_url is therefore always the product's authoritative wheel
       * image. finish_image_url remains a separate optional finish reference.
       */
      const toBackendImageUrl = value => {
        const source = String(value || '').trim();
        if (!source) return undefined;

        try {
          const absolute = new URL(source, window.location.href);
          return /^https?:$/i.test(absolute.protocol)
            ? absolute.href
            : undefined;
        } catch {
          return undefined;
        }
      };

      const productWheelImageUrl = toBackendImageUrl(this.dataset.productImage);
      const finishImageUrl = toBackendImageUrl(this._selectedFinishImage);
      const wheelImageUrl = productWheelImageUrl || finishImageUrl;

      const generatedMode = this._aiMode === 'generated_car';

      if (!generatedMode && !this._aiCarBase64) {
        throw new Error('Bilbildet mangler. Last opp bildet på nytt.');
      }

      if (!wheelImageUrl) {
        throw new Error(
          'Felgbildet mangler på produktet. Last inn produktsiden på nytt og prøv igjen.'
        );
      }

      const generatedVehicle = generatedMode
        ? await this._aiBuildGeneratedVehiclePayload()
        : null;

      const bodyData = generatedMode
        ? {
            ...generatedVehicle,
            wheel_image_url: wheelImageUrl,
            wheel_brand: this.dataset.productVendor,
            wheel_model: resultMeta.wheel_model || this.dataset.productTitle,
            desired_diameter: this._aiDesiredDiam || this._selectedDiameter || undefined,
            product_handle: this.dataset.productHandle,
            product_title: this.dataset.productTitle,
            finish_name: this._selectedFinish || undefined,
            finish_image_url: finishImageUrl,
            dark_studio: darkStudio,
            dark_background: darkStudio,
            lower_car: lowerCar,
          }
        : {
            // Uploaded-car request intentionally remains the proven request shape.
            car_image: this._aiCarBase64,
            wheel_image_url: wheelImageUrl,
            wheel_brand: this.dataset.productVendor,
            wheel_model:
              resultMeta.wheel_model ||
              this.dataset.productTitle,
            current_diameter:
              this._aiCurrentDiam ||
              undefined,
            desired_diameter:
              this._aiDesiredDiam ||
              undefined,
            product_handle: this.dataset.productHandle,
            product_title: this.dataset.productTitle,
            finish_name:
              this._selectedFinish ||
              undefined,
            finish_image_url: finishImageUrl,
            dark_studio: darkStudio,
            dark_background: darkStudio,
            lower_car: lowerCar,
          };

      if (this._aiCustomerId && this._aiCustomerEmail) {
        bodyData.customer_id = this._aiCustomerId;
        bodyData.customer_email = this._aiCustomerEmail;
      }

      if (this.dataset.customerToken) {
        bodyData.customer_token = this.dataset.customerToken;
      }

      const res = await fetch(`${API_BASE}/api/visualizer/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bodyData),
      });

      let json;
      try {
        json = await res.json();
      } catch {
        throw new Error(`AI-tjenesten svarte med ugyldige data (${res.status}).`);
      }

      if (generationId !== this._aiGenerationId) return;

      if (res.status === 402) {
        if (json.needs_login) {
          this._aiCreditStatus = {
            free_available: false,
            free_remaining: 0,
            credits: 0,
            logged_in: false,
          };
        } else if (json.needs_purchase) {
          this._aiCreditStatus = {
            free_available: false,
            free_remaining: 0,
            credits: 0,
            logged_in: true,
          };
        }

        this._aiUpdateCreditUI();
        this._aiSetStep(2);

        if (diameterError) {
          diameterError.hidden = false;
          diameterError.innerHTML = `<p>${this._esc(
            (json.error?.message || json.error) ||
            'Du trenger flere AI-visualiseringer for å fortsette.'
          )}</p>`;
        }
        return;
      }

      if (!res.ok || !json.success) {
        throw new Error(
          (json.error?.message || json.error) ||
          'Kunne ikke generere bilde'
        );
      }

      if (!json.image_url && !json.image) {
        throw new Error('AI-tjenesten returnerte ikke et bilde. Prøv igjen.');
      }

      if (json.credits_remaining != null && this._aiCreditStatus) {
        this._aiCreditStatus.credits = json.credits_remaining;
        this._aiCreditStatus.free_available = false;
        this._aiUpdateCreditUI();
      }

      let mimeType = String(json.image_mime_type || 'image/jpeg').trim() || 'image/jpeg';
      let rawImage = typeof json.image === 'string' ? json.image.trim() : '';
      let backendUrl = typeof json.image_url === 'string' ? json.image_url.trim() : '';

      const normalizeResultUrl = value => {
        const source = String(value || '').trim();
        if (!source) return '';
        if (/^(data:|blob:)/i.test(source)) return source;
        try {
          return new URL(source, `${API_BASE}/`).href;
        } catch {
          return source;
        }
      };

      if (/^data:/i.test(rawImage)) {
        const dataMatch = rawImage.match(/^data:([^;,]+)?(?:;base64)?,(.*)$/is);
        if (dataMatch) {
          if (dataMatch[1]) mimeType = dataMatch[1];
          rawImage = dataMatch[2] || '';
        }
      } else if (/^(https?:|blob:|\/\/|\/)/i.test(rawImage)) {
        if (!backendUrl) backendUrl = rawImage;
        rawImage = '';
      }

      backendUrl = normalizeResultUrl(backendUrl);
      let compactBase64 = rawImage.replace(/\s+/g, '');

      if (compactBase64.startsWith('iVBOR')) mimeType = 'image/png';
      else if (compactBase64.startsWith('UklGR')) mimeType = 'image/webp';
      else if (compactBase64.startsWith('R0lGOD')) mimeType = 'image/gif';

      if (!resultImg) {
        throw new Error('Fant ikke bildefeltet i AI-visualiseringen.');
      }

      /*
       * Decode base64 in small chunks.
       *
       * The earlier decoder called atob() on the entire multi-megabyte image,
       * which temporarily kept all of these in memory at the same time:
       * - the base64 string
       * - one complete binary string
       * - the Uint8Array chunks
       * - the final Blob
       *
       * Safari can hit its image-memory limit after several generations.
       * Chunked atob() removes the extra full-size binary string while keeping
       * the same Blob-first result flow that already worked for image 1 and 2.
       */
      const base64ToObjectUrl = (base64, type) => {
        if (!base64) return '';

        const BASE64_CHUNK_CHARS = 1024 * 1024; // multiple of 4
        const blobParts = [];

        for (let offset = 0; offset < base64.length; offset += BASE64_CHUNK_CHARS) {
          const slice = base64.slice(offset, offset + BASE64_CHUNK_CHARS);
          const binarySlice = atob(slice);
          const bytes = new Uint8Array(binarySlice.length);

          for (let index = 0; index < binarySlice.length; index += 1) {
            bytes[index] = binarySlice.charCodeAt(index);
          }

          blobParts.push(bytes);
        }

        return URL.createObjectURL(new Blob(blobParts, { type }));
      };


      /*
       * Load candidates on a detached Image. No handlers are attached to the
       * visible resultImg, so a delayed error event from image 1 cannot reject
       * image 2.
       */
      const preloadSource = source => new Promise((resolve, reject) => {
        const probe = new Image();
        let settled = false;

        const finish = callback => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timer);
          probe.onload = null;
          probe.onerror = null;
          callback();
        };

        const timer = window.setTimeout(() => {
          finish(() => reject(new Error('Bildet brukte for lang tid på å laste.')));
        }, 30000);

        probe.onload = () => finish(() => {
          if (probe.naturalWidth > 0 && probe.naturalHeight > 0) {
            resolve(source);
          } else {
            reject(new Error('Bildet ble lastet uten gyldig størrelse.'));
          }
        });

        probe.onerror = () => finish(() => {
          reject(new Error('Bildekilden kunne ikke lastes.'));
        });

        probe.decoding = 'async';
        probe.src = source;
      });

      const isIOSLike =
        /iPad|iPhone|iPod/i.test(navigator.userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

      const loadBackendUrl = async () => {
        if (!backendUrl) return '';

        let lastError = null;
        for (let attempt = 0; attempt < 3; attempt += 1) {
          let source = backendUrl;

          if (/^https?:/i.test(source)) {
            try {
              const url = new URL(source);
              url.searchParams.set('_sbf_ai', `${Date.now()}_${attempt}`);
              source = url.href;
            } catch { /* keep original source */ }
          }

          try {
            if (attempt > 0) {
              await new Promise(resolve => {
                window.setTimeout(resolve, attempt * 650);
              });
            }

            return await preloadSource(source);
          } catch (error) {
            lastError = error;
          }
        }

        if (lastError) {
          console.warn('[SBF AI] Backend-bildet kunne ikke lastes:', lastError);
        }
        return '';
      };

      const loadBase64Blob = async () => {
        if (!compactBase64) return '';

        try {
          createdObjectUrl = base64ToObjectUrl(compactBase64, mimeType);
          return await preloadSource(createdObjectUrl);
        } catch (error) {
          if (createdObjectUrl) {
            try { URL.revokeObjectURL(createdObjectUrl); } catch { /* ignore */ }
            createdObjectUrl = '';
          }

          console.warn('[SBF AI] Base64-bildet kunne ikke lastes:', error);
          return '';
        }
      };

      let loadedSource = '';
      let loadedType = '';

      /*
       * iPhone/iPad: use the backend URL first and do not create a duplicate
       * Blob unless the URL has actually failed. Keep _aiCarBase64 so the same
       * uploaded car can be used for image 2, 3 and later generations.
       */
      if (isIOSLike) {
        loadedSource = await loadBackendUrl();

        if (loadedSource) {
          loadedType = 'url';

          // Release the unused multi-megabyte result base64 references.
          json.image = null;
          rawImage = '';
          compactBase64 = '';
        } else {
          loadedSource = await loadBase64Blob();
          if (loadedSource) loadedType = 'blob';
        }
      } else {
        loadedSource = await loadBase64Blob();

        if (loadedSource) {
          loadedType = 'blob';
        } else {
          loadedSource = await loadBackendUrl();
          if (loadedSource) loadedType = 'url';
        }
      }

      if (!loadedSource) {
        throw new Error('Det ferdige AI-bildet kunne ikke lastes.');
      }

      if (generationId !== this._aiGenerationId) return;

      if (!loadedSource) {
        throw lastImageError || new Error('Det ferdige AI-bildet kunne ikke lastes.');
      }

      resultImg.onload = null;
      resultImg.onerror = null;
      resultImg.removeAttribute('srcset');
      resultImg.src = loadedSource;

      if (loadedType === 'blob') {
        acceptedObjectUrl = loadedSource;
        this._aiResultObjectUrl = loadedSource;
      } else {
        this._aiResultObjectUrl = null;
        if (createdObjectUrl) {
          try { URL.revokeObjectURL(createdObjectUrl); } catch { /* ignore */ }
          createdObjectUrl = '';
        }
      }

      if (previousObjectUrl && previousObjectUrl !== acceptedObjectUrl) {
        window.setTimeout(() => {
          try { URL.revokeObjectURL(previousObjectUrl); } catch { /* ignore */ }
        }, 250);
      }

      this._aiResultImage = (!isIOSLike && !backendUrl) ? (compactBase64 || null) : null;
      this._aiResultUrl = loadedType === 'url' ? loadedSource : (backendUrl || null);
      this._aiResultMimeType = mimeType;
      this._aiResultMeta = resultMeta;

      this._aiApplyResultSummary(resultMeta);
      this._aiSaveResult(
        this._aiResultUrl ? null : this._aiResultImage,
        this._aiResultUrl,
        resultMeta
      );

      this._aiSetStep(4);
      this._aiSetBtnGenerating(false);
      this._aiSetBtnReady();

      requestAnimationFrame(() => this._aiApplyResultSummary(resultMeta));
      window.setTimeout(() => this._aiApplyResultSummary(resultMeta), 150);
    } catch (err) {
      if (generationId !== this._aiGenerationId) return;

      console.error('Generation failed', err);
      this._aiSetBtnGenerating(false);

      if (resultImg) {
        resultImg.onload = null;
        resultImg.onerror = null;
        resultImg.removeAttribute('srcset');
        resultImg.removeAttribute('src');
      }

      if (createdObjectUrl && createdObjectUrl !== acceptedObjectUrl) {
        try { URL.revokeObjectURL(createdObjectUrl); } catch { /* ignore */ }
      }
      if (previousObjectUrl) {
        try { URL.revokeObjectURL(previousObjectUrl); } catch { /* ignore */ }
      }

      this._aiResultObjectUrl = null;
      this._aiResultImage = null;
      this._aiResultUrl = null;
      this._aiResultMeta = null;
      try {
        localStorage.removeItem(`wc_ai_result_${this.dataset.productHandle}`);
      } catch { /* ignore */ }

      if (resultError) {
        resultError.hidden = false;
        resultError.innerHTML = `<p>${this._esc(err.message)}</p>
          <button type="button" class="wc__btn3d" data-role="ai-retry">
            <span class="wc__btn3d-top">Prøv igjen</span>
          </button>`;

        resultError
          .querySelector('[data-role="ai-retry"]')
          ?.addEventListener(
            'click',
            () => this._aiGenerateWithCredits(),
            { once: true }
          );
      }

      this._aiSetStep(4);
    } finally {
      if (generationId === this._aiGenerationId) {
        if (generateBtn) generateBtn.disabled = false;
        if (generateLabel) generateLabel.textContent = originalGenerateText;
      }
    }
  }

  async _aiPurchaseCredits() {
    if (!this._aiCustomerId || !this._aiCustomerEmail) {
      window.location.href = '/account/login';
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/api/vipps/create-credit-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: this._aiCustomerId,
          email: this._aiCustomerEmail,
          return_url: window.location.href.split('?')[0],
        }),
      });

      const json = await res.json();
      if (!res.ok || !json.success) throw new Error((json.error?.message || json.error) || 'Kunne ikke opprette betaling');

      window.location.href = json.redirect_url;
    } catch (err) {
      console.error('Credit purchase failed', err);
      const errorEl = this._aiModal.querySelector('[data-role="ai-result-error"]');
      if (errorEl) {
        errorEl.hidden = false;
        errorEl.innerHTML = `<p>${this._esc(err.message)}</p>`;
      }
    }
  }

  _checkVippsReturn() {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('vipps_ref');
    if (!ref) return;

    // Show toast and poll for payment status
    this._showVippsToast('Bekrefter betaling...');
    this._pollVippsPayment(ref);
  }

  async _pollVippsPayment(ref) {
    const poll = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/vipps/credit-status?ref=${ref}`);
        const data = await res.json();

        if (data.status === 'captured') {
          this._showVippsToast('10 kreditter lagt til!');
          // Refresh credit status so badge updates to "X igjen"
          this._aiLoadCreditStatus();
          const url = new URL(window.location.href);
          url.searchParams.delete('vipps_ref');
          url.searchParams.delete('status');
          window.history.replaceState({}, '', url.toString());
          return;
        }

        if (data.status === 'expired' || data.status === 'aborted') {
          this._showVippsToast('Betalingen ble avbrutt');
          const url = new URL(window.location.href);
          url.searchParams.delete('vipps_ref');
          url.searchParams.delete('status');
          window.history.replaceState({}, '', url.toString());
          return;
        }
      } catch (e) {
        console.error('Vipps poll error:', e);
      }
      setTimeout(poll, 2000);
    };

    poll();
  }

  _showVippsToast(message) {
    // Remove existing toast
    const existing = document.querySelector('.wc__vipps-toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.className = 'wc__vipps-toast';
    toast.textContent = message;
    toast.style.cssText = 'position:fixed;top:20px;left:50%;transform:translateX(-50%);background:#1a1a2e;color:#c8a84e;padding:12px 24px;border-radius:8px;font-size:14px;font-weight:600;z-index:99999;box-shadow:0 4px 12px rgba(0,0,0,0.3);';
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 4000);
  }

  _aiSetBtnGenerating(on) {
    if (!this._aiBtn) return;
    const top = this._aiBtn.querySelector('.wc__ai-btn-top');
    if (!top) return;

    if (on) {
      this._aiBtn.classList.add('wc__ai-btn--generating');
      this._aiBtn.classList.remove('wc__ai-btn--ready');
      this._aiBtnOrigHTML = top.innerHTML;

      const title = top.querySelector('.wc__ai-btn-title');
      const desc = top.querySelector('.wc__ai-btn-desc');
      const kicker = top.querySelector('.wc__ai-btn-kicker');
      if (title) {
        title.textContent = 'Genererer AI-visualisering…';
        if (desc) desc.textContent = 'Dette tar vanligvis 10–20 sekunder';
        if (kicker) kicker.innerHTML = 'AI VISUALIZER <b>JOBBER</b>';
      } else {
        top.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2l2.09 6.26L20.18 10l-6.09 1.74L12 18l-2.09-6.26L3.82 10l6.09-1.74L12 2z"/></svg> Genererer bilde<span class="wc__dots"></span>`;
      }
      this._aiSyncHeroButton();
      return;
    }

    this._aiBtn.classList.remove('wc__ai-btn--generating');
    if (this._aiBtnOrigHTML) top.innerHTML = this._aiBtnOrigHTML;
    this._aiSyncHeroButton();
  }

  _aiSetBtnReady() {
    if (!this._aiBtn) return;
    this._aiBtn.classList.remove('wc__ai-btn--generating');
    this._aiBtn.classList.add('wc__ai-btn--ready');
    const top = this._aiBtn.querySelector('.wc__ai-btn-top');
    if (!top) return;

    const title = top.querySelector('.wc__ai-btn-title');
    const desc = top.querySelector('.wc__ai-btn-desc');
    const kicker = top.querySelector('.wc__ai-btn-kicker');
    if (title) {
      title.textContent = 'AI-bildet er klart';
      if (desc) desc.textContent = 'Trykk for å åpne visualiseringen';
      if (kicker) kicker.innerHTML = 'AI VISUALIZER <b>KLAR</b>';
      this._aiSyncHeroButton();
      return;
    }

    top.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2l2.09 6.26L20.18 10l-6.09 1.74L12 18l-2.09-6.26L3.82 10l6.09-1.74L12 2z"/></svg> Bildet er klart!`;
    this._aiSyncHeroButton();
  }

  _aiIsIOSLike() {
    return (
      /iPad|iPhone|iPod/i.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );
  }

  _aiSaveResult(base64, url, meta = null) {
    const handle = this.dataset.productHandle;
    if (!handle) return;

    /*
     * Never persist AI result images on iPhone/iPad.
     * Backend image URLs may be temporary and Blob URLs never survive a page
     * reload. Saving either one makes the AI button reopen a dead image and
     * Safari immediately shows "Load failed" before a new generation starts.
     */
    if (this._aiIsIOSLike()) {
      try { localStorage.removeItem(`wc_ai_result_${handle}`); } catch {}
      return;
    }

    try {
      const entry = {
        ts: Date.now(),
        mimeType: this._aiResultMimeType || 'image/jpeg',
        meta: meta || this._aiResultMeta || this._aiBuildResultMeta(),
      };
      if (url) entry.url = url;
      else if (base64) entry.image = base64;
      localStorage.setItem(`wc_ai_result_${handle}`, JSON.stringify(entry));
    } catch { /* quota exceeded */ }
  }

  _aiLoadResult() {
    const handle = this.dataset.productHandle;
    if (!handle) return null;

    /*
     * Remove any stale result left by older code before the AI button state is
     * restored. The visualizer must open on the upload step after a reload.
     */
    if (this._aiIsIOSLike()) {
      try { localStorage.removeItem(`wc_ai_result_${handle}`); } catch {}
      return null;
    }

    try {
      const raw = localStorage.getItem(`wc_ai_result_${handle}`);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (Date.now() - data.ts > 24 * 60 * 60 * 1000) {
        localStorage.removeItem(`wc_ai_result_${handle}`);
        return null;
      }
      return data;
    } catch { return null; }
  }
}


if (!customElements.get('wheel-configurator')) {
  customElements.define('wheel-configurator', WheelConfiguratorElement);
}

const style = document.createElement('style');

style.innerHTML = `
.wc__brand-logo{
  width:auto;
  height:auto;
  max-width:none !important;
  object-fit:contain;
  vertical-align:middle;
  margin-right:6px;
  display:inline-block;
}
.wc__brand-logo--audi{
  height:72px !important;
  width:auto !important;
  max-width:none !important;
  object-fit:contain;
  display:inline-block;
  vertical-align:middle;
  margin-right:8px;
}
.wc__brand-logo--porsche{
  height:52px !important;
  width:auto !important;
  max-width:none !important;
  object-fit:contain;
  display:inline-block;
}

.wc__brand-logo--bmw{
  height:72px !important;
  width:auto !important;
  max-width:none !important;
  object-fit:contain;
  display:inline-block;
  margin-left:-70px;
}
.wc__brand-logo--mercedes {
  width: auto !important;
  height: 62px !important;
  object-fit: contain !important;
  display: block !important;

  margin: 0 !important;
  margin-left: 35px !important;
  transform: translateX(-35px) !important;
}
  .wc__brand-logo--tesla {
  max-width: 200px !important;
  max-height: 42px !important;
  object-fit: contain;
  display: block;
  margin: -40 auto 6px auto;
}
  .wc__brand-logo--ferrari {
  max-width: 72px !important;
  max-height: 72px !important;
  object-fit: contain;
  display: block;
  margin: 0 auto 6px auto;
}
  .wc__brand-logo--lamborghini {
  max-width: 82px !important;
  max-height: 82px !important;
  object-fit: contain;
  display: block;
  margin: -40 auto 6px auto;
}
`;
document.head.appendChild(style);

/* ================================================================
 * SBF HYBRID-FORGED CLEAN BRIDGE
 *
 * One authoritative implementation:
 * - Physical API variants decide PCD, width and ET.
 * - Shopify variants decide price, variant ID and cart.
 * - Square cars never receive a wider rear wheel.
 * - Staggered cars retain valid wider rear alternatives.
 * - Hybrid-Forged stock is shown green without quantity.
 * ================================================================ */
(() => {
  const Configurator = customElements.get('wheel-configurator');
  if (!Configurator || Configurator.prototype.__sbfHybridCleanBridge) return;

  const proto = Configurator.prototype;
  proto.__sbfHybridCleanBridge = true;

  const originalHydrate = proto._hydrateHybridShopifyVariants;
  const originalPairs = proto._getQbPairCandidates;
  const originalAutoSelect = proto._qbAutoSelect;
  const originalGetSelectedVariant = proto._getSelectedVariant;
  const originalRenderSize = proto._renderSizeStep;
  const originalRenderEt = proto._renderEtStep;

  const parseDiameter = value => {
    const match = String(value || '').match(/^\s*(\d+(?:\.\d+)?)/);
    return match ? Math.round(Number(match[1])) : 0;
  };

  const parseWidth = value => {
    const match = String(value || '').match(
      /^\s*\d+(?:\.\d+)?\s*[xX]\s*(\d+(?:\.\d+)?)/
    );
    return match ? Number(match[1]) : null;
  };

  const parseEtBounds = value => {
    const match = String(value || '').match(
      /ET\s*(-?\d+(?:\.\d+)?)(?:\s*-\s*(-?\d+(?:\.\d+)?))?/i
    );
    if (!match) return null;

    const first = Number(match[1]);
    const second = match[2] !== undefined ? Number(match[2]) : first;
    if (!Number.isFinite(first) || !Number.isFinite(second)) return null;

    return {
      min: Math.min(first, second),
      max: Math.max(first, second),
    };
  };

  const parseSingleEt = value => {
    const bounds = parseEtBounds(value);
    return bounds && bounds.min === bounds.max ? bounds.min : null;
  };

  const normalizePcd = value =>
    String(value || '').trim().toLowerCase().replace(/\s+/g, '');

  proto._ensureKnownVehiclePcd = function() {
    const vehicle = this._vehicle;
    const specs = vehicle?.specs;
    if (!specs) return;

    const make = String(
      vehicle.make ||
      vehicle.make_slug ||
      specs.make ||
      ''
    ).toLowerCase();

    const model = String(
      vehicle.model ||
      vehicle.model_slug ||
      specs.model ||
      ''
    ).toLowerCase();

    const display = String(
      vehicle.displayName ||
      vehicle.name ||
      ''
    ).toLowerCase();

    const isByd = make.includes('byd') || display.includes('byd');
    const isSealion7 =
      model.includes('sealion-7') ||
      model.includes('sealion 7') ||
      display.includes('sealion 7');

    if (isByd && isSealion7 && !normalizePcd(specs.pcd)) {
      specs.pcd = '5x120';
    }
  };

  proto._isHybridVehicleStaggeredClean = function(diameter) {
    const specs = this._vehicle?.specs || {};
    const entry = this._diamSpecs?.get(Number(diameter));

    if (specs.is_staggered === true) return true;

    const frontWidth = Number(
      entry?.front?.width ??
      [...(entry?.front?.widths || [])][0] ??
      specs.width_front
    );
    const rearWidth = Number(
      entry?.rear?.width ??
      [...(entry?.rear?.widths || [])][0] ??
      specs.width_rear
    );

    if (
      Number.isFinite(frontWidth) &&
      Number.isFinite(rearWidth) &&
      Math.abs(frontWidth - rearWidth) > 0.001
    ) {
      return true;
    }

    const frontDiameter = Number(
      entry?.front?.diameter ??
      specs.diameter_front ??
      specs.oem_diameter
    );
    const rearDiameter = Number(
      entry?.rear?.diameter ??
      specs.diameter_rear ??
      specs.rim_diameter_r
    );

    return (
      Number.isFinite(frontDiameter) &&
      Number.isFinite(rearDiameter) &&
      Math.round(frontDiameter) !== Math.round(rearDiameter)
    );
  };

  proto._getHybridPhysicalRowsClean = function(diameter) {
    this._ensureKnownVehiclePcd();

    const vehiclePcd = normalizePcd(this._vehicle?.specs?.pcd);
    if (!vehiclePcd) return [];

    const rows = Array.isArray(this._hybridFitmentVariants)
      ? this._hybridFitmentVariants
      : [];

    return rows
      .filter(variant =>
        normalizePcd(variant.option1) === vehiclePcd &&
        parseDiameter(variant.option2) === Math.round(Number(diameter))
      )
      .map(variant => ({
        variant,
        diameter: parseDiameter(variant.option2),
        width: parseWidth(variant.option2),
        et: parseSingleEt(variant.option3),
      }))
      .filter(row =>
        row.diameter > 0 &&
        Number.isFinite(row.width) &&
        Number.isFinite(row.et)
      );
  };

  proto._findHybridShopifyVariantClean = function(
    diameter,
    width,
    physicalEt
  ) {
    const source = Array.isArray(this._hybridShopifyVariantsClean)
      ? this._hybridShopifyVariantsClean
      : (this._allVariants || []);

    const candidates = source.filter(variant => {
      const d = parseDiameter(variant.option2 || variant.title);
      const w = parseWidth(variant.option2 || variant.title);

      return (
        d === Math.round(Number(diameter)) &&
        Number.isFinite(w) &&
        Math.abs(w - Number(width)) <= 0.001 &&
        Number(variant.id) > 0
      );
    });

    if (!candidates.length) return null;

    const wantedEt = Number(physicalEt);

    const ranked = candidates
      .map(variant => {
        const bounds = parseEtBounds(
          `${variant.option3 || ''} ${variant.title || ''}`
        );

        let score = 50;
        if (bounds && Number.isFinite(wantedEt)) {
          if (
            wantedEt >= bounds.min - 0.001 &&
            wantedEt <= bounds.max + 0.001
          ) {
            score = 0;
          } else {
            score = Math.min(
              Math.abs(wantedEt - bounds.min),
              Math.abs(wantedEt - bounds.max)
            );
          }
        } else if (!bounds) {
          // Some Shopify purchase variants only contain the size.
          score = 1;
        }

        if (variant.available === false) score += 100;
        return { variant, score };
      })
      .sort((a, b) => a.score - b.score);

    const selected = ranked[0]?.variant || null;
    if (!selected) return null;

    const agreedPrice = this._getHybridForgedPriceBySize?.(
      selected.option2 || selected.title
    );

    // Preserve Shopify's contextual/B2B price. The agreed price exists only as
    // an emergency fallback when no usable Shopify price is present.
    if (agreedPrice && !(Number(selected.price) > 0)) {
      selected.price = agreedPrice.price;
      if (!(Number(selected.compare_at_price) > 0)) {
        selected.compare_at_price = agreedPrice.compareAtPrice;
      }
    }

    selected.available = true;
    selected.inventory_policy = 'continue';
    return selected;
  };

  proto._getQbPairCandidates = function(...args) {
    if (!this._isHybridForged) {
      return typeof originalPairs === 'function'
        ? originalPairs.apply(this, args)
        : [];
    }

    this._ensureKnownVehiclePcd();

    const diameter = Math.round(
      Number(this._selectedDiameter) ||
      Number(this._urlDiameter) ||
      Number(this._vehicle?.specs?.diameter_front) ||
      Number(this._vehicle?.specs?.oem_diameter) ||
      0
    );

    if (!diameter) return [];

    const physicalRows = this._getHybridPhysicalRowsClean(diameter);
    if (!physicalRows.length) return [];

    const specs = this._vehicle?.specs || {};
    const entry = this._diamSpecs?.get(diameter);
    const staggered = this._isHybridVehicleStaggeredClean(diameter);

    const frontTargetWidth = Number(
      entry?.front?.width ??
      [...(entry?.front?.widths || [])][0] ??
      specs.width_front ??
      specs.oem_width
    );
    const frontTargetEt = Number(
      entry?.front?.et ??
      specs.et_front ??
      specs.et
    );
    const rearTargetWidth = Number(
      entry?.rear?.width ??
      [...(entry?.rear?.widths || [])][0] ??
      specs.width_rear ??
      specs.width_front ??
      specs.oem_width
    );
    const rearTargetEt = Number(
      entry?.rear?.et ??
      specs.et_rear ??
      specs.et_front ??
      specs.et
    );

    // Build every valid OEM width/ET reference for each axle. A vehicle can
    // have several OEM rear combinations at the same diameter (for example
    // BMW i4: 20x9 ET42 and 20x10 ET39). Comparing only against the first row
    // incorrectly removed the valid 20x10 Hybrid-Forged rear option.
    const getAxleReferences = axle => {
      const references = [];
      const seen = new Set();
      const add = (width, et) => {
        width = Number(width);
        et = Number(et);
        if (!Number.isFinite(width) || !Number.isFinite(et)) return;
        const key = `${width.toFixed(3)}|${et.toFixed(3)}`;
        if (seen.has(key)) return;
        seen.add(key);
        references.push({ width, et });
      };

      for (const pair of entry?.pairs || []) {
        const pairDiameter = Number(
          axle === 'rear' ? pair.rearDiameter : pair.frontDiameter
        );
        if (Math.round(pairDiameter || diameter) !== diameter) continue;
        add(
          axle === 'rear' ? pair.rearWidth : pair.frontWidth,
          axle === 'rear' ? pair.rearEt : pair.frontEt
        );
      }

      const axleEntry = entry?.[axle];
      if (axleEntry?.etByWidth instanceof Map) {
        for (const [widthKey, ets] of axleEntry.etByWidth.entries()) {
          for (const et of ets || []) add(Number(widthKey), et);
        }
      }

      add(
        axle === 'rear' ? rearTargetWidth : frontTargetWidth,
        axle === 'rear' ? rearTargetEt : frontTargetEt
      );

      return references;
    };

    const frontReferences = getAxleReferences('front');
    const rearReferences = getAxleReferences('rear');

    const getBestReference = (row, references) => {
      if (!row || !Array.isArray(references)) return null;

      // Hybrid-Forged has fixed physical ET values. For these wheels only,
      // an ET deviation of up to +/- 7.5 mm from an approved OEM reference
      // is allowed. Do not use the stricter generic geometry gate here.
      const matches = references
        .filter(reference =>
          Number.isFinite(Number(reference.et)) &&
          Number.isFinite(Number(row.et)) &&
          Math.abs(Number(row.et) - Number(reference.et)) <= 7.5 + 0.001
        )
        .map(reference => ({
          reference,
          score:
            Math.abs(Number(row.width) - reference.width) * 100 +
            Math.abs(Number(row.et) - reference.et),
        }))
        .sort((a, b) => a.score - b.score);

      return matches[0] || null;
    };

    const isSafeRecommendedRow = (row, references) =>
      !!getBestReference(row, references);

    const chooseClosest = (rows, references) => {
      if (!rows.length) return null;

      // Only OEM-quality geometry is allowed in the recommended cards.
      // Variants that are merely mountable remain available through Custom Fit.
      const ranked = rows
        .map(row => ({ row, match: getBestReference(row, references) }))
        .filter(item => item.match)
        .sort((a, b) => a.match.score - b.match.score);

      return ranked[0]?.row || null;
    };

    const frontRows = physicalRows.filter(row =>
      !Number.isFinite(frontTargetWidth) ||
      row.width <= frontTargetWidth + 0.5001
    );

    const front = chooseClosest(
      frontRows.length ? frontRows : physicalRows,
      frontReferences
    );

    if (!front) return [];

    const unique = new Map();

    if (!staggered) {
      // Square car: only the selected front width can be used at the rear.
      // All exact ET variants for that width remain selectable.
      for (const row of physicalRows) {
        if (Math.abs(row.width - front.width) > 0.001) continue;
        if (!isSafeRecommendedRow(row, frontReferences)) continue;

        const pair = {
          frontDiameter: diameter,
          frontWidth: row.width,
          frontEt: row.et,
          rearDiameter: diameter,
          rearWidth: row.width,
          rearEt: row.et,
          source: 'hybrid-forged-square-physical',
          recommended: true,
          hybridForged: true,
          wheeltrade: false,
        };

        unique.set(this._qbPairKey(pair), pair);
      }

      return [...unique.values()].sort(
        (a, b) => Math.abs(a.frontEt - frontTargetEt) -
                  Math.abs(b.frontEt - frontTargetEt)
      );
    }

    // Staggered car: keep the best rear ET for each valid rear width.
    const rearByWidth = new Map();

    for (const row of physicalRows) {
      if (row.width < front.width - 0.001) continue;
      if (row.width > front.width + 1.5001) continue;
      if (!isSafeRecommendedRow(row, rearReferences)) continue;

      const key = row.width.toFixed(3);
      const current = rearByWidth.get(key);
      const score =
        (Number.isFinite(rearTargetWidth)
          ? Math.abs(row.width - rearTargetWidth) * 100
          : 0) +
        (Number.isFinite(rearTargetEt)
          ? Math.abs(row.et - rearTargetEt)
          : 0);

      if (!current || score < current.score) {
        rearByWidth.set(key, { row, score });
      }
    }

    for (const { row: rear } of rearByWidth.values()) {
      const pair = {
        frontDiameter: diameter,
        frontWidth: front.width,
        frontEt: front.et,
        rearDiameter: diameter,
        rearWidth: rear.width,
        rearEt: rear.et,
        source: 'hybrid-forged-staggered-physical',
        recommended: true,
        hybridForged: true,
        wheeltrade: false,
      };

      unique.set(this._qbPairKey(pair), pair);
    }

    return [...unique.values()].sort(
      (a, b) => a.rearWidth - b.rearWidth
    );
  };

  proto._applyHybridPairClean = function(pair) {
    if (!pair) return false;

    const frontVariant = this._findHybridShopifyVariantClean(
      pair.frontDiameter,
      pair.frontWidth,
      pair.frontEt
    );

    if (!frontVariant) return false;

    this._selectedPcd = String(frontVariant.option1 || '').trim();
    this._selectedSize = String(frontVariant.option2 || '').trim();
    this._selectedEt = String(frontVariant.option3 || '').trim();
    this._selectedExactEt = Number(pair.frontEt);

    const isStaggered = this._isHybridVehicleStaggeredClean(
      pair.frontDiameter
    );

    const splitPair =
      Math.round(Number(pair.rearDiameter)) !==
        Math.round(Number(pair.frontDiameter)) ||
      Math.abs(Number(pair.rearWidth) - Number(pair.frontWidth)) > 0.001 ||
      Math.abs(Number(pair.rearEt) - Number(pair.frontEt)) > 0.001;

    if (isStaggered && splitPair) {
      const rearVariant = this._findHybridShopifyVariantClean(
        pair.rearDiameter,
        pair.rearWidth,
        pair.rearEt
      );

      if (!rearVariant) return false;

      this._qbRearSelection = {
        pcd: String(rearVariant.option1 || '').trim(),
        size: String(rearVariant.option2 || '').trim(),
        et: String(rearVariant.option3 || '').trim(),
        exactEt: Number(pair.rearEt),
      };
    } else {
      this._qbRearSelection = {
        pcd: null,
        size: null,
        et: null,
        exactEt: null,
      };
    }

    this._qbActivePair = pair;
    this._qbSelectedPairKey = this._qbPairKey(pair);
    return true;
  };

  proto._qbAutoSelect = function(...args) {
    if (!this._isHybridForged) {
      return typeof originalAutoSelect === 'function'
        ? originalAutoSelect.apply(this, args)
        : undefined;
    }

    this._ensureKnownVehiclePcd();
    this._qbEnsureDefaults();

    if (!this._selectedDiameter || !this._vehicle?.specs) return;

    const pairs = this._getQbPairCandidates();
    if (!pairs.length) {
      this._selectedSize = null;
      this._selectedEt = null;
      this._selectedExactEt = null;
      this._qbRearSelection = {
        pcd: null,
        size: null,
        et: null,
        exactEt: null,
      };
      this._qbActivePair = null;
      this._qbSelectedPairKey = null;
      return;
    }

    const selectedPair =
      pairs.find(
        pair => this._qbPairKey(pair) === this._qbSelectedPairKey
      ) ||
      pairs[0];

    if (!this._applyHybridPairClean(selectedPair)) {
      this._selectedSize = null;
      this._selectedEt = null;
      this._selectedExactEt = null;
      return;
    }

    this._driveHorizonPicker?.();
  };

  proto._forceHybridGreenStockClean = function() {
    if (!this._isHybridForged) return;

    for (const variant of this._allVariants || []) {
      variant.available = true;
      variant.inventory_policy = 'continue';

      const key = this._variantKey(variant);
      const existing = this._inventory?.[key] || {};

      this._inventory[key] = {
        ...existing,
        id: key,
        qty: 99,
        managed: 'shopify',
        available: true,
        inventoryPolicy: 'continue',
        max_load:
          existing.max_load ??
          variant.max_load ??
          variant.maxLoad ??
          900,
        tuv_max_load:
          existing.tuv_max_load ??
          variant.tuv_max_load ??
          variant.tuvMaxLoad ??
          null,
      };
    }
  };

  proto._normalizeHybridStockLabelsClean = function() {
    if (!this._isHybridForged) return;

    this.querySelectorAll(
      '.wc__size-stock, .wc__et-stock'
    ).forEach(element => {
      const dot = element.querySelector('.wc__stock-dot');
      if (dot) {
        dot.classList.remove(
          'wc__stock-dot--low',
          'wc__stock-dot--out',
          'wc__stock-dot--sold-out'
        );
        dot.classList.add('wc__stock-dot--ok');
      }

      const text = element.textContent || '';
      if (/kun|stk|best\.|bestilling|utsolgt/i.test(text)) {
        element.innerHTML =
          '<span class="wc__stock-dot wc__stock-dot--ok"></span> På lager';
      }
    });
  };

  proto._hydrateHybridShopifyVariants = async function(...args) {
    const result = typeof originalHydrate === 'function'
      ? await originalHydrate.apply(this, args)
      : undefined;

    if (!this._isHybridForged) return result;

    // The original hydration has now produced the real Shopify variants
    // containing numeric IDs and prices. Keep a dedicated purchase copy.
    this._hybridShopifyVariantsClean = (this._allVariants || []).map(
      variant => ({ ...variant })
    );

    this._forceHybridGreenStockClean();
    this._buildOptionSets();
    this._applyVehicleFilter();
    this._initQuickBuyMode();

    if (this._quickBuyMode && this._vehicle?.specs) {
      this._qbAutoSelect();
    }

    this._render();
    this._normalizeHybridStockLabelsClean();
    return result;
  };

  proto._getSelectedVariant = function(...args) {
    const selected = typeof originalGetSelectedVariant === 'function'
      ? originalGetSelectedVariant.apply(this, args)
      : null;

    if (selected || !this._isHybridForged) return selected;

    const pair = this._qbActivePair;
    if (!pair) return null;

    return this._findHybridShopifyVariantClean(
      pair.frontDiameter,
      pair.frontWidth,
      pair.frontEt
    );
  };

  proto._renderSizeStep = function(...args) {
    const result = typeof originalRenderSize === 'function'
      ? originalRenderSize.apply(this, args)
      : undefined;
    this._normalizeHybridStockLabelsClean();
    return result;
  };

  proto._renderEtStep = function(...args) {
    const result = typeof originalRenderEt === 'function'
      ? originalRenderEt.apply(this, args)
      : undefined;
    this._normalizeHybridStockLabelsClean();
    return result;
  };
})();

/* ================================================================
 * SBF BYD SEALION KNOWN SPECS + HUB RING INFO
 *
 * The registration payload can identify this car only as "BYD SEALION".
 * Inject the known EP81304/Sealion specifications before any filtering:
 * PCD 5x120, vehicle hub 64.1, OEM 20x8 ET40.
 * ================================================================ */
(() => {
  const Configurator = customElements.get('wheel-configurator');
  if (!Configurator || Configurator.prototype.__sbfBydSealionKnownSpecs) return;

  const proto = Configurator.prototype;
  proto.__sbfBydSealionKnownSpecs = true;

  const originalConnected = proto.connectedCallback;
  const originalDisconnected = proto.disconnectedCallback;
  const originalRenderCompat = proto._renderCompatInfo;
  const originalRenderOemCard = proto._renderOemSpecCard;

  proto._isKnownBydSealion = function(vehicle = this._vehicle) {
    if (!vehicle) return false;

    const specs = vehicle.specs || {};
    const plate = String(
      vehicle.plate ||
      vehicle.regnr ||
      vehicle.registration ||
      vehicle.registrationNumber ||
      ''
    ).toUpperCase().replace(/[\s-]/g, '');

    const make = String(
      vehicle.make ||
      vehicle.make_slug ||
      specs.make ||
      ''
    ).toLowerCase();

    const model = String(
      vehicle.model ||
      vehicle.model_slug ||
      specs.model ||
      ''
    ).toLowerCase();

    const display = String(
      vehicle.displayName ||
      vehicle.name ||
      vehicle.title ||
      ''
    ).toLowerCase();

    if (plate === 'EP81304') return true;

    const isByd = make.includes('byd') || display.includes('byd');
    const isSealion =
      model.includes('sealion') ||
      display.includes('sealion');

    return isByd && isSealion;
  };

  proto._applyKnownBydSealionSpecs = function() {
    const vehicle = this._vehicle || getVehicle();
    if (!this._isKnownBydSealion(vehicle)) return false;

    vehicle.specs ||= {};
    const specs = vehicle.specs;

    specs.pcd = '5x120';
    specs.bore = 64.1;
    specs.center_bore = 64.1;
    specs.centre_bore = 64.1;
    specs.hub = 64.1;
    specs.hub_bore = 64.1;

    specs.oem_diameter = 20;
    specs.diameter_front = 20;
    specs.diameter_rear = 20;
    specs.diameter_min = 20;
    specs.diameter_max = Math.max(Number(specs.diameter_max) || 0, 20);

    specs.oem_width = 8;
    specs.width_front = 8;
    specs.width_rear = 8;
    specs.width_max_front = Math.max(Number(specs.width_max_front) || 0, 8.5);
    specs.width_max_rear = Math.max(Number(specs.width_max_rear) || 0, 8.5);

    specs.et = 40;
    specs.et_front = 40;
    specs.et_rear = 40;
    specs.is_staggered = false;

    this._vehicle = vehicle;

    try {
      saveVehicle(vehicle);
    } catch (_) {}

    return true;
  };

  proto._ensureKnownVehiclePcd = function() {
    this._applyKnownBydSealionSpecs();
  };

  proto.connectedCallback = function(...args) {
    this._vehicle = getVehicle();
    this._applyKnownBydSealionSpecs();

    const result = originalConnected.apply(this, args);

    this._applyKnownBydSealionSpecs();

    queueMicrotask(() => {
      this._applyKnownBydSealionSpecs();
      this._applyVehicleFilter?.();
      this._initQuickBuyMode?.();

      if (this._quickBuyMode && this._vehicle?.specs) {
        this._qbAutoSelect?.();
      }

      this._render?.();
    });

    if (!this.__sbfBydSealionVehicleListener) {
      this.__sbfBydSealionVehicleListener = () => {
        queueMicrotask(() => {
          this._vehicle = getVehicle();
          if (!this._applyKnownBydSealionSpecs()) return;

          this._applyVehicleFilter?.();
          this._initQuickBuyMode?.();

          if (this._quickBuyMode && this._vehicle?.specs) {
            this._qbAutoSelect?.();
          }

          this._render?.();
        });
      };

      window.addEventListener(
        'vehicle:changed',
        this.__sbfBydSealionVehicleListener
      );
    }

    return result;
  };

  proto.disconnectedCallback = function(...args) {
    if (this.__sbfBydSealionVehicleListener) {
      window.removeEventListener(
        'vehicle:changed',
        this.__sbfBydSealionVehicleListener
      );
      this.__sbfBydSealionVehicleListener = null;
    }

    return typeof originalDisconnected === 'function'
      ? originalDisconnected.apply(this, args)
      : undefined;
  };

  proto._renderOemSpecCard = function(...args) {
    this._applyKnownBydSealionSpecs();
    return typeof originalRenderOemCard === 'function'
      ? originalRenderOemCard.apply(this, args)
      : undefined;
  };

  proto._renderCompatInfo = function(...args) {
    this._applyKnownBydSealionSpecs();

    const result = typeof originalRenderCompat === 'function'
      ? originalRenderCompat.apply(this, args)
      : undefined;

    if (!this._isHybridForged || !this._isKnownBydSealion()) {
      return result;
    }

    const compat =
      this.querySelector('[data-role="wc-compat"]') ||
      this._compatCard;

    if (!compat) return result;

    const ringText = 'Passer med senterringer 72,6–64,1 mm';
    const existingText = compat.textContent || '';

    if (!existingText.includes(ringText)) {
      const row = document.createElement('div');
      row.className = 'wc__card-row wc__card-row--ok';
      row.innerHTML =
        `${CHECK_SVG}<span><strong>${ringText}</strong></span>`;
      compat.appendChild(row);
    }

    return result;
  };
})();

/* ================================================================
 * SBF AVENTADOR AI — REMOVE DUPLICATE ACTION ICONS
 *
 * The premium JavaScript already inserts one real icon inside each action
 * button. Older premium CSS also adds the same icon with ::before.
 * Keep the JavaScript icon and suppress only the duplicate pseudo-element.
 *
 * The AI modal is moved to document.body while open, so these selectors
 * target the modal directly instead of relying on a wheel-configurator parent.
 * ================================================================ */
(() => {
  const styleId = 'sbf-aw-single-action-icon-fix';
  if (document.getElementById(styleId)) return;

  const iconStyle = document.createElement('style');
  iconStyle.id = styleId;
  iconStyle.textContent = `
    [data-role="wc-ai-modal"][data-aw-brand="aventador"]
    [data-role="aw-download-result"]::before,
    [data-role="wc-ai-modal"][data-aw-brand="aventador"]
    [data-role="aw-create-another"]::before {
      content: none !important;
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
      width: 0 !important;
      height: 0 !important;
      min-width: 0 !important;
      min-height: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      border: 0 !important;
      background: none !important;
      background-image: none !important;
      box-shadow: none !important;
    }
  `;

  document.head.appendChild(iconStyle);
})();


/* SBF_JR_CONCAVER_STOCK_AWARE_ET_START */
(() => {
  const Configurator = customElements.get('wheel-configurator');
  if (!Configurator || Configurator.prototype.__sbfJrConcaverStockAwareEt) return;

  const proto = Configurator.prototype;
  proto.__sbfJrConcaverStockAwareEt = true;

  const originalRemoveSubsetRanges = proto._removeSubsetRanges;

  const isTargetProduct = instance => {
    if (!instance || instance._isHybridForged) return false;

    const vendor = String(instance.dataset?.productVendor || '').toLowerCase();
    const handle = String(instance.dataset?.productHandle || '').toLowerCase();

    return (
      vendor.includes('japan racing') ||
      vendor.includes('concaver') ||
      handle.includes('japan-racing') ||
      handle.includes('concaver')
    );
  };

  const profileOf = value => {
    const match = String(value || '').match(/\(([^)]+)\)\s*$/);
    return match ? match[1].trim().toLowerCase() : '';
  };

  const isSellable = option =>
    Number(option?.qty || 0) > 0 ||
    option?.available === true ||
    option?.inventoryPolicy === 'continue';

  proto._removeSubsetRanges = function(etOptions) {
    if (
      !isTargetProduct(this) ||
      !Array.isArray(etOptions) ||
      etOptions.length < 2
    ) {
      return typeof originalRemoveSubsetRanges === 'function'
        ? originalRemoveSubsetRanges.call(this, etOptions)
        : undefined;
    }

    const ranges = etOptions
      .map((option, index) => {
        const range =
          typeof this._parseEtRange === 'function'
            ? this._parseEtRange(option.et)
            : null;

        return range
          ? {
              index,
              option,
              lo: range.lo,
              hi: range.hi,
              profile: profileOf(option.et),
            }
          : null;
      })
      .filter(Boolean);

    const toRemove = new Set();

    for (const a of ranges) {
      for (const b of ranges) {
        if (a.index === b.index) continue;

        const strictSubset =
          b.lo <= a.lo &&
          a.hi <= b.hi &&
          (b.hi - b.lo) > (a.hi - a.lo);

        if (!strictSubset) continue;

        // Face/Profile er fysisk variantinformasjon.
        // Ulike profiler skal aldri slås sammen.
        if (a.profile !== b.profile) continue;

        // En tilgjengelig variant skal aldri fjernes
        // til fordel for en utsolgt bredere range.
        if (isSellable(a.option) && !isSellable(b.option)) continue;

        toRemove.add(a.index);
      }
    }

    for (const index of [...toRemove].sort((a, b) => b - a)) {
      etOptions.splice(index, 1);
    }

    // OEM-valget i grunnkoden tar første range som inneholder OEM ET.
    // Ved overlapp prioriterer vi derfor tilgjengelig variant,
    // men beholder ellers eksisterende rekkefølge.
    const originalOrder = new Map(
      etOptions.map((option, index) => [option, index])
    );

    etOptions.sort((a, b) => {
      const ra =
        typeof this._parseEtRange === 'function'
          ? this._parseEtRange(a.et)
          : null;

      const rb =
        typeof this._parseEtRange === 'function'
          ? this._parseEtRange(b.et)
          : null;

      if (ra && rb) {
        const overlaps =
          Math.max(ra.lo, rb.lo) <= Math.min(ra.hi, rb.hi);

        if (overlaps && isSellable(a) !== isSellable(b)) {
          return isSellable(a) ? -1 : 1;
        }
      }

      return originalOrder.get(a) - originalOrder.get(b);
    });
  };
})();
/* SBF_JR_CONCAVER_STOCK_AWARE_ET_END */

